/**
 * Grass-vs-Grass comparison registry powering /compare/[slug] pages.
 *
 * Each entry composes:
 *   - 200-word hand-written intro that escapes "thin programmatic" classification
 *   - A structured comparison table (climate, mowing, water, fertilizer, traffic, shade,
 *     maintenance, cold tolerance, drought tolerance, root depth, establishment)
 *   - "Pick A if..." / "Pick B if..." decision blocks
 *   - FAQ array (page-specific Q&A; rendered as FAQPage JSON-LD too)
 *   - Tied pillar guide slugs for Tier-1 internal linking
 *
 * Per the project plan, hand-curated content + ~200-word intro + multiple unique
 * sections per page is what keeps Google from classifying these as scaled/doorway
 * content. Add new entries by hand, not via codegen.
 */

import type { ReactNode } from 'react'
import type { AuthorSlug } from '@/lib/types/author'
import type { ProductKey } from '@/data/guide-product-recommendations'
import type { PickBadge } from '@/data/article-product-picks'
import type { ReferencePhoto } from '@/data/grass-reference-photos'
import {
  ANONYMOUS_WEB_ANALYSIS_LIMITS,
  EMAIL_TIER_ANALYSIS_LIMITS,
  FREE_LIMITS,
  PREMIUM_TRIAL_DAYS,
} from '@/lib/auth/feature-flags'
import { WEB_DISCOUNT } from '@/lib/pricing-display'
import {
  TURF_BASE_PREP_COST,
  TURF_DIY_TOOL_COST,
  TURF_INFILL_COST,
  TURF_MATERIAL_COSTS,
  TURF_PRO_LABOR_COST,
  TURF_REMOVAL_COST,
  type TurfCostRange,
} from '@/data/artificial-turf-costs'
import { formatRange, rangeAcrossRegions, spanRanges } from '@/lib/cost-hub'

export type GrassKey =
  | 'Kentucky Bluegrass'
  | 'Perennial Ryegrass'
  | 'Tall Fescue'
  | 'Fine Fescue'
  | 'Bermudagrass'
  | 'Zoysiagrass'
  | 'St. Augustinegrass'
  | 'Centipede'
  | 'Bahiagrass'
  | 'Buffalograss'
  | 'Annual Ryegrass'
  | 'Mixed Cool-Season Blend'
  | 'Kikuyugrass'

export type GuideSlug =
  | 'kentucky-bluegrass'
  | 'perennial-ryegrass'
  | 'tall-fescue'
  | 'fine-fescue'
  | 'bermudagrass'
  | 'zoysiagrass'
  | 'st-augustinegrass'
  | 'centipede'
  | 'bahiagrass'
  | 'buffalograss'
  | 'annual-ryegrass'
  | 'mixed-cool-season-blend'
  | 'kikuyugrass'

export interface ComparisonRow {
  label: string
  a: string
  b: string
}

export interface ComparisonFaq {
  question: string
  answer: string
}

export type ComparisonKind = 'grass' | 'brand' | 'topic'

interface BaseComparisonEntry {
  /** URL slug under /compare/ */
  slug: string
  /** Discriminator that switches the page footer between pillar-guide cross-links (grass), product-category CTAs (brand), and internal deep links (topic). */
  kind: ComparisonKind
  /** Display label for breadcrumbs and listings. */
  shortLabel: string
  /** Full H1 title. */
  h1: string
  /** SEO meta title (often shorter than H1). */
  metaTitle: string
  /** SEO meta description. */
  metaDescription: string
  /** Last modified ISO date (YYYY-MM-DD) for sitemap. */
  lastModified: string
  /** Optional author override. Compare pages default to the site's default
   * author since the content is hand-curated decision-guide material rather
   * than a single-author byline. Set this only when a specific persona's voice
   * should be attributed. */
  authorSlug?: AuthorSlug
  /**
   * GEO direct-answer block. Rendered as the first prose element, between the
   * H1 and the VS panel, so the page answers its own question inside the first
   * 60 words rather than four elements down. One sentence, no hedging.
   */
  directAnswer?: string
  /**
   * First-party ownership disclosure, rendered above the fold whenever present.
   * REQUIRED on every entry where one side is a WhatGrassIsThis property, so a
   * reader knows they are reading our case for our own product before they read
   * the table.
   */
  ownershipDisclosure?: string
  /** 200-word hand-written intro. ReactNode so we can include <p> blocks + emphasis. */
  intro: ReactNode
  /** One-line verdict statement shown in a card above the table. */
  verdict: string
  /** Structured comparison rows. Keep label order consistent across entries. */
  table: ComparisonRow[]
  /** Decision block for picking side A. */
  pickA: { headline: string; reasons: string[] }
  /** Decision block for picking side B. */
  pickB: { headline: string; reasons: string[] }
  /** Page-specific FAQs (also rendered as FAQPage JSON-LD). */
  faqs: ComparisonFaq[]
}

export interface GrassComparisonEntry extends BaseComparisonEntry {
  kind: 'grass'
  /** Side A grass + linked pillar guide. */
  a: { key: GrassKey; guideSlug: GuideSlug; displayName: string }
  /** Side B grass + linked pillar guide. */
  b: { key: GrassKey; guideSlug: GuideSlug; displayName: string }
}

/**
 * Hero/card artwork for an app matchup side. App sides have no packshot and no
 * reference photo, and we ship no third-party logos, so each side declares how
 * its tile is drawn instead of falling through to a generic phone glyph.
 */
export interface AppTile {
  /** Our own side: renders the real What Grass Is This? app icon. */
  ours?: boolean
  /** Competitor wordmark, one string per typeset line. */
  lines?: string[]
  /** Short mark used where the tile is too small to set the wordmark. */
  monogram?: string
}

export interface BrandComparisonSide {
  /** Brand name, e.g. "Scotts". */
  brand: string
  /** Full display name shown in the footer card, e.g. "Scotts Turf Builder Weed & Feed". */
  displayName: string
  /** Short noun phrase shown above the product card describing what this side is, e.g. "synthetic combo herbicide + fertilizer". */
  productType: string
  /** Optional /products category slug for the "Browse top picks" CTA. Omit if no curated product category exists (e.g. Sunday — subscription-only, not on Amazon). */
  productsCategory?: string
  /**
   * Catalog key for a direct, PAAPI-verified Amazon buy button. Hydrated via
   * linkFromCatalog at render. Omit when the side has no Amazon path (Sunday =
   * subscription service, DIY = not a single product).
   */
  productKey?: ProductKey
  /** Short "why this product" line shown on the compare buy card. */
  buyReason?: string
  /** Optional badge to steer the pick (e.g. "Best value"). Reuses the article-picks badge. */
  badge?: PickBadge
  /** App matchups only (category: 'app'). Ignored on product sides. */
  appTile?: AppTile
}

export interface BrandComparisonEntry extends BaseComparisonEntry {
  kind: 'brand'
  a: BrandComparisonSide
  b: BrandComparisonSide
  /**
   * Landing-page grouping and icon family within the brand kind. Defaults to
   * `product` (fertilizer, seed, herbicide). `app` routes the card into the
   * "Lawn App Comparisons" section and marks the entry as a software matchup,
   * which the related-comparison matcher keys on: `brandCategoryOf` reduces a
   * productType to seed/fertilizer/other, so two app entries would otherwise
   * both land in `other` and cross-link to nothing.
   */
  category?: 'product' | 'app'
}

/**
 * Landing-page section + icon family for a topic comparison. `diagnostic` is
 * "what is wrong with my lawn" (dormant vs dead), `lookalike` is visual weed-vs-grass
 * ID, `landscape` is a whole-yard surface decision (turf vs grass).
 */
export type TopicGroup = 'diagnostic' | 'lookalike' | 'landscape'

/** Deep-link icon token. A string rather than a component so this data module stays lucide-free. */
export type TopicLinkIcon = 'diagnose' | 'calculator' | 'weed' | 'guide'

export interface TopicComparisonSide {
  /** Column header, VS-panel name, decision-block subject. */
  displayName: string
  /** Short noun phrase under the name in the VS panel (mirrors brand's productType). */
  sub: string
  /**
   * Local /public image with its credit + license travelling with it, so
   * ComparePhotoCredits can attribute it without a second lookup. Omit or null
   * to fall back to the topic-group icon tile.
   */
  photo?: ReferencePhoto | null
  /** Internal deep link rendered as this side's decision-block action. */
  link?: { href: string; label: string; icon: TopicLinkIcon }
}

/**
 * Comparisons whose two sides are neither a GrassKey with a pillar guide nor a
 * purchasable product: dormant vs dead turf, weed-vs-grass lookalikes, and
 * artificial turf vs a real lawn. They render the same template as grass and
 * brand pages, minus the affiliate surfaces, and route the reader to the
 * matching /weeds, /guides, /diagnose, or /tools page instead of Amazon.
 */
export interface TopicComparisonEntry extends BaseComparisonEntry {
  kind: 'topic'
  topicGroup: TopicGroup
  a: TopicComparisonSide
  b: TopicComparisonSide
  /** Optional full-width tool CTA rendered right after the decision blocks. */
  toolCta?: { href: string; label: string; blurb: string }
}

export type ComparisonEntry =
  | GrassComparisonEntry
  | BrandComparisonEntry
  | TopicComparisonEntry

const bermudaVsZoysia: GrassComparisonEntry = {
  slug: 'bermuda-vs-zoysia',
  kind: 'grass',
  shortLabel: 'Bermuda vs Zoysia',
  h1: 'Bermuda vs Zoysia Grass: Which Is Right for Your Lawn?',
  metaTitle: 'Bermuda vs Zoysia Grass: Which Is Better for You? (2026)',
  metaDescription:
    'Bermuda vs Zoysia grass compared head-to-head: maintenance level, mowing height, shade tolerance, water use, cost, and which one fits your lawn best.',
  lastModified: '2026-08-05',
  a: { key: 'Bermudagrass', guideSlug: 'bermudagrass', displayName: 'Bermudagrass' },
  b: { key: 'Zoysiagrass', guideSlug: 'zoysiagrass', displayName: 'Zoysiagrass' },
  intro: (
    <>
      <p>
        Bermudagrass and zoysiagrass are the two most popular warm-season turf choices in the
        southern United States, and homeowners shopping for a new lawn usually narrow it down to
        these two before anything else. They look superficially similar from the curb (both dense,
        both warm-season, both green up in late spring) but they behave very differently the
        moment you start maintaining them. Bermuda is the aggressive, fast-recovering, full-sun
        athlete that wants frequent mowing and heavy feeding. Zoysia is the slower-growing,
        denser, more forgiving option that handles part shade and asks for less from you week to
        week.
      </p>
      <p>
        The right pick comes down to four questions: how much sun your lawn gets, how often you
        want to mow, whether you can tolerate winter dormancy (both go brown, but for different
        lengths of time), and your budget. Bermuda wins on price and recovery speed. Zoysia wins
        on shade tolerance and lower maintenance hours. We break down every dimension below.
      </p>
    </>
  ),
  verdict:
    'Bermuda wins for full-sun, high-traffic lawns and lower upfront cost. Zoysia wins for partial shade, lower maintenance hours, and slower growth that means fewer mows per year.',
  table: [
    { label: 'Climate zone (USDA)', a: '7-10 (warm-season)', b: '6-10 (warm-season)' },
    { label: 'Sun requirement', a: '6-8 hours full sun', b: '4-6 hours (handles part shade)' },
    { label: 'Shade tolerance', a: 'Low', b: 'Medium' },
    { label: 'Traffic tolerance', a: 'Very high (recovers fastest)', b: 'High (but slow to repair)' },
    { label: 'Drought tolerance', a: 'High', b: 'Very high' },
    { label: 'Mowing height', a: '0.5 to 1.5 inches (reel) / 1.5 to 2.5 inches (rotary)', b: '1 to 2 inches' },
    { label: 'Mowing frequency in peak season', a: 'Every 3 to 5 days', b: 'Every 7 to 14 days' },
    { label: 'Annual nitrogen need', a: '4 to 5 lbs / 1,000 sq ft', b: '2 to 3 lbs / 1,000 sq ft' },
    { label: 'Water need (peak)', a: '1.0 to 1.25 inches / week', b: '0.75 to 1.0 inches / week' },
    { label: 'Winter dormancy', a: 'Full dormancy late Nov to early April (4 to 5 months)', b: 'Full dormancy mid-Nov to mid-April (5 months)' },
    { label: 'Green-up speed in spring', a: 'Fast (recovers in 2 to 3 weeks)', b: 'Slow (4 to 6 weeks)' },
    { label: 'Establishment from sod', a: '2 to 4 weeks', b: '4 to 8 weeks' },
    { label: 'Establishment from seed', a: 'Available (60 to 90 days)', b: 'Limited cultivars; sod usually required' },
    { label: 'Cost per pallet of sod (500 sq ft)', a: '$150 to $250', b: '$300 to $500' },
    { label: 'Maintenance level', a: 'High', b: 'Medium' },
  ],
  pickA: {
    headline: 'Pick Bermudagrass if...',
    reasons: [
      'Your lawn gets 6+ hours of direct sun every day with no significant tree cover.',
      'You have kids, dogs, or heavy backyard traffic and need a lawn that recovers from wear in days, not weeks.',
      'You want the cheapest path from bare dirt to lawn (seed is widely available; sod is cheaper than zoysia).',
      'You do not mind mowing every 3 to 5 days during the peak growing season.',
      'You are in Zone 8 or warmer with consistent summer heat above 85 degrees.',
    ],
  },
  pickB: {
    headline: 'Pick Zoysiagrass if...',
    reasons: [
      'Your lawn has partial shade from trees or buildings (4 to 6 hours of sun, not 6 to 8).',
      'You want a softer, denser, more carpet-like feel underfoot than bermuda gives.',
      'You can tolerate slow spring green-up in exchange for fewer mows per year.',
      'You are in the transition zone (Zone 6 or 7) where bermuda risks winterkill.',
      'You are willing to pay more upfront for sod (zoysia is rarely seeded successfully) to get lower ongoing maintenance.',
    ],
  },
  faqs: [
    {
      question: 'Is bermuda or zoysia better for full sun?',
      answer:
        'Bermudagrass is better for full sun. It needs 6 to 8 hours of direct sunlight to thrive and out-performs zoysia in those conditions on every metric that matters: recovery speed, density, and drought tolerance. Zoysia tolerates 4 to 6 hours of sun and is the better choice when the lawn has any meaningful shade, but in full-sun lawns bermuda wins.',
    },
    {
      question: 'Can you mix bermuda and zoysia in the same lawn?',
      answer:
        'Mixing bermuda and zoysia in the same lawn rarely works long-term. Bermuda is aggressive and spreads through both rhizomes and stolons; zoysia spreads more slowly. In a mixed lawn, bermuda almost always takes over the sunny areas within 2 to 3 growing seasons, leaving zoysia only in the shadier zones. If you want both, treat them as separate zones with a hard physical barrier (edging or a sidewalk) between them.',
    },
    {
      question: 'Which grass is cheaper, bermuda or zoysia?',
      answer:
        'Bermudagrass is significantly cheaper than zoysia. Common bermuda seed costs $2 to $4 per pound for 8 to 10 pounds per 1,000 square feet, putting a new bermuda lawn at $20 to $40 per 1,000 sq ft for seed alone. Hybrid bermuda sod runs $150 to $250 per 500 sq ft pallet. Zoysia is almost always sold as sod (most cultivars do not establish reliably from seed) and runs $300 to $500 per 500 sq ft pallet. Over the long run, zoysia costs less to maintain because it needs less fertilizer and fewer mows, but the upfront cost is roughly double bermuda.',
    },
    {
      question: 'Does zoysia or bermuda go dormant longer in winter?',
      answer:
        'Zoysia goes dormant slightly longer than bermuda in most climates. Bermuda greens up at about 65 degree Fahrenheit soil temperature, which typically happens in late March to early April in the South. Zoysia waits for soil temperatures closer to 70 degrees, often 2 to 4 weeks later than bermuda. Both go dormant when temperatures drop below 50 to 55 degrees in late fall. If you cannot stand a long brown lawn, bermuda gives you about 2 to 4 more weeks of green per year than zoysia in the same climate.',
    },
    {
      question: 'Which is better for kids and pets, bermuda or zoysia?',
      answer:
        'Bermudagrass is better for high-traffic lawns with active kids and dogs because it recovers faster from wear. A bermuda lawn that gets torn up by daily pet activity will repair itself in 5 to 10 days through stolons and rhizomes; zoysia takes 3 to 6 weeks to recover the same damage. The trade-off is bermuda needs more frequent mowing to look its best. If your lawn is the heart of family activity and you are willing to mow weekly, choose bermuda. If activity is lighter and you want a softer feel underfoot, choose zoysia.',
    },
    {
      question: 'How can you tell the difference between bermuda and zoysia?',
      answer:
        'The two easiest tells are blade feel and the seed head. Bermuda has fine, soft blades and a seed head with three to seven finger-like spikes that splay out like a bird\'s foot. Zoysia has stiffer, slightly prickly blades that feel rough when you rub them the wrong way, and a single short spike for a seed head. Bermuda also throws visible above-ground runners and looks more open, while zoysia forms a denser, more uniform carpet. When both grow side by side, zoysia is the one that feels stiff underfoot.',
    },
  ],
}

const kbgVsTallFescue: GrassComparisonEntry = {
  slug: 'kentucky-bluegrass-vs-tall-fescue',
  kind: 'grass',
  shortLabel: 'Kentucky Bluegrass vs Tall Fescue',
  h1: 'Kentucky Bluegrass vs Tall Fescue: Which Cool-Season Grass Wins?',
  metaTitle: 'Kentucky Bluegrass vs Tall Fescue: Which Wins for You?',
  metaDescription:
    'Tall fescue wins for heat, drought, and low upkeep; Kentucky bluegrass for cold and self-repair. Compare shade, water, and maintenance to pick yours.',
  lastModified: '2026-07-11',
  a: {
    key: 'Kentucky Bluegrass',
    guideSlug: 'kentucky-bluegrass',
    displayName: 'Kentucky Bluegrass',
  },
  b: { key: 'Tall Fescue', guideSlug: 'tall-fescue', displayName: 'Tall Fescue' },
  intro: (
    <>
      <p>
        Kentucky bluegrass and tall fescue are the two heavyweight cool-season grasses, and almost
        every northern-tier US homeowner ends up picking one or the other when they reseed or
        install new turf. Bluegrass is the postcard lawn: fine-bladed, deep blue-green,
        self-repairing through rhizomes, and the king of dense uniform carpets. Tall fescue is
        the work-horse: coarser-bladed, deeper-rooted, far more drought and heat tolerant, and
        increasingly the smart pick as summers get hotter.
      </p>
      <p>
        The decision usually breaks down on three axes. How cold are your winters? How hot and
        dry are your summers? And how much watering and fertilizing are you willing to do? Bluegrass
        wants colder winters, cooler summers, and a higher input lawn. Tall fescue handles
        transition-zone climates that would burn bluegrass out, requires roughly 30 percent less
        water, and asks for fewer fertilizer applications. The trade is appearance and feel:
        bluegrass forms a tighter, finer carpet that bare feet love.
      </p>
    </>
  ),
  verdict:
    'Kentucky bluegrass wins on appearance, density, and cold tolerance for cold-winter / mild-summer climates. Tall fescue wins on drought tolerance, deeper roots, and works in the transition zone where bluegrass struggles.',
  table: [
    { label: 'Climate zone (USDA)', a: '3-7 (cool-season)', b: '4-8 (cool-season, transition)' },
    { label: 'Sun requirement', a: '6+ hours full sun', b: '4-6 hours (handles part shade)' },
    { label: 'Shade tolerance', a: 'Low', b: 'Medium' },
    { label: 'Traffic tolerance', a: 'High (self-repairs)', b: 'High (deep roots)' },
    { label: 'Drought tolerance', a: 'Medium', b: 'High (3-foot roots)' },
    { label: 'Heat tolerance', a: 'Medium (browns above 90 deg)', b: 'High (handles summers up to 95)' },
    { label: 'Cold tolerance', a: 'Very high (winter survival in Zone 3)', b: 'High (survives Zone 4)' },
    { label: 'Mowing height', a: '2.5 to 3.5 inches', b: '3 to 4 inches' },
    { label: 'Annual nitrogen need', a: '3 to 5 lbs / 1,000 sq ft', b: '2 to 4 lbs / 1,000 sq ft' },
    { label: 'Water need (peak)', a: '1.5 inches / week', b: '1 to 1.25 inches / week' },
    { label: 'Spreading habit', a: 'Self-repairs through rhizomes', b: 'Bunch grass (does not spread)' },
    { label: 'Recovery from damage', a: 'Excellent (fills gaps on its own)', b: 'Slow (needs overseeding)' },
    { label: 'Blade width', a: 'Fine (2-4 mm)', b: 'Coarse (4-8 mm)' },
    { label: 'Establishment time', a: 'Slow (14 to 28 days)', b: 'Fast (7 to 14 days)' },
    { label: 'Maintenance level', a: 'High', b: 'Medium' },
  ],
  pickA: {
    headline: 'Pick Kentucky bluegrass if...',
    reasons: [
      'You live in the northern tier with cold winters and mild summers (Zones 3-6).',
      'You want the finest, most postcard-perfect lawn appearance and you do not mind paying for it in water and fertilizer.',
      'You have a high-traffic lawn and want the self-repairing rhizome network to fill in worn spots on its own.',
      'You can commit to weekly mowing and 4 or more fertilizer applications per year.',
      'Your lawn gets at least 6 hours of direct sun.',
    ],
  },
  pickB: {
    headline: 'Pick tall fescue if...',
    reasons: [
      'You live in the transition zone (Zones 6-8) where summers regularly hit 90+.',
      'You have water restrictions or want a lower water bill (about 30 percent less than bluegrass).',
      'Your lawn has partial shade from trees that bluegrass would thin out under.',
      'You want a tough, deep-rooted lawn that handles drought stress without going dormant.',
      'You prefer fewer fertilizer applications and less weekly maintenance.',
    ],
  },
  faqs: [
    {
      question: 'Is tall fescue better than Kentucky bluegrass for hot summers?',
      answer:
        'Tall fescue is significantly better than Kentucky bluegrass for hot summers. Tall fescue grows roots 3 feet deep or more, which lets it pull water from soil depths that bluegrass cannot reach. In a hot, dry summer, bluegrass will go dormant (brown) by mid-July without irrigation, while tall fescue stays green with the same or less water. In the transition zone (Zones 6-8), tall fescue is almost always the better cool-season pick.',
    },
    {
      question: 'Can you mix Kentucky bluegrass and tall fescue?',
      answer:
        'Yes, mixing Kentucky bluegrass and tall fescue is common and works well. A typical premium cool-season blend is 60 to 70 percent turf-type tall fescue plus 20 to 30 percent Kentucky bluegrass. The fescue provides drought tolerance and bulk, while the bluegrass adds fine texture and self-repair through rhizomes. In a mixed stand, the bluegrass will fill in any thin spots the bunching fescue leaves behind, and the result is more resilient than either grass alone.',
    },
    {
      question: 'Which is easier to grow from seed, bluegrass or tall fescue?',
      answer:
        'Tall fescue is significantly easier to grow from seed than Kentucky bluegrass. Tall fescue germinates in 7 to 14 days; bluegrass takes 14 to 28 days, sometimes longer in cool soil. Tall fescue also tolerates a wider range of soil and moisture conditions during establishment. For first-time DIY seeders, especially in fall when timing is tight, tall fescue is much more forgiving.',
    },
    {
      question: 'Does Kentucky bluegrass or tall fescue need more water?',
      answer:
        'Kentucky bluegrass needs significantly more water than tall fescue. Bluegrass has a shallow root system (6 to 8 inches deep) and requires about 1.5 inches of water per week in peak summer. Tall fescue has roots 2 to 3 feet deep and gets by on 1 to 1.25 inches per week, roughly 30 percent less. Over a full growing season in a 5,000 square foot lawn, switching from bluegrass to tall fescue saves about 5,000 to 8,000 gallons of water.',
    },
    {
      question: 'Which grass repairs damage faster?',
      answer:
        'Kentucky bluegrass repairs damage faster than tall fescue because it spreads through underground rhizomes that send up new shoots to fill gaps. Tall fescue is a bunch grass; it does not spread, so any bare spot has to be filled in by overseeding. If your lawn takes a lot of traffic that creates small dead spots, bluegrass self-repairs. If your damage is one big section (pet urine spot, equipment scar), you will overseed either way.',
    },
    {
      question: 'How do you tell Kentucky bluegrass from tall fescue?',
      answer:
        'Look at blade width and the leaf tip. Kentucky bluegrass has narrow, fine blades with a distinctive boat-shaped tip that looks like the prow of a canoe. Tall fescue has noticeably wider, coarser blades with prominent veins and a pointed tip. In a lawn, bluegrass forms a tight, fine, uniform carpet while tall fescue grows in coarser clumps. Bluegrass is also the one that fills in bare spots on its own through underground rhizomes, so if your lawn quietly self-repairs worn areas, it has bluegrass in it.',
    },
    {
      question: 'Is Kentucky bluegrass or tall fescue better for shade?',
      answer:
        'Tall fescue is the better choice for shade. It holds up in partial shade with about 4 to 6 hours of sun, while Kentucky bluegrass needs 6 or more hours of direct sun and thins out under tree cover. If your lawn has filtered light or north-facing areas, lean toward tall fescue or a fescue-heavy blend. For deep shade where even tall fescue struggles, fine fescue is the more shade-tolerant cool-season option.',
    },
    {
      question: 'Is tall fescue or Kentucky bluegrass cheaper?',
      answer:
        'Tall fescue is usually cheaper to establish from seed. Turf-type tall fescue seed runs roughly 3 to 5 dollars per pound and germinates in 7 to 14 days, while Kentucky bluegrass seed costs more per pound and takes 14 to 28 days to germinate, which means more watering before the lawn fills in. As sod the two are priced similarly. Tall fescue also costs less to maintain over time because it needs about 30 percent less water and fewer fertilizer applications.',
    },
  ],
}

const bermudaVsKentuckyBluegrass: GrassComparisonEntry = {
  slug: 'bermuda-vs-kentucky-bluegrass',
  kind: 'grass',
  shortLabel: 'Bermuda vs Kentucky Bluegrass',
  h1: 'Bermuda Grass vs Kentucky Bluegrass: Warm-Season vs Cool-Season',
  metaTitle: 'Bermuda vs Kentucky Bluegrass: Honest Comparison (2026)',
  metaDescription:
    'Bermuda vs Kentucky bluegrass compared: heat vs cold tolerance, winter color, water needs, maintenance, and which one fits your climate and lifestyle.',
  lastModified: '2026-08-05',
  a: { key: 'Bermudagrass', guideSlug: 'bermudagrass', displayName: 'Bermudagrass' },
  b: {
    key: 'Kentucky Bluegrass',
    guideSlug: 'kentucky-bluegrass',
    displayName: 'Kentucky Bluegrass',
  },
  intro: (
    <>
      <p>
        Bermudagrass and Kentucky bluegrass almost never belong in the same yard, and that is
        exactly why people search for the comparison. They sit on opposite sides of the warm-season
        versus cool-season divide. Bermuda is a heat-loving southern grass that thrives at 85 to 95
        degrees and goes completely brown all winter. Kentucky bluegrass is a cold-hardy northern
        grass that greens up early, holds color into late fall, and struggles in summer heat without
        regular watering. The only place the two genuinely compete is the transition zone (roughly
        Kansas east to Virginia), where summers are hot enough to stress bluegrass and winters are
        cold enough to risk bermuda winterkill.
      </p>
      <p>
        Outside that band the climate makes the decision for you: bermuda for the South and lower
        transition zone, bluegrass for the North. Inside it, the choice comes down to which season
        you care about most. Want a green lawn in July with minimal watering? That is bermuda. Want
        green in spring and fall and you are willing to irrigate through summer? That is bluegrass.
        We break down sun, water, winter color, maintenance, and cost below, and clear up the common
        mix-up between Kentucky bluegrass and "Kentucky 31," which is actually a tall fescue.
      </p>
    </>
  ),
  verdict:
    'Bermuda wins for hot southern summers, full sun, drought tolerance, and a green July lawn; it is the easier pick from Zone 7 south. Kentucky bluegrass wins for cold northern winters, early spring green-up, and a fine self-repairing carpet, but it needs more summer water. In the transition zone, choose by the season you most want green.',
  table: [
    { label: 'Season type', a: 'Warm-season', b: 'Cool-season' },
    { label: 'Climate zone (USDA)', a: '7-10 (warm-season)', b: '3-7 (cool-season)' },
    { label: 'Best region', a: 'South + lower transition zone', b: 'North + upper transition zone' },
    { label: 'Sun requirement', a: '6-8 hours full sun', b: '6+ hours full sun' },
    { label: 'Shade tolerance', a: 'Low', b: 'Low to medium' },
    { label: 'Traffic tolerance', a: 'Very high (recovers fastest)', b: 'High (self-repairs via rhizomes)' },
    { label: 'Drought tolerance', a: 'High', b: 'Medium (goes dormant in heat)' },
    { label: 'Heat tolerance', a: 'Very high (thrives 85-95 deg)', b: 'Medium (browns above 90 deg)' },
    { label: 'Cold tolerance', a: 'Low (winterkill risk in Zone 6)', b: 'Very high (survives Zone 3)' },
    { label: 'Winter color', a: 'Fully dormant / brown late Nov to early April', b: 'Holds color later; brief dormancy under hard cold' },
    { label: 'Peak green season', a: 'Summer (June to September)', b: 'Spring and fall' },
    { label: 'Mowing height', a: '0.5 to 1.5 inches (reel) / 1.5 to 2.5 inches (rotary)', b: '2.5 to 3.5 inches' },
    { label: 'Mowing frequency in peak season', a: 'Every 3 to 5 days', b: 'Every 5 to 7 days' },
    { label: 'Annual nitrogen need', a: '4 to 5 lbs / 1,000 sq ft', b: '3 to 5 lbs / 1,000 sq ft' },
    { label: 'Water need (peak)', a: '1.0 to 1.25 inches / week', b: '1.5 inches / week' },
    { label: 'Blade texture', a: 'Fine', b: 'Fine (boat-shaped tip)' },
    { label: 'Establishment from seed', a: 'Available (60 to 90 days)', b: 'Slow (14 to 28 days to germinate)' },
    { label: 'Maintenance level', a: 'High', b: 'High' },
  ],
  pickA: {
    headline: 'Pick Bermudagrass if...',
    reasons: [
      'You live in the South or lower transition zone (Zone 7 and warmer) with long, hot summers.',
      'Your lawn gets 6+ hours of direct sun with little to no tree cover.',
      'You want a lawn that stays green through July and August on less water.',
      'You have heavy foot traffic, kids, or dogs and need the fastest recovery from wear.',
      'You can accept a fully brown, dormant lawn from late fall through early spring.',
    ],
  },
  pickB: {
    headline: 'Pick Kentucky bluegrass if...',
    reasons: [
      'You live in the northern tier with cold winters and mild summers (Zones 3 to 6).',
      'You want the earliest spring green-up and color that holds into late fall.',
      'You want a fine-bladed, postcard carpet that self-repairs worn spots through rhizomes.',
      'You are willing to irrigate through summer heat to keep it from going dormant.',
      'You would rather have a green shoulder-season lawn than a green midsummer one.',
    ],
  },
  faqs: [
    {
      question: 'Can you grow bermuda and Kentucky bluegrass together?',
      answer:
        'No, growing bermuda and Kentucky bluegrass together does not work well because they peak in opposite seasons. Bermuda thrives in summer heat and goes brown all winter; bluegrass thrives in cool weather and stresses in summer. In a mixed lawn, one grass is always struggling or dormant while the other grows, so the lawn never looks uniform. Bermuda is also aggressive and will invade and overtake bluegrass in the sunny areas during summer. The only climate where mixing is ever considered is the transition zone, and even there it is better to pick one grass for the season you care about most.',
    },
    {
      question: 'Is Kentucky 31 the same as Kentucky bluegrass?',
      answer:
        'No, Kentucky 31 is not the same as Kentucky bluegrass. Kentucky 31 (often written K-31) is a cultivar of tall fescue, a completely different species. It is a coarse-bladed, deep-rooted, drought-tolerant bunch grass. Kentucky bluegrass is a fine-bladed cool-season grass that spreads by underground rhizomes. People confuse the two because both have "Kentucky" in the name, but they look and behave nothing alike. If you are comparing bermuda to "Kentucky 31," you are really comparing bermuda to tall fescue, not to Kentucky bluegrass.',
    },
    {
      question: 'Which is better in the transition zone, bermuda or Kentucky bluegrass?',
      answer:
        'In the transition zone neither grass is perfect, which is why this comparison is so common there. Bermuda survives the hot summers easily but goes brown for roughly 5 months in winter and risks winterkill in the colder pockets. Kentucky bluegrass survives the winter but needs heavy summer irrigation to avoid going dormant in the heat. For the warmer, lower half of the transition zone, lean toward bermuda. For the cooler, upper half, lean toward bluegrass or a bluegrass blend. Many transition-zone homeowners ultimately choose turf-type tall fescue instead, since it splits the difference better than either of these two.',
    },
    {
      question: 'Which grass stays green longer, bermuda or Kentucky bluegrass?',
      answer:
        'It depends entirely on your climate. In the South, bermuda stays green far longer because the warm season is long and bluegrass would not survive the heat at all. In the North, bluegrass stays green far longer; it greens up early in spring, holds color into late fall, and only browns in deep winter cold, while bermuda is not even viable. In the transition zone, bluegrass usually delivers more total green weeks per year because bermuda spends about 5 months brown and dormant, but bluegrass only achieves that with regular summer watering.',
    },
    {
      question: 'Does bermuda or Kentucky bluegrass need more water?',
      answer:
        'Kentucky bluegrass needs more water than bermuda. Bluegrass has a relatively shallow root system and requires about 1.5 inches per week in peak summer, and more than that during heat waves or it goes dormant. Bermuda is more drought tolerant, getting by on 1.0 to 1.25 inches per week thanks to deeper roots and warm-season efficiency. In the transition zone where the two actually compete, bermuda is the clearly lower-water choice through the hot months, while bluegrass front-loads its biggest water demand into exactly the time of year water is most scarce.',
    },
    {
      question: 'How do you tell bermuda from Kentucky bluegrass?',
      answer:
        'Season and growth habit make it obvious. Bermuda is brown and dormant all winter and explodes with growth in summer heat, throwing visible above-ground runners (stolons) and a seed head with finger-like spikes that splay out like a bird\'s foot. Kentucky bluegrass is green in cool weather, has fine blades with a distinctive boat-shaped tip that looks like the prow of a canoe, and spreads by underground rhizomes with no surface runners. The simplest test: if the lawn is brown in January in a cold climate but lush in July, it is bermuda; if it is green in spring and fall but stressed in midsummer, it is Kentucky bluegrass.',
    },
  ],
}

const stAugustineVsZoysia: GrassComparisonEntry = {
  slug: 'st-augustine-vs-zoysia',
  kind: 'grass',
  shortLabel: 'St. Augustine vs Zoysia',
  h1: 'St. Augustine vs Zoysia Grass: Which Should You Plant?',
  metaTitle: 'St. Augustine vs Zoysia Grass: Side-by-Side Comparison (2026)',
  metaDescription:
    'St. Augustine vs zoysia grass compared head-to-head: shade tolerance, salt tolerance, traffic, maintenance, cold tolerance, and which one fits your yard.',
  lastModified: '2026-08-05',
  a: {
    key: 'St. Augustinegrass',
    guideSlug: 'st-augustinegrass',
    displayName: 'St. Augustinegrass',
  },
  b: { key: 'Zoysiagrass', guideSlug: 'zoysiagrass', displayName: 'Zoysiagrass' },
  intro: (
    <>
      <p>
        St. Augustinegrass and zoysiagrass are the two warm-season grasses homeowners most often
        compare when their lawn has any meaningful shade. Both handle dappled sunlight better than
        bermudagrass, both form dense carpets, and both are sold almost exclusively as sod (very
        few homeowners successfully seed either). The differences emerge in cold tolerance, salt
        tolerance, texture, and how the lawn behaves once it has established.
      </p>
      <p>
        St. Augustine has the broadest blades of any common US lawn grass, gives a tropical
        coarse-textured appearance, and is the king of coastal salt-spray and dense shade in the
        Gulf Coast and Florida. Zoysia is finer-bladed, tougher under foot, and tolerates colder
        winters than St. Augustine, making it the better pick in the upper South and transition
        zone. Both ask less of you than bermudagrass; pick between them based on climate and
        appearance.
      </p>
    </>
  ),
  verdict:
    'St. Augustine wins on shade tolerance, salt tolerance, and tropical appearance for Gulf Coast and Florida lawns. Zoysia wins on cold tolerance, traffic tolerance, and finer texture for the upper South and transition zone.',
  table: [
    { label: 'Climate zone (USDA)', a: '8-10 (warm, sub-tropical)', b: '6-10 (warm, transition)' },
    { label: 'Sun requirement', a: '4-6 hours (best shade tolerance of warm-season)', b: '4-6 hours (medium shade tolerance)' },
    { label: 'Shade tolerance', a: 'High', b: 'Medium' },
    { label: 'Salt tolerance', a: 'High (coastal favorite)', b: 'Medium' },
    { label: 'Traffic tolerance', a: 'Medium', b: 'High' },
    { label: 'Drought tolerance', a: 'Medium', b: 'Very high' },
    { label: 'Cold tolerance', a: 'Low (damage below 25 deg F)', b: 'Medium (survives Zone 6 winters)' },
    { label: 'Mowing height', a: '3 to 4 inches', b: '1 to 2 inches' },
    { label: 'Annual nitrogen need', a: '3 to 4 lbs / 1,000 sq ft', b: '2 to 3 lbs / 1,000 sq ft' },
    { label: 'Water need (peak)', a: '1 to 1.5 inches / week', b: '0.75 to 1.0 inches / week' },
    { label: 'Spreading habit', a: 'Stolons (aboveground only)', b: 'Both rhizomes and stolons' },
    { label: 'Blade width', a: 'Coarse (8-10 mm)', b: 'Medium (3-5 mm)' },
    { label: 'Establishment from sod', a: '3 to 5 weeks', b: '4 to 8 weeks' },
    { label: 'Disease pressure', a: 'High (chinch bugs, gray leaf spot)', b: 'Low to medium' },
    { label: 'Maintenance level', a: 'Medium-high', b: 'Medium' },
  ],
  pickA: {
    headline: 'Pick St. Augustinegrass if...',
    reasons: [
      'You live in Zone 8 or warmer (Gulf Coast, Florida, coastal Texas, Southern California).',
      'Your lawn has dense shade from oaks or palms; St. Augustine handles deeper shade than any other warm-season grass.',
      'You live within a few miles of the coast and need salt-spray tolerance.',
      'You want a tropical, coarse-textured lawn appearance with broad blades.',
      'You can commit to monitoring for chinch bugs and gray leaf spot, which hit St. Augustine harder than other grasses.',
    ],
  },
  pickB: {
    headline: 'Pick zoysiagrass if...',
    reasons: [
      'You live in the upper South or transition zone (Zones 6 and 7) where winters get cold enough to damage St. Augustine.',
      'You want a finer-textured, more carpet-like lawn appearance.',
      'You have moderate to high traffic from kids, dogs, or play; zoysia handles wear better.',
      'You want lower disease pressure and fewer pest issues than St. Augustine.',
      'You prefer a denser, lower-mowed appearance (1 to 2 inches vs St. Augustine\'s 3 to 4).',
    ],
  },
  faqs: [
    {
      question: 'Is zoysia or St. Augustine more shade tolerant?',
      answer:
        'St. Augustinegrass is more shade tolerant than zoysia. St. Augustine handles 4 hours of dappled sun and still maintains acceptable density, while zoysia needs at least 4 to 6 hours of direct light. For lawns under mature oak or palm canopy, St. Augustine is the only warm-season grass that consistently performs. Zoysia in similar shade will thin out within 2 to 3 growing seasons.',
    },
    {
      question: 'Which is more cold tolerant, zoysia or St. Augustine?',
      answer:
        'Zoysiagrass is significantly more cold tolerant than St. Augustinegrass. Zoysia survives winters down to Zone 6 (regular lows in the single digits) and recovers reliably the next spring. St. Augustine begins suffering damage below 25 degrees Fahrenheit and can be killed outright by hard freezes in the 15 to 20 degree range. If you are in Zone 6 or 7, zoysia is the safer pick. St. Augustine is realistically a Zone 8+ grass.',
    },
    {
      question: 'Which grass is better for the beach or coastal lawns?',
      answer:
        'St. Augustinegrass is the better coastal pick because of its high salt tolerance. St. Augustine handles direct salt spray from ocean winds, brackish irrigation water, and storm surge better than any other common warm-season grass. Zoysia has medium salt tolerance and works further inland but struggles within a few hundred feet of the surf. The Floratam, Palmetto, and Seville cultivars of St. Augustine are specifically bred for coastal conditions.',
    },
    {
      question: 'Does St. Augustine or zoysia have worse pest problems?',
      answer:
        'St. Augustinegrass has significantly worse pest pressure than zoysia. Chinch bugs are the primary issue, causing yellow-to-brown patches that can wipe out an entire lawn during summer if untreated. St. Augustine is also more susceptible to gray leaf spot disease, especially in humid Gulf Coast and Florida summers. Zoysia has comparatively low disease and insect pressure, with billbugs being the main pest concern, and they rarely cause whole-lawn damage. Plan on at least one preventive insecticide application per year for St. Augustine in Florida; zoysia often gets by with none.',
    },
    {
      question: 'Which is more expensive, St. Augustine or zoysia?',
      answer:
        'Zoysia is generally more expensive than St. Augustine. A 500 square foot pallet of zoysia sod runs $300 to $500; St. Augustine runs $200 to $400 for the same coverage. Both are almost always installed as sod (seeding success is poor for both). Over the long run, zoysia costs less to maintain because of lower fertilizer and pest-control needs, but the upfront investment is higher.',
    },
    {
      question: 'How can you tell zoysia from St. Augustine?',
      answer:
        'Blade width separates them instantly. St. Augustine has broad, flat blades and thick above-ground runners that give it a coarse, tropical look. Zoysia has much finer blades and forms a denser, stiffer carpet that feels prickly compared to St. Augustine\'s softer texture. St. Augustine spreads by surface stolons only, while zoysia spreads by both stolons and underground rhizomes, so zoysia knits into a tighter, more even surface.',
    },
    {
      question: 'Will zoysia take over St. Augustine, or can you mix the two?',
      answer:
        'In a mixed lawn the outcome depends on conditions, not a fixed winner. In warm, irrigated, high-fertility full sun, St. Augustine can actually out-spread zoysia through its fast surface stolons, while zoysia is slow to spread laterally but its density helps it hold ground over time. In shade, St. Augustine has the clear edge because zoysia thins out without enough light. Cultivar, mowing height, and fertility all shift the balance. Neither blends into a uniform lawn long-term, so if you want one grass throughout, match it to your dominant light condition rather than trying to maintain a mix.',
    },
  ],
}

const bermudaVsStAugustine: GrassComparisonEntry = {
  slug: 'bermuda-vs-st-augustine',
  kind: 'grass',
  shortLabel: 'Bermuda vs St. Augustine',
  h1: 'Bermuda vs St. Augustine Grass: Which Should You Plant?',
  metaTitle: 'Bermuda vs St. Augustine Grass: Side-by-Side Comparison (2026)',
  metaDescription:
    'Bermuda vs St. Augustine grass compared: shade tolerance, salt tolerance, traffic, mowing, water, pests, and which one fits your Southern lawn best.',
  lastModified: '2026-08-06',
  a: { key: 'Bermudagrass', guideSlug: 'bermudagrass', displayName: 'Bermudagrass' },
  b: {
    key: 'St. Augustinegrass',
    guideSlug: 'st-augustinegrass',
    displayName: 'St. Augustinegrass',
  },
  intro: (
    <>
      <p>
        Bermudagrass and St. Augustinegrass are the two most common Southern lawn choices, and the
        decision usually comes down to one question: how much shade does your lawn get? Bermuda
        is a full-sun athlete that recovers from foot traffic faster than any other warm-season
        grass, but thins out the moment a tree starts casting shade. St. Augustine is the opposite
        story: the only warm-season grass that handles 4 hours of dappled sun without thinning, but
        slower to recover from heavy traffic and more susceptible to chinch bugs and disease.
      </p>
      <p>
        Beyond the shade question, the lawns look and feel completely different. Bermuda has fine,
        wiry blades and mows down to half an inch with a reel mower for that golf-course look. St.
        Augustine has the broadest blades of any common US lawn grass and gives a tropical, coarse-
        textured appearance you mow at 3 to 4 inches. Bermuda needs more fertilizer and more mowing;
        St. Augustine needs more pest scouting and more sod replacement after a hard winter. Pick
        based on your trees, your traffic, and your tolerance for chinch bug season.
      </p>
    </>
  ),
  verdict:
    'Bermuda wins for full-sun, high-traffic lawns and the lowest mowing-height golf-course look. St. Augustine wins for shaded yards, coastal salt spray, and the tropical broad-bladed appearance.',
  table: [
    { label: 'Climate zone (USDA)', a: '7-10 (warm-season)', b: '8-10 (warm, sub-tropical)' },
    { label: 'Sun requirement', a: '6-8 hours full sun', b: '4-6 hours (best shade tolerance of warm-season)' },
    { label: 'Shade tolerance', a: 'Low', b: 'High' },
    { label: 'Salt tolerance', a: 'Medium', b: 'High (coastal favorite)' },
    { label: 'Traffic tolerance', a: 'Very high (recovers fastest)', b: 'Medium' },
    { label: 'Drought tolerance', a: 'High', b: 'Medium' },
    { label: 'Cold tolerance', a: 'Medium (Zone 7 hybrids OK)', b: 'Low (damage below 25 deg F)' },
    { label: 'Mowing height', a: '0.5 to 1.5 inches (reel) / 1.5 to 2.5 (rotary)', b: '3 to 4 inches' },
    { label: 'Mowing frequency in peak season', a: 'Every 3 to 5 days', b: 'Every 7 to 10 days' },
    { label: 'Annual nitrogen need', a: '4 to 5 lbs / 1,000 sq ft', b: '3 to 4 lbs / 1,000 sq ft' },
    { label: 'Water need (peak)', a: '1.0 to 1.25 inches / week', b: '1.0 to 1.5 inches / week' },
    { label: 'Spreading habit', a: 'Both rhizomes and stolons', b: 'Stolons only (aboveground)' },
    { label: 'Blade width', a: 'Fine (1-3 mm)', b: 'Coarse (8-10 mm)' },
    { label: 'Disease/pest pressure', a: 'Low to medium', b: 'High (chinch bugs, gray leaf spot)' },
    { label: 'Establishment options', a: 'Seed, sod, or sprigs', b: 'Sod or plugs only (no viable seed sold)' },
    { label: 'Establishment cost (typical)', a: 'Seed ~$25-40 / 1,000 sq ft; sod ~$0.35-0.60 / sq ft', b: 'Sod ~$0.45-0.75 / sq ft; plugs cheaper but slower' },
    { label: 'Maintenance level', a: 'High', b: 'Medium-high' },
  ],
  pickA: {
    headline: 'Pick Bermudagrass if...',
    reasons: [
      'Your lawn gets 6+ hours of direct sun every day with no significant tree shade.',
      'You have kids, dogs, or high-traffic backyard activity and need a lawn that recovers from wear in days.',
      'You want the golf-course look with blades cut to 1 inch or shorter.',
      'You prefer fine-bladed turf over the broad-bladed tropical look.',
      'You are willing to mow every 3 to 5 days during the peak growing season.',
    ],
  },
  pickB: {
    headline: 'Pick St. Augustinegrass if...',
    reasons: [
      'Your lawn has significant tree shade and bermuda has already thinned out there.',
      'You live within a few miles of the coast and need salt-spray tolerance.',
      'You live in Zone 9 or 10 and never see hard freezes that would damage St. Augustine.',
      'You want the tropical, broad-bladed lawn appearance and a taller mow height (3 to 4 inches).',
      'You can budget for at least one preventive chinch bug treatment per summer.',
    ],
  },
  faqs: [
    {
      question: 'Can St. Augustine grass take over bermudagrass?',
      answer:
        'St. Augustine can crowd out bermuda in shaded areas, but bermuda almost always wins in full sun. The two grasses partition by sun exposure: in dense shade where bermuda thins out, St. Augustine fills in via its aggressive stolons. In open sun, bermuda is the more aggressive spreader and will invade St. Augustine over time. If you want one or the other to dominate the whole lawn, you need to match the grass to the lighting condition; trying to convert one to the other while the underlying sun pattern stays the same does not work.',
    },
    {
      question: 'Which is easier to maintain, bermuda or St. Augustine?',
      answer:
        'Neither is a low-maintenance grass; they are roughly tied but have different demands. Bermuda needs more mowing (every 3 to 5 days in summer vs every 7 to 10 for St. Augustine) and more fertilizer (4 to 5 lbs nitrogen per year vs 3 to 4 for St. Augustine). St. Augustine needs less mowing but more pest scouting; chinch bugs can wipe out an entire St. Augustine lawn in 2 to 3 weeks if untreated, and gray leaf spot disease is common in humid Gulf Coast summers. For a low-maintenance Southern lawn, centipede is the better choice than either of these.',
    },
    {
      question: 'Will St. Augustine survive a Texas winter?',
      answer:
        'St. Augustine survives most Texas winters in the southern half of the state (south of roughly Austin), but is at risk in central and north Texas where hard freezes are common. The 2021 February freeze killed wide swaths of St. Augustine across the entire state, including normally safe areas. If you live north of Austin or in the Panhandle, bermuda or zoysia is the safer pick. South Texas and the coast can rely on St. Augustine year over year.',
    },
    {
      question: 'Is bermuda or St. Augustine better for backyards with dogs?',
      answer:
        'Bermudagrass is significantly better for backyards with dogs. Bermuda recovers from worn paths and dig spots in 5 to 10 days through its rhizome-and-stolon network; St. Augustine takes 3 to 6 weeks to recover the same damage and the stolons can be torn out by determined diggers. Dog urine spots affect both grasses similarly, but the faster overall recovery makes bermuda the right choice for active dog households.',
    },
    {
      question: 'Can you overseed St. Augustine with bermuda?',
      answer:
        'Overseeding St. Augustine with bermuda is not recommended. The two grasses thrive in opposite shade conditions, so a mixed lawn will end up with bermuda taking the sunny patches and St. Augustine dominating the shade. Worse, common bermuda seed is hard to eradicate once it establishes, so you cannot easily reverse the decision later. If you have a St. Augustine lawn that is thinning, the right move is plug or sod-patch with more St. Augustine, or replace the dead area completely with bermuda if the cause is too much sun for St. Augustine to handle.',
    },
    {
      question: 'Is bermuda or St. Augustine grass cheaper?',
      answer:
        'Bermuda is cheaper to establish because it is the only one of the two you can start from seed, at roughly $25 to $40 per 1,000 square feet versus $450 to $750 for the same area in St. Augustine sod. St. Augustine has no viable retail seed, so sod or plugs are the only options. Ongoing costs flip slightly the other way: bermuda wants more fertilizer and far more frequent mowing, while St. Augustine typically needs a preventive chinch bug treatment each summer. Over a five year horizon the establishment savings still leave bermuda the cheaper lawn for most yards.',
    },
    {
      question: 'Is bermuda or St. Augustine better for Florida lawns?',
      answer:
        'St. Augustine is the default Florida lawn for good reason: it handles the shade from live oaks and palms, tolerates salt spray near both coasts, and Florida rarely sees the hard freezes that damage it. Floratam, the most common Florida variety, wants full sun, while Palmetto and Seville handle shadier lots. Bermuda makes sense in Florida mainly for full-sun, high-traffic yards and athletic-style lawns in the central and northern parts of the state. If your lot has any meaningful tree canopy, St. Augustine is the safer Florida pick.',
    },
    {
      question: 'How can you tell if you have bermuda or St. Augustine grass?',
      answer:
        'Blade width is the giveaway. St. Augustine has very broad, flat blades (the widest of any common US lawn grass) and thick, visible above-ground runners you can trace with a finger. Bermuda has fine, wiry blades a fraction of that width and a bird\'s-foot seed head with several finger-like spikes. If the lawn looks coarse and tropical with chunky surface runners, it is St. Augustine; if it looks fine and dense like a golf fairway, it is bermuda.',
    },
  ],
}

const fescueVsBermuda: GrassComparisonEntry = {
  slug: 'fescue-vs-bermuda',
  kind: 'grass',
  shortLabel: 'Fescue vs Bermuda',
  h1: 'Tall Fescue vs Bermuda Grass: Which Is Better for Your Lawn?',
  metaTitle: 'Tall Fescue vs Bermuda Grass: Honest Comparison (2026)',
  metaDescription:
    'Tall fescue vs bermuda grass compared: climate fit, winter color, water, mowing, shade, and which one is right for the transition zone.',
  lastModified: '2026-08-05',
  a: { key: 'Tall Fescue', guideSlug: 'tall-fescue', displayName: 'Tall Fescue' },
  b: { key: 'Bermudagrass', guideSlug: 'bermudagrass', displayName: 'Bermudagrass' },
  intro: (
    <>
      <p>
        Tall fescue and bermudagrass sit on opposite sides of the cool-season / warm-season divide,
        and the right pick is mostly a function of where you live. Tall fescue is the workhorse
        cool-season grass: deep-rooted, drought-tolerant, handles partial shade, and stays green
        nearly year-round. Bermuda is the warm-season king: full-sun loving, recovers from traffic
        faster than anything else, but goes completely brown from late fall through early spring.
      </p>
      <p>
        In most US climates, geography decides for you. North of the transition zone (Zone 6 and
        colder) bermuda struggles to survive winter; south of Zone 8 fescue cooks in summer heat.
        The interesting decision is the transition zone, that band running roughly from Kentucky
        and Tennessee through Northern Texas, where either grass can theoretically work. There the
        decision comes down to a single question: do you want a lawn that stays green all year
        (fescue) or one that is bullet-proof in summer heat but brown for four months (bermuda)?
        We break down both choices below.
      </p>
    </>
  ),
  verdict:
    'Tall fescue wins for the transition zone, cool-season climates, partial shade, and year-round green color. Bermuda wins for full-sun lawns in Zone 7+ where summer heat and high traffic outpace what fescue can handle.',
  table: [
    { label: 'Climate zone (USDA)', a: '4-8 (cool-season, transition)', b: '7-10 (warm-season)' },
    { label: 'Sun requirement', a: '4-6 hours (handles part shade)', b: '6-8 hours full sun' },
    { label: 'Shade tolerance', a: 'Medium', b: 'Low' },
    { label: 'Traffic tolerance', a: 'High (deep roots)', b: 'Very high (recovers fastest)' },
    { label: 'Drought tolerance', a: 'High (3-foot roots)', b: 'High' },
    { label: 'Heat tolerance', a: 'High (handles 95 deg)', b: 'Excellent (thrives at 95+)' },
    { label: 'Winter color', a: 'Green year-round', b: 'Brown / dormant Nov to April' },
    { label: 'Mowing height', a: '3 to 4 inches', b: '0.5 to 1.5 inches (reel) / 1.5 to 2.5 (rotary)' },
    { label: 'Mowing frequency in peak season', a: 'Every 5 to 7 days', b: 'Every 3 to 5 days' },
    { label: 'Annual nitrogen need', a: '2 to 4 lbs / 1,000 sq ft', b: '4 to 5 lbs / 1,000 sq ft' },
    { label: 'Water need (peak)', a: '1 to 1.25 inches / week', b: '1.0 to 1.25 inches / week' },
    { label: 'Spreading habit', a: 'Bunch grass (does not spread)', b: 'Both rhizomes and stolons' },
    { label: 'Recovery from damage', a: 'Slow (needs overseeding)', b: 'Excellent (fills gaps on its own)' },
    { label: 'Establishment from seed', a: '7 to 14 days', b: '14 to 30 days' },
    { label: 'Maintenance level', a: 'Medium', b: 'High' },
  ],
  pickA: {
    headline: 'Pick tall fescue if...',
    reasons: [
      'You live in the transition zone (Kentucky, Tennessee, Virginia, Carolinas) or anywhere north of it.',
      'You want a lawn that stays green year-round and cannot stand 4 months of brown dormancy.',
      'Your lawn has partial shade from trees that bermuda would thin out under.',
      'You want lower mowing and fertilizing maintenance than bermuda requires.',
      'You have water restrictions; fescue uses about the same water as bermuda but does not need a separate dormancy-recovery feed cycle.',
    ],
  },
  pickB: {
    headline: 'Pick bermudagrass if...',
    reasons: [
      'You live in Zone 8 or warmer where summers regularly hit 95+ and fescue browns out by July.',
      'Your lawn gets 6+ hours of direct sun with no significant tree shade.',
      'You have kids, dogs, or heavy backyard traffic and need the fastest recovery from wear.',
      'You are willing to accept full winter dormancy (brown lawn November to April) in exchange for summer toughness.',
      'You want the golf-course look with a sub-inch mow height; fescue cannot be cut anywhere near that low.',
    ],
  },
  faqs: [
    {
      question: 'Can tall fescue and bermuda grow together?',
      answer:
        'Tall fescue and bermuda do not coexist well long-term. In the transition zone where both can theoretically survive, they take turns dominating with the seasons: bermuda is aggressive in summer heat and takes over, fescue rebounds in fall and spring but loses ground each summer. After 2 to 3 years a mixed lawn usually settles into bermuda dominating the sunny areas and fescue clinging only to the shadiest patches. Pick one or the other for the bulk of the lawn rather than trying to maintain a mix.',
    },
    {
      question: 'Will bermuda kill my fescue lawn in the transition zone?',
      answer:
        'Yes, bermuda will outcompete fescue in the transition zone if it gets a foothold. Common bermuda seed is widespread (it blows in from roadsides, comes in topsoil, hitches a ride on lawn mowers), and once it establishes in a fescue lawn it spreads aggressively in summer when fescue is heat-stressed. If you want a long-term fescue lawn in the transition zone, you need to apply a bermuda-suppressing pre-emergent like Specticle or Roundup-with-MSO spot treatments to keep bermuda out.',
    },
    {
      question: 'Which is easier to seed, bermuda or tall fescue?',
      answer:
        'Tall fescue is significantly easier to seed than bermuda. Tall fescue germinates in 7 to 14 days, establishes a full lawn in 60 to 90 days, and tolerates a wide range of soil and moisture conditions during establishment. Bermuda germinates in 14 to 30 days, requires soil temperatures above 65 degrees Fahrenheit to start at all, and needs consistent moisture for a much longer establishment window. For DIY seeders, fescue is more forgiving on every dimension.',
    },
    {
      question: 'Does fescue or bermuda use less water?',
      answer:
        'Tall fescue and bermuda use roughly the same amount of water per week in their respective growing seasons (about 1 to 1.25 inches), but bermuda gets 4 to 5 months off entirely while dormant in winter, so total annual water use is lower for bermuda. The trade is that fescue stays green using that water year-round while bermuda goes brown. If your water cost is high, bermuda saves money on irrigation; if you value the year-round green appearance, fescue is worth the extra irrigation.',
    },
    {
      question: 'Is fescue or bermuda better for shade?',
      answer:
        'Tall fescue is significantly better for shade than bermuda. Tall fescue tolerates 4 to 6 hours of direct sun and stays acceptable under mature tree canopy; bermuda needs 6 to 8 hours of full sun and thins dramatically with any meaningful shade. If your lawn has trees, fescue is the right pick regardless of climate. The only exception is St. Augustine or zoysia in Zone 8+, both of which handle shade better than either bermuda or fescue.',
    },
    {
      question: 'What is the difference between fescue and bermuda grass, and how do you tell them apart?',
      answer:
        'The simplest tell is the season. Tall fescue holds its green color through the winter in the transition zone, while bermuda goes completely brown and dormant from late fall through early spring, so a green winter lawn beside a brown one usually marks fescue versus bermuda. Up close, fescue has wide, coarse blades and grows in upright clumps, while bermuda has fine, wiry blades, spreads with above-ground runners, and produces a bird\'s-foot seed head. Fescue is cool-season and bunch-type; bermuda is warm-season and aggressive.',
    },
  ],
}

const fineVsTallFescue: GrassComparisonEntry = {
  slug: 'fine-vs-tall-fescue',
  kind: 'grass',
  shortLabel: 'Fine Fescue vs Tall Fescue',
  h1: 'Fine Fescue vs Tall Fescue: Differences, Pros and Cons',
  metaTitle: 'Fine Fescue vs Tall Fescue: Honest Comparison (2026)',
  metaDescription:
    'Fine fescue vs tall fescue compared: shade tolerance, drought tolerance, traffic, blade texture, maintenance, and which fescue fits your lawn.',
  lastModified: '2026-05-15',
  a: { key: 'Fine Fescue', guideSlug: 'fine-fescue', displayName: 'Fine Fescue' },
  b: { key: 'Tall Fescue', guideSlug: 'tall-fescue', displayName: 'Tall Fescue' },
  intro: (
    <>
      <p>
        Fine fescue and tall fescue share a name and a cool-season label, but the two grasses
        behave like distant cousins rather than siblings. Fine fescue is a group (creeping red,
        chewings, hard, and sheep fescue) defined by their needle-thin blades, exceptional shade
        tolerance, and very low maintenance demands. Tall fescue is a single species defined by
        deep roots, drought tolerance, traffic tolerance, and coarse texture. Both stay green in
        winter, both handle the cool-season range, but they fit completely different niches.
      </p>
      <p>
        Fine fescue is the right pick for shaded, low-traffic areas where you want a fine-textured
        lawn that needs minimal fertilizer and almost no mowing. Tall fescue is the right pick for
        open, high-traffic lawns where deep roots and durability matter. The premium cool-season
        seed blends most homeowners buy actually contain both: tall fescue as the structural bulk
        plus a fine fescue percentage to handle the shaded edges where tall fescue thins out.
      </p>
    </>
  ),
  verdict:
    'Fine fescue wins for shade, low-maintenance lawns, and fine-textured appearance. Tall fescue wins for high-traffic, full-sun-tolerant cool-season lawns with deep drought-resistant roots.',
  table: [
    { label: 'Climate zone (USDA)', a: '3-7 (cool-season)', b: '4-8 (cool-season, transition)' },
    { label: 'Sun requirement', a: '3-5 hours (best shade tolerance of cool-season)', b: '4-6 hours (handles part shade)' },
    { label: 'Shade tolerance', a: 'Very high', b: 'Medium' },
    { label: 'Traffic tolerance', a: 'Low', b: 'High (deep roots)' },
    { label: 'Drought tolerance', a: 'High (once established)', b: 'High (3-foot roots)' },
    { label: 'Heat tolerance', a: 'Low (browns above 85 deg F)', b: 'High (handles 95 deg F)' },
    { label: 'Cold tolerance', a: 'Excellent (Zone 3)', b: 'High (Zone 4)' },
    { label: 'Mowing height', a: '2.5 to 3.5 inches (often left taller)', b: '3 to 4 inches' },
    { label: 'Mowing frequency in peak season', a: 'Every 10 to 14 days', b: 'Every 5 to 7 days' },
    { label: 'Annual nitrogen need', a: '1 to 2 lbs / 1,000 sq ft', b: '2 to 4 lbs / 1,000 sq ft' },
    { label: 'Water need (peak)', a: '0.75 to 1 inch / week', b: '1 to 1.25 inches / week' },
    { label: 'Spreading habit', a: 'Creeping red spreads; others bunch', b: 'Bunch grass (does not spread)' },
    { label: 'Blade width', a: 'Very fine (1-2 mm, needle-thin)', b: 'Coarse (4-8 mm)' },
    { label: 'Establishment from seed', a: '7 to 14 days', b: '7 to 14 days' },
    { label: 'Maintenance level', a: 'Low', b: 'Medium' },
  ],
  pickA: {
    headline: 'Pick fine fescue if...',
    reasons: [
      'You have a shaded lawn under mature trees where tall fescue thins out.',
      'You want the lowest-maintenance cool-season lawn possible (low water, low fertilizer, infrequent mowing).',
      'You like the fine, soft, needle-thin blade texture and do not need a coarse durable lawn.',
      'Traffic is light; you do not have kids or dogs running paths through the lawn daily.',
      'You live in Zone 3 to 6 where summers stay mostly under 85 degrees.',
    ],
  },
  pickB: {
    headline: 'Pick tall fescue if...',
    reasons: [
      'You have a sunny or partly-sunny lawn that gets meaningful traffic.',
      'You live in the transition zone (Zones 6 to 8) where summers regularly hit 90 degrees.',
      'You want a deep-rooted, drought-tolerant lawn that stays green through summer heat.',
      'You prefer the bulkier feel of a coarse-bladed lawn over the wispy texture of fine fescue.',
      'You can commit to weekly mowing during the growing season.',
    ],
  },
  faqs: [
    {
      question: 'Can you mix fine fescue and tall fescue?',
      answer:
        'Yes, mixing fine fescue and tall fescue is one of the most common premium seed blends. A typical Northern blend is 60 to 70 percent turf-type tall fescue plus 20 to 30 percent fine fescue (often creeping red), with the fine fescue specifically positioned to fill in the shaded edges where tall fescue would thin out. This gives a lawn that handles both sun and shade in the same yard without obvious patches between zones.',
    },
    {
      question: 'Is fine fescue or tall fescue more shade tolerant?',
      answer:
        'Fine fescue is significantly more shade tolerant than tall fescue. Fine fescue handles 3 to 5 hours of direct sun and stays acceptable under mature tree canopy with dappled light; tall fescue needs 4 to 6 hours of direct sun and thins noticeably in deep shade. For lawns under heavy tree cover, fine fescue is the only cool-season grass that consistently performs.',
    },
    {
      question: 'Does fine fescue or tall fescue handle traffic better?',
      answer:
        'Tall fescue handles traffic significantly better than fine fescue. Tall fescue has deep roots (2 to 3 feet) and a coarse blade that bounces back from foot traffic; fine fescue has shallow roots and fine blades that bruise easily under heavy use. For lawns with kids, dogs, or play activity, tall fescue is the right pick. Fine fescue is for lower-traffic areas like shaded ornamental lawns or under-tree understory.',
    },
    {
      question: 'Which fescue needs less mowing?',
      answer:
        'Fine fescue needs significantly less mowing than tall fescue. Fine fescue grows slowly and is often mowed only every 10 to 14 days in peak season; many homeowners let it grow longer and treat it almost like a no-mow lawn at 4 inches or taller. Tall fescue grows much faster and typically needs mowing every 5 to 7 days in peak season. If reducing your mowing schedule is a priority, fine fescue is the lower-maintenance pick.',
    },
    {
      question: 'Will fine fescue survive a hot summer?',
      answer:
        'Fine fescue survives most cool-climate summers but struggles in the transition zone and hot summers. Fine fescue browns out and goes semi-dormant above 85 degrees Fahrenheit, especially without consistent water. In Zones 3 to 6 fine fescue handles summer fine; in Zones 7 and 8 it usually thins out by August and needs fall overseeding to recover. If you live in a region that regularly sees 90+ degree summers, tall fescue is the more reliable cool-season choice.',
    },
  ],
}

const centipedeVsStAugustine: GrassComparisonEntry = {
  slug: 'centipede-vs-st-augustine',
  kind: 'grass',
  shortLabel: 'Centipede vs St. Augustine',
  h1: 'Centipede vs St. Augustine Grass: Which Southern Lawn Is Right for You?',
  metaTitle: 'Centipede vs St. Augustine: Which Grass Is Better? (2026)',
  metaDescription:
    'Centipede vs St. Augustine grass compared: maintenance level, fertilizer, soil pH, shade, traffic, pests, and which one fits your Southern lawn.',
  lastModified: '2026-08-05',
  a: { key: 'Centipede', guideSlug: 'centipede', displayName: 'Centipedegrass' },
  b: {
    key: 'St. Augustinegrass',
    guideSlug: 'st-augustinegrass',
    displayName: 'St. Augustinegrass',
  },
  intro: (
    <>
      <p>
        Centipedegrass and St. Augustinegrass are both Southern warm-season grasses, but they sit
        at opposite ends of the maintenance spectrum. Centipede is famously nicknamed the "lazy
        man's grass" because it grows slowly, needs only 1 to 2 pounds of nitrogen per 1,000 square
        feet per year (the lowest of any common lawn grass), and tolerates acidic soil that would
        struggle to grow anything else. St. Augustine is the vigorous spreader that handles dense
        shade and coastal salt spray but demands more fertilizer, more pest management, and more
        attention overall.
      </p>
      <p>
        Pick centipede when you want a Southern lawn that just exists with minimal intervention,
        especially on acidic, poorer soil in the Coastal Plain. Pick St. Augustine when you have
        significant tree shade, live near the coast, or need a more vigorous grass that recovers
        from damage faster. Neither handles heavy traffic well; for high-traffic lawns in the
        same climate range, bermuda or zoysia is the better pick.
      </p>
    </>
  ),
  verdict:
    'Centipede wins for low-maintenance, low-fertilizer lawns on acidic Southern soil. St. Augustine wins for shaded lawns, coastal yards, and faster recovery from foot traffic.',
  table: [
    { label: 'Climate zone (USDA)', a: '7-9 (warm, Coastal Plain)', b: '8-10 (warm, sub-tropical)' },
    { label: 'Sun requirement', a: '6+ hours (medium shade tolerance)', b: '4-6 hours (best shade tolerance of warm-season)' },
    { label: 'Shade tolerance', a: 'Medium', b: 'High' },
    { label: 'Salt tolerance', a: 'Low', b: 'High (coastal favorite)' },
    { label: 'Traffic tolerance', a: 'Low', b: 'Medium' },
    { label: 'Drought tolerance', a: 'Medium', b: 'Medium' },
    { label: 'Cold tolerance', a: 'Low (damage below 20 deg F)', b: 'Low (damage below 25 deg F)' },
    { label: 'Soil pH preference', a: 'Acidic (5.0 to 6.0)', b: 'Slightly acidic to neutral (6.0 to 7.5)' },
    { label: 'Mowing height', a: '1 to 2 inches', b: '3 to 4 inches' },
    { label: 'Mowing frequency in peak season', a: 'Every 10 to 14 days', b: 'Every 7 to 10 days' },
    { label: 'Annual nitrogen need', a: '1 to 2 lbs / 1,000 sq ft (lowest of any lawn grass)', b: '3 to 4 lbs / 1,000 sq ft' },
    { label: 'Water need (peak)', a: '0.75 to 1 inch / week', b: '1.0 to 1.5 inches / week' },
    { label: 'Spreading habit', a: 'Stolons only (slow)', b: 'Stolons only (faster)' },
    { label: 'Disease/pest pressure', a: 'Low (centipede decline is main issue)', b: 'High (chinch bugs, gray leaf spot)' },
    { label: 'Maintenance level', a: 'Low', b: 'Medium-high' },
  ],
  pickA: {
    headline: 'Pick centipedegrass if...',
    reasons: [
      'You want the lowest-maintenance Southern lawn possible (1-2 lbs nitrogen per year, mow every 10-14 days).',
      'Your soil is naturally acidic with a pH between 5.0 and 6.0 (common across the Coastal Plain from Georgia to East Texas).',
      'Your lawn has at least 6 hours of direct sun and only light traffic.',
      'You are willing to accept slower recovery from damage in exchange for lower ongoing inputs.',
      'You live inland (centipede is not salt tolerant) in Zone 7 through 9.',
    ],
  },
  pickB: {
    headline: 'Pick St. Augustinegrass if...',
    reasons: [
      'Your lawn has significant tree shade where centipede or bermuda would thin out.',
      'You live within a few miles of the coast and need salt-spray tolerance.',
      'You want a more vigorous grass that recovers from damage faster than centipede.',
      'You can budget for chinch bug prevention and disease scouting that centipede does not need.',
      'You live in Zone 8 or warmer where hard freezes are rare.',
    ],
  },
  faqs: [
    {
      question: 'Is centipede or St. Augustine easier to maintain?',
      answer:
        'Centipedegrass is significantly easier to maintain than St. Augustinegrass. Centipede needs 1 to 2 pounds of nitrogen per 1,000 square feet per year (compared to 3 to 4 for St. Augustine), tolerates acidic soil that would require regular liming for St. Augustine, mows every 10 to 14 days instead of every 7 to 10, and has minimal pest pressure compared to St. Augustine\'s chinch bug and gray leaf spot problems. Centipede earned its "lazy man\'s grass" nickname for good reason.',
    },
    {
      question: 'Can centipede grow in shade like St. Augustine?',
      answer:
        'No, centipedegrass needs significantly more sun than St. Augustine. Centipede tolerates light shade but really needs 6 hours of direct sun to maintain density; St. Augustine handles 4 hours of dappled sun and stays acceptable under mature tree canopy. For shaded Southern lawns, St. Augustine is one of the few options that consistently performs.',
    },
    {
      question: 'Will centipede tolerate the same fertilizer as St. Augustine?',
      answer:
        'No, applying St. Augustine fertilizer rates to centipede will hurt it. Centipede is intolerant of high-nitrogen feeding and develops "centipede decline" when over-fertilized; the symptoms look like a slow yellowing and thinning of the entire lawn. Stay below 2 pounds of nitrogen per 1,000 square feet per year on centipede, and use centipede-specific fertilizers (often 15-0-15 or similar) that match its lower-input needs.',
    },
    {
      question: 'Which grass survives a cold snap better?',
      answer:
        'Both centipede and St. Augustine are warm-season grasses with limited cold tolerance, and both can be damaged or killed by hard freezes. Centipede is slightly less cold-hardy than St. Augustine and typically suffers damage starting around 20 degrees Fahrenheit; St. Augustine starts showing damage closer to 25 degrees. Neither is a safe choice for the upper transition zone or anywhere that regularly sees temperatures in the teens.',
    },
    {
      question: 'Can I overseed centipede with St. Augustine?',
      answer:
        'You cannot effectively overseed either centipede or St. Augustine because both are sold almost exclusively as sod or plugs, not seed. Centipede seed exists but is expensive and slow to establish (3 to 12 months from seed to a usable lawn). St. Augustine effectively requires sod or plugs; viable seed is not commercially available. If you want to switch from one to the other, plan on a full sod replacement rather than an overseeding transition.',
    },
  ],
}

// ---------- Brand-vs-brand comparisons ----------

const scottsWeedFeedVsMilorganite: BrandComparisonEntry = {
  slug: 'scotts-weed-feed-vs-milorganite',
  kind: 'brand',
  shortLabel: 'Scotts Weed & Feed vs Milorganite',
  h1: 'Scotts Weed & Feed vs Milorganite: Which One Should You Use?',
  metaTitle: 'Milorganite vs Scotts Weed & Feed: Is It Better? (2026)',
  metaDescription:
    'Milorganite vs Scotts Weed & Feed compared: NPK, organic vs synthetic, weed control, slow release, whether you can use them together, and which one your lawn needs.',
  lastModified: '2026-06-14',
  a: {
    brand: 'Scotts',
    displayName: 'Scotts Turf Builder Weed & Feed',
    productType: 'Synthetic combo herbicide + fertilizer (granular)',
    productsCategory: 'weed-control',
    productKey: 'weed-feed-scotts',
    buyReason: 'One pass to kill listed broadleaf weeds and feed the lawn.',
    badge: { label: 'Fastest weed knockdown', tone: 'gold', tier: 'editor' },
  },
  b: {
    brand: 'Milorganite',
    displayName: 'Milorganite Organic Nitrogen Fertilizer',
    productType: 'Organic slow-release nitrogen fertilizer (granular)',
    productsCategory: 'fertilizer',
    productKey: 'fertilizer-milorganite',
    buyReason: 'Slow-release, low-burn feeding you can apply without weed pressure.',
  },
  intro: (
    <>
      <p>
        Scotts Turf Builder Weed &amp; Feed and Milorganite are two of the most-Googled lawn
        products in America, and homeowners often ask which one they should buy. The truth is
        they are not really competitors; they are different tools for different jobs. Scotts
        Weed &amp; Feed is a synthetic combo product that kills broadleaf weeds (dandelions,
        clover, chickweed) while feeding the lawn with a fast-acting nitrogen blast. Milorganite
        is an organic, slow-release nitrogen fertilizer derived from heat-dried microbes; it
        feeds the lawn steadily over 8 to 10 weeks but does nothing to weeds.
      </p>
      <p>
        The real decision is which problem you have right now. If your lawn has active broadleaf
        weeds that need killing in the spring or fall growing season, Scotts Weed &amp; Feed is
        the right pick. If your lawn is already weed-free and you just want a steady, low-burn
        feeding program through summer, Milorganite is the better choice. Many homeowners use
        both in the same year: Scotts Weed &amp; Feed once in spring for the weed knockdown,
        Milorganite three times across summer and fall for routine feeding.
      </p>
    </>
  ),
  verdict:
    'Scotts Weed & Feed wins when you have active broadleaf weeds and want one product to handle both feeding and weed control. Milorganite wins for routine summer feeding, organic preference, and zero burn risk.',
  table: [
    { label: 'Product type', a: 'Synthetic combo (herbicide + fertilizer)', b: 'Organic slow-release fertilizer' },
    { label: 'NPK ratio (typical)', a: '28-0-3 (varies by variant)', b: '6-4-0' },
    { label: 'Weed control', a: 'Yes (2,4-D + mecoprop or similar)', b: 'None' },
    { label: 'Nitrogen source', a: 'Urea (fast-release synthetic)', b: 'Heat-dried microbial biosolids (slow organic)' },
    { label: 'Slow-release fraction', a: 'Low (~30-50%)', b: 'Very high (95%+)' },
    { label: 'Burn risk', a: 'Medium (apply to dry lawn, water in)', b: 'Very low (cannot burn at normal rates)' },
    { label: 'Feeding duration', a: '4 to 6 weeks', b: '8 to 10 weeks' },
    { label: 'Application rate', a: '2.5 to 3.5 lbs per 1,000 sq ft', b: '32 lbs per 2,500 sq ft (~13 lbs / 1,000)' },
    { label: 'Best time to apply', a: 'Spring or fall when weeds are actively growing, daytime temps 60-80 deg F', b: 'Any time grass is growing, including summer heat' },
    { label: 'Effect on overseeding', a: 'Blocks seed germination for ~6 weeks', b: 'Safe to overseed before or after' },
    { label: 'Grass type restrictions', a: 'Several variants by grass type; St. Augustine has its own', b: 'Safe for all lawn grasses' },
    { label: 'Organic certification', a: 'No', b: 'OMRI-listed organic' },
    { label: 'Smell at application', a: 'Mild fertilizer smell', b: 'Strong (microbial, "compost") smell for 2-3 days' },
    { label: 'Price per 1,000 sq ft application', a: '$8 to $12', b: '$4 to $6' },
    { label: 'Best for', a: 'Spring weed knockdown + feeding in one pass', b: 'Routine summer feeding without weed pressure' },
  ],
  pickA: {
    headline: 'Pick Scotts Weed & Feed if...',
    reasons: [
      'Your lawn currently has visible broadleaf weeds (dandelions, clover, chickweed, plantain).',
      'You want one product to handle both feeding and weed control in a single application.',
      'You can apply during the spring or fall growing season when weeds are actively growing (not during summer heat above 85 degrees).',
      'You are not planning to overseed for at least 6 weeks after application.',
      'You are comfortable with synthetic chemistry and standard 2,4-D-based herbicides.',
    ],
  },
  pickB: {
    headline: 'Pick Milorganite if...',
    reasons: [
      'Your lawn is already weed-free and you only need to feed.',
      'You want zero burn risk and the freedom to apply during summer heat without damage.',
      'You prefer organic-certified products and slow-release nutrient delivery.',
      'You are overseeding or just laid new sod and need a fertilizer that will not block seed germination.',
      'You have pets or kids and want a product safe to walk on immediately after watering in.',
    ],
  },
  faqs: [
    {
      question: 'Can you use Scotts Weed & Feed and Milorganite together?',
      answer:
        'Yes, you can use Scotts Weed & Feed and Milorganite in the same growing season, but not in the same application. The typical rotation is Scotts Weed & Feed once in early spring (March or April) when broadleaf weeds are actively growing, then Milorganite three times across summer and fall (June, August, October) for routine feeding without weed pressure. Do not apply both within 4 weeks of each other; the lawn cannot use that much nitrogen at once and you waste the second application.',
    },
    {
      question: 'Is Milorganite better than Scotts fertilizer?',
      answer:
        'Milorganite is better than Scotts as a routine slow-release fertilizer, but Scotts is better when you also need weed control. Milorganite\'s 6-4-0 NPK delivers ~8 to 10 weeks of steady feeding from a single application, has near-zero burn risk, and is OMRI-listed organic. Scotts Turf Builder (the non-weed-and-feed variant) at 28-0-3 delivers more nitrogen faster but is synthetic and carries some burn risk. For routine feeding alone, Milorganite wins. For weed knockdown plus feeding, Scotts Weed & Feed wins.',
    },
    {
      question: 'Does Milorganite kill weeds like Scotts Weed & Feed?',
      answer:
        'No, Milorganite does not kill weeds. Milorganite is a pure fertilizer; it contains no herbicide active ingredients. A common myth is that Milorganite\'s iron content somehow suppresses weeds, but the iron is at a level that helps grass color, not weed control. If you have active broadleaf weeds, you need a herbicide like Scotts Weed & Feed, Ortho Weed-B-Gon, or a selective post-emergent. Milorganite plus a separate weed control is also a valid combination.',
    },
    {
      question: 'How long after Scotts Weed & Feed can you apply Milorganite?',
      answer:
        'Wait 4 to 6 weeks after Scotts Weed & Feed before applying Milorganite. Both products contain nitrogen, and stacking them within a few weeks gives the lawn more nitrogen than it can use; the excess leaches into groundwater or runs off as waste. The Scotts Weed & Feed nitrogen pulse lasts about 4 to 6 weeks, after which the lawn is ready for the next feeding. This timing also lets the broadleaf herbicide in Scotts finish its work before adding more nitrogen pressure.',
    },
    {
      question: 'Which is cheaper, Scotts Weed & Feed or Milorganite?',
      answer:
        'Milorganite is cheaper per 1,000 square feet applied. Milorganite costs about $4 to $6 per 1,000 sq ft per application; Scotts Weed & Feed runs $8 to $12 for the same coverage. Over a typical 4-application growing season on a 5,000 sq ft lawn, Milorganite costs about $100 to $120 while Scotts comparable program runs $200 to $250. If you only need to feed (no weed pressure), Milorganite is significantly cheaper.',
    },
    {
      question: 'Can you put down Milorganite and Scotts Weed & Feed at the same time?',
      answer:
        'You should not put Milorganite and Scotts Weed & Feed down in the same application. Both add nitrogen, and applying them together gives the lawn far more than it can absorb, so the excess is wasted or runs off as pollution. There is also no reason to mix them: the herbicide in Scotts works best applied on its own to a dry lawn, while Milorganite is a slow-release feed for a weed-free lawn. Space them out instead. Apply Scotts Weed & Feed first when broadleaf weeds are actively growing, then wait 4 to 6 weeks before your first Milorganite feeding.',
    },
    {
      question: 'How does Milorganite compare to Scotts Green Max and Scotts Natural Lawn Food?',
      answer:
        'Scotts Green Max is a fast-release synthetic fertilizer with added iron for a quick green-up, so it colors the lawn faster than Milorganite but feeds for a shorter window and carries more burn risk. Scotts Natural Lawn Food is Scotts\' organic, slow-release option and is the closest match to Milorganite; both are low-burn and feed steadily, though Milorganite\'s 6-4-0 adds a little phosphorus and its well-known iron content for color. For steady, low-risk feeding all three work. Choose Milorganite or Scotts Natural Lawn Food if you want organic slow-release, and Green Max only when you want a fast cosmetic green-up.',
    },
    {
      question: 'Does Milorganite contain PFAS?',
      answer:
        'Milorganite is made from heat-dried microbes (biosolids), and like most biosolid-derived products it can contain trace levels of PFAS. The Milwaukee Metropolitan Sewerage District, which produces Milorganite, publishes testing data and reports levels that fall within applicable guidance. If trace PFAS is a concern for you, a plant-derived organic fertilizer or a synthetic slow-release product avoids biosolids entirely. For most homeowners applying a few times per year, the exposure risk is considered low.',
    },
  ],
}

const scottsVsPenningtonSeed: BrandComparisonEntry = {
  slug: 'scotts-vs-pennington-grass-seed',
  kind: 'brand',
  shortLabel: 'Pennington vs Scotts Grass Seed',
  h1: 'Pennington vs Scotts Grass Seed: Which Is Better?',
  metaTitle: 'Pennington vs Scotts Grass Seed: Which Is Better? (2026)',
  metaDescription:
    'Pennington vs Scotts grass seed compared: cultivar quality, price vs store-brand seed, germination, coating, bare-spot repair, and which one is right for your lawn.',
  lastModified: '2026-06-14',
  a: {
    brand: 'Scotts',
    displayName: 'Scotts Turf Builder Grass Seed',
    productType: 'Retail-tier coated grass seed blends',
    productsCategory: 'grass-seed',
    productKey: 'seed-scotts-sun-shade',
    buyReason: 'Widely available coated blend with seed, feed, and soil improver in one.',
  },
  b: {
    brand: 'Pennington',
    displayName: 'Pennington Smart Seed',
    productType: 'Drought-tolerant cultivar grass seed blends',
    productsCategory: 'grass-seed',
    productKey: 'seed-cool-pennington',
    buyReason: 'Drought-tolerant cultivars that need up to 30% less water once established.',
  },
  intro: (
    <>
      <p>
        Scotts and Pennington are the two most-sold grass seed brands in the US, and almost
        every homeowner reseeding a lawn has stood in front of both bags in the same Home Depot
        aisle. The brands look similar on the shelf and ship comparable retail prices, but the
        seed inside the bag is meaningfully different. Scotts Turf Builder Grass Seed leads with
        the WaterSmart Plus coating story (a starter fertilizer and absorbent polymer coating
        each seed) and aggressive marketing. Pennington Smart Seed leads on cultivar quality and
        a stronger emphasis on drought-tolerant turf-type tall fescue cultivars.
      </p>
      <p>
        Both brands sell solid mid-tier seed; both will give you a viable lawn. The differences
        show up over 2 to 3 growing seasons as cultivar quality compounds. Pennington Smart
        Seed cultivars tend to use less water once established (Pennington claims up to 30 percent
        less), have finer texture, and produce a denser stand. Scotts seed tends to germinate
        faster because of the coating but the underlying cultivar genetics are usually one notch
        down from Pennington Smart Seed. For premium results choose Pennington; for fast cheap
        coverage Scotts is fine.
      </p>
    </>
  ),
  verdict:
    'Pennington Smart Seed wins on cultivar quality and long-term drought tolerance. Scotts wins on fastest germination, ease of finding the right product on a big-box shelf, and slightly lower upfront cost.',
  table: [
    { label: 'Product positioning', a: 'Mass-market retail; "fix any lawn" branding', b: 'Smart Seed line emphasizes cultivar genetics + drought tolerance' },
    { label: 'Seed coating', a: 'WaterSmart Plus (absorbent polymer + starter fertilizer)', b: 'Lighter coating; emphasizes pure seed weight' },
    { label: 'Pure seed by weight', a: 'About 75-85% (coating adds bulk weight)', b: 'About 85-95% (less coating)' },
    { label: 'Cultivar quality', a: 'Mid-tier, varies by blend', b: 'Higher-tier; named cultivars in Smart Seed line' },
    { label: 'Germination time', a: '7 to 14 days (coating speeds initial water uptake)', b: '7 to 21 days' },
    { label: 'Drought tolerance claim', a: 'WaterSmart claims 30% less water for 6 weeks (establishment only)', b: 'Up to 30% less water for life of lawn (established)' },
    { label: 'Cool-season blends', a: 'Sun & Shade, Dense Shade, High Traffic, Tall Fescue, KBG', b: 'Dense Shade, Sun & Shade, Sunny, Tall Fescue, KBG, Centipede' },
    { label: 'Warm-season blends', a: 'Bermudagrass (Pensacola, Princess), Centipede, Bahiagrass', b: 'Bermudagrass, Centipede, Bahiagrass, Pensacola Bahia' },
    { label: 'Bag size availability', a: '3, 7, 20 lb retail bags', b: '3, 7, 20 lb retail bags' },
    { label: 'Price per pound (typical retail)', a: '$4 to $7', b: '$5 to $9' },
    { label: 'Germination guarantee', a: 'Standard money-back if seed fails to germinate', b: 'Standard money-back if seed fails to germinate' },
    { label: 'Endophyte enhancement', a: 'Limited to some blends', b: 'Common in Smart Seed cool-season blends' },
    { label: 'Where to buy', a: 'Home Depot, Lowe\'s, Walmart, Amazon, Target', b: 'Home Depot, Lowe\'s, Tractor Supply, Walmart, Amazon' },
    { label: 'Best for', a: 'Fast germination, easiest to find on shelf, lower upfront cost', b: 'Long-term lawn quality, drought tolerance, premium cultivars' },
    { label: 'Bag clarity', a: 'Generic blend names; cultivars often not listed', b: 'Smart Seed bags list specific named cultivars on the label' },
  ],
  pickA: {
    headline: 'Pick Scotts grass seed if...',
    reasons: [
      'You want the fastest germination and the coating helps you avoid the watering mistakes most DIY seeders make in the first 2 weeks.',
      'You are doing a small overseeding job or patching bare spots and want the cheapest viable option per 1,000 sq ft.',
      'You want the most retail availability (Scotts is in literally every big-box and grocery garden section).',
      'You are not particularly fussy about cultivar quality and just want green grass to fill in.',
      'You are reseeding a high-foot-traffic lawn where the Scotts High Traffic blend specifically targets your use case.',
    ],
  },
  pickB: {
    headline: 'Pick Pennington Smart Seed if...',
    reasons: [
      'You want the best long-term cultivar quality and are willing to pay 10 to 20 percent more upfront.',
      'You want the drought-tolerance benefit to last for years, not just the establishment window.',
      'You can read a seed label and want to know which specific cultivars are in the bag (Smart Seed lists them).',
      'You are seeding cool-season grass and want endophyte-enhanced cultivars (better pest resistance built-in).',
      'You are reseeding a premium lawn where the visual quality of the established stand matters more than the upfront cost.',
    ],
  },
  faqs: [
    {
      question: 'Is Pennington Smart Seed worth the extra cost over Scotts?',
      answer:
        'For most homeowners reseeding a lawn they plan to keep for more than 3 years, yes. Pennington Smart Seed uses higher-quality cultivars and lists specific cultivar names on the bag (Scotts typically does not), and the drought-tolerance benefit lasts for the life of the established lawn rather than just the establishment window. The price premium of 10 to 20 percent per pound is small compared to the cost difference between a great lawn and a mediocre one over 5 years.',
    },
    {
      question: 'Does the Scotts WaterSmart coating actually work?',
      answer:
        'The Scotts WaterSmart Plus coating works, but only for the establishment window (the first 2 to 6 weeks). The coating absorbs water and releases it slowly to the germinating seed, which helps homeowners who forget to water consistently. After the seed has germinated and rooted, the coating provides no further benefit. The Pennington Smart Seed approach (premium cultivars rather than coatings) provides drought tolerance that persists for the life of the lawn.',
    },
    {
      question: 'Which brand has better tall fescue?',
      answer:
        'Pennington Smart Seed Tall Fescue is generally a higher-quality blend than Scotts Turf Builder Tall Fescue. Pennington lists specific turf-type tall fescue cultivars on the bag (often named cultivars from the National Turfgrass Evaluation Program) while Scotts uses generic blend descriptions. If you can find the same cultivar names on independent NTEP ratings, Pennington usually selects from the top performers.',
    },
    {
      question: 'Can you mix Scotts and Pennington seed in the same lawn?',
      answer:
        'Yes, mixing Scotts and Pennington grass seed in the same lawn is fine as long as both blends are designed for the same grass type and climate. Different brand bags of the same species (e.g., Scotts tall fescue + Pennington Smart Seed tall fescue) will establish and blend together without issue. Do not mix bags meant for different climates (Scotts cool-season + Pennington warm-season bahiagrass would not work in the same lawn).',
    },
    {
      question: 'Which brand has a stronger germination guarantee?',
      answer:
        'Scotts and Pennington both offer the same standard money-back guarantee if the seed fails to germinate when used according to the bag instructions. Neither brand offers a stronger guarantee than the other. In practice, both brands honor the guarantee with proof of purchase if seed fails (rare with either brand when sown correctly).',
    },
    {
      question: 'Is Pennington grass seed worth more than generic or store-brand seed?',
      answer:
        'For a lawn you plan to keep, yes. Pennington Smart Seed uses named, tested turf cultivars and lists them on the bag, while most generic and store-brand seed relies on unnamed, lower-tier cultivars with a higher percentage of inert filler and occasional weed seed. The difference shows up over 2 to 3 seasons: Smart Seed produces a denser, finer, more drought-tolerant stand. Generic seed will still germinate and fill in for less money upfront, so it is fine for a temporary lawn or a rental, but for a lawn you care about, the small premium for named cultivars is worth it.',
    },
    {
      question: 'Pennington Smart Patch vs Scotts EZ Seed: which is better for bare spots?',
      answer:
        'Both are all-in-one bare-spot repair products that combine seed, mulch, and starter fertilizer, and both work well for small patches. Scotts EZ Seed uses a water-absorbing mulch that swells and darkens when it needs water, which makes watering nearly foolproof. Pennington Smart Patch pairs its mulch with the same higher-tier Smart Seed cultivars, so the repaired patch blends into a quality lawn better over time. For the easiest watering experience choose EZ Seed; for the best long-term match to a premium lawn choose Smart Patch. For large areas, buy straight seed instead, since both repair products are expensive per square foot.',
    },
  ],
}

const lescoVsScotts: BrandComparisonEntry = {
  slug: 'lesco-vs-scotts-fertilizer',
  kind: 'brand',
  shortLabel: 'Lesco vs Scotts Fertilizer',
  h1: 'Lesco vs Scotts Fertilizer: Which Should You Buy?',
  metaTitle: 'Lesco vs Scotts Fertilizer: Honest Comparison (2026)',
  metaDescription:
    'Lesco vs Scotts lawn fertilizer compared: NPK quality, slow-release fraction, cost per pound, where to buy, and how Lesco stacks up against Scotts, Andersons, and Milorganite.',
  lastModified: '2026-06-14',
  a: {
    brand: 'Lesco',
    displayName: 'Lesco Professional Lawn Fertilizer',
    productType: 'Commercial-grade professional fertilizer (SiteOne / Amazon)',
    productsCategory: 'fertilizer',
    // Lesco's primary channel is SiteOne, but SiteOne itself lists several Lesco
    // SKUs on Amazon — this 24-0-11 is the flagship pro turf formula (first NPK in
    // the table below), so homeowners without a SiteOne nearby can still buy it.
    productKey: 'fertilizer-lesco-24-0-11',
    buyReason: 'The flagship 24-0-11 pro turf formula, high slow-release and priced per pound for landscapers.',
    badge: { label: 'Pro-grade pick', tone: 'gold', tier: 'editor' },
  },
  b: {
    brand: 'Scotts',
    displayName: 'Scotts Turf Builder Fertilizer',
    productType: 'Retail-tier consumer fertilizer (big-box and online)',
    productsCategory: 'fertilizer',
    productKey: 'fertilizer-scotts-turf-builder',
    buyReason: 'Homeowner-friendly granular feed for any grass type, no pro account needed.',
  },
  intro: (
    <>
      <p>
        Lesco and Scotts are the two most-asked-about lawn fertilizer brands in the US, but the
        two products live in different worlds. Lesco is a commercial-grade professional line
        sold mainly through SiteOne Landscape Supply (with several SKUs also listed on Amazon
        by SiteOne); it ships in 50-pound bags, lists
        precise NPK and slow-release percentages on the label, and is priced for landscapers who
        buy by the pallet. Scotts is the retail giant: 15- to 40-pound bags at Home Depot, Lowe&apos;s,
        Walmart, and Target, with consumer-friendly packaging, premix programs, and significantly
        higher cost per actual pound of nitrogen.
      </p>
      <p>
        For homeowners who live near a SiteOne store and do not mind buying in 50-pound increments,
        Lesco is significantly cheaper per pound of nitrogen, has higher slow-release fractions,
        and tends to use better-quality polymer-coated urea. For homeowners without SiteOne
        access, or who want smaller bags and the retail return policy, Scotts is the practical
        choice despite costing 1.5 to 2x more per pound of actual nutrient delivered.
      </p>
    </>
  ),
  verdict:
    'Lesco wins on cost per pound, slow-release quality, and pro-grade NPK precision (if you have SiteOne access). Scotts wins on retail availability, smaller bag sizes, and a more forgiving return policy.',
  table: [
    { label: 'Tier', a: 'Commercial / professional', b: 'Retail / consumer' },
    { label: 'Where to buy', a: 'SiteOne Landscape Supply (in-person, account often required); select SKUs on Amazon', b: 'Home Depot, Lowe\'s, Walmart, Target, Amazon' },
    { label: 'Bag size', a: '50 lb (commercial standard)', b: '12.5, 15, 33.3, 40 lb consumer sizes' },
    { label: 'NPK ratio (most common)', a: '24-0-11, 19-0-7, 32-0-10 (varies)', b: '32-0-4, 24-25-4 (Starter), 32-0-10' },
    { label: 'Slow-release fraction', a: '50 to 65% polymer-coated urea (SCU)', b: '30 to 50% (typically MCU or SCU)' },
    { label: 'Release duration', a: '8 to 12 weeks per application', b: '6 to 8 weeks per application' },
    { label: 'Coverage per 50 lb bag', a: '~14,000 to 16,000 sq ft', b: 'Equivalent retail bag (4 x ~13 lb) covers ~16,000 sq ft' },
    { label: 'Price per 50 lb (typical)', a: '$35 to $50 (Lesco)', b: '$75 to $110 (4 x retail bag equivalent)' },
    { label: 'Price per lb of N', a: '~$0.20 to $0.30 per lb of actual N', b: '~$0.50 to $0.70 per lb of actual N' },
    { label: 'Label clarity (NPK, SCU%)', a: 'Detailed (lists exact percentages and N sources)', b: 'Marketing-forward (% slow-release sometimes hidden)' },
    { label: 'Pre-mixed weed/insect combos', a: 'Available; tailored to professional grade', b: 'Extensive (Triple Action, Halts, GrubEx, etc.)' },
    { label: 'Return policy', a: 'Often final sale; commercial pricing assumes you know the product', b: 'Standard retail return policy (often 90 days at big-box)' },
    { label: 'Bag handling', a: '50 lb bag; needs space and pickup truck or strong back', b: '12 to 40 lb consumer bags fit in a car trunk' },
    { label: 'Best for', a: 'Frequent feeders, large lawns, cost-conscious DIY who can handle 50 lb bags', b: 'Smaller lawns, casual feeders, anyone without SiteOne nearby' },
    { label: 'Online availability', a: 'No (must visit SiteOne in person; account often required)', b: 'Widely available online and at every big-box store' },
  ],
  pickA: {
    headline: 'Pick Lesco fertilizer if...',
    reasons: [
      'You have a SiteOne Landscape Supply store within reasonable driving distance.',
      'You have a lawn larger than 5,000 sq ft and feed at least 3 times per year.',
      'You can handle 50-pound bags (storage, transport, application).',
      'You want the highest slow-release fraction available and you read NPK labels carefully.',
      'You are comfortable shopping at a contractor-focused store without retail-style consumer support.',
    ],
  },
  pickB: {
    headline: 'Pick Scotts fertilizer if...',
    reasons: [
      'You do not live near a SiteOne (most homeowners) or you do not want to make a special trip.',
      'You have a lawn under 5,000 sq ft where 50-pound bags are inconvenient and you would have leftover product.',
      'You want the convenience of online ordering, retail returns, and 12 to 40 lb consumer-friendly bag sizes.',
      'You are happy paying 1.5 to 2x more per pound of nitrogen in exchange for the retail experience.',
      'You want the broadest selection of pre-mixed weed/insect combos (Triple Action, Halts, GrubEx, etc.).',
    ],
  },
  faqs: [
    {
      question: 'Is Lesco fertilizer really better than Scotts?',
      answer:
        'On a per-pound-of-nitrogen basis, Lesco delivers more value than Scotts: typically 50 to 65 percent slow-release polymer-coated urea (vs Scotts\' 30 to 50 percent), 8 to 12 week release duration (vs 6 to 8 weeks for Scotts), and roughly half the price per actual pound of nitrogen delivered. For homeowners with SiteOne access who feed regularly, Lesco is the better technical choice. The catch is access: Lesco is sold only at SiteOne stores, often requires an account, and ships in 50-pound bags that not every homeowner can handle.',
    },
    {
      question: 'Can a homeowner buy Lesco?',
      answer:
        'Yes, homeowners can buy Lesco at SiteOne Landscape Supply stores. Some SiteOne locations require a customer account (free to set up) and some do not; the policy varies by location. You do not need a contractor license or business credentials to walk into most SiteOne stores and buy a bag of Lesco. Call ahead to confirm the location\'s policy. Lesco is not stocked at Home Depot, Lowe\'s, Walmart, or other big-box garden centers, but SiteOne does list several Lesco SKUs on Amazon, so homeowners without a nearby SiteOne can still order the pro-grade product online.',
    },
    {
      question: 'How much money does Lesco save per year over Scotts?',
      answer:
        'For a typical 7,500 sq ft lawn fed 4 times per year, Lesco saves about $150 to $250 per year over an equivalent Scotts program. Lesco runs $35 to $50 per 50-lb bag (covers about 15,000 sq ft per bag depending on rate); Scotts equivalent in retail bags runs $75 to $110 per same coverage. Over 5 years that\'s $750 to $1,250 in savings, which is meaningful if you have SiteOne access and use a lot of fertilizer.',
    },
    {
      question: 'Is Scotts or Lesco better for new lawns?',
      answer:
        'Scotts has the more obvious starter fertilizer for new lawns (Scotts Turf Builder Starter Food for New Grass, 24-25-4), readily available at any big-box store. Lesco also offers high-phosphorus starter formulas (typically 18-24-12 or similar) at SiteOne, but availability is store-dependent. For a one-time starter application on a small new lawn, the Scotts retail bag is easier to find and the price difference does not matter much at small volumes. For larger new installations, Lesco is the better economic choice.',
    },
    {
      question: 'Can you use Lesco in a Scotts spreader?',
      answer:
        'Yes, Lesco fertilizer works in any standard broadcast or drop spreader, including Scotts-branded spreaders. Spreader settings are not brand-specific; they are based on the granule size and application rate. The Lesco bag lists application rate in pounds per 1,000 square feet; the Scotts spreader has setting numbers, and you can match them via the spreader\'s own calibration chart or by checking Scotts\' app for the closest match by particle size. The most reliable approach is to weigh out a test amount and walk a known area to calibrate.',
    },
    {
      question: 'Lesco vs Andersons fertilizer: which professional brand is better?',
      answer:
        'Lesco and Andersons are both professional-grade fertilizers a step above retail brands, and both are excellent. The practical differences come down to particle size and access. Andersons is known for small dispersible-granule (DG) particles that break down fast and suit low-cut and reel-mowed lawns, and it is easier to buy in homeowner quantities, including on Amazon. Lesco uses larger conventional granules, comes in 50-lb bags through SiteOne, and is usually the cheapest per pound if you have SiteOne access. Choose Andersons for finer particles, wider availability, and smaller bags; choose Lesco for the lowest cost per pound at volume.',
    },
    {
      question: 'Which is the best budget lawn fertilizer: Lesco, Scotts, or Milorganite?',
      answer:
        'It depends on access and what you value. Lesco is the cheapest per pound at volume, but only if you can get to a SiteOne store. Milorganite is the cheapest widely available option, organic and slow-release with almost no burn risk, though it feeds at a lower nitrogen rate so you apply more. Scotts is the most expensive per pound but the most convenient, sold in every big-box store with grass-type-specific blends. For the lowest cost with SiteOne access, choose Lesco; for cheap and easy to find anywhere, choose Milorganite; for convenience and retail availability, choose Scotts.',
    },
  ],
}

const scottsWeedFeedVsTripleAction: BrandComparisonEntry = {
  slug: 'scotts-weed-feed-vs-triple-action',
  kind: 'brand',
  shortLabel: 'Scotts Weed & Feed vs Triple Action',
  h1: 'Scotts Weed & Feed vs Triple Action: Which Scotts Bag Do You Need?',
  metaTitle: 'Scotts Weed & Feed vs Triple Action: Which to Buy 2026',
  metaDescription:
    'Scotts Weed & Feed vs Triple Action compared: which one prevents crabgrass, what each kills, NPK, application timing by soil temperature, and the right bag for your lawn.',
  lastModified: '2026-06-05',
  a: {
    brand: 'Scotts',
    displayName: 'Scotts Turf Builder Weed & Feed',
    productType: 'Broadleaf weed killer + fertilizer (granular)',
    productsCategory: 'weed-control',
    productKey: 'weed-feed-scotts',
    buyReason: 'Best when weeds are already up and you just need to kill and feed.',
  },
  b: {
    brand: 'Scotts',
    displayName: 'Scotts Turf Builder Triple Action',
    productType: 'Crabgrass preventer + weed killer + fertilizer (granular)',
    productsCategory: 'weed-control',
    productKey: 'weed-triple-action-scotts',
    buyReason: 'One spring pass: prevents crabgrass, kills weeds, and feeds at once.',
    badge: { label: 'Best all-in-one', tone: 'gold', tier: 'editor' },
  },
  intro: (
    <>
      <p>
        Both of these are Scotts Turf Builder bags, so this is not a brand fight; it is a "which
        one does my lawn actually need this year" decision. Scotts Turf Builder Weed &amp; Feed is a
        two-job bag: it feeds the lawn and kills broadleaf weeds (dandelion, clover, chickweed,
        plantain) that are already growing. Scotts Turf Builder Triple Action is a three-job bag: it
        does the same feed-plus-broadleaf-control, then adds a pendimethalin pre-emergent that stops
        crabgrass seeds from sprouting in the first place.
      </p>
      <p>
        That pre-emergent is the whole decision. If your real problem is crabgrass coming back every
        summer, you want Triple Action, and you want it down early in spring once the soil holds
        about 55 deg F for a few days, before crabgrass germinates. If your lawn just has a flush of
        dandelions and clover and crabgrass has never been an issue, plain Weed &amp; Feed is the
        cheaper, simpler pick, and you apply it whenever those broadleaf weeds are actively growing.
      </p>
      <p>
        One thing to clear up first: standard Scotts Weed &amp; Feed is not a pre-emergent and will
        not prevent crabgrass. If that is what you came here to learn, the rest of this page sorts
        out exactly when each bag earns its place.
      </p>
    </>
  ),
  verdict:
    'Triple Action wins when you need to prevent crabgrass and want one early-spring pass that also feeds and kills broadleaf weeds. Weed & Feed wins when crabgrass is not your problem and you just need to clean up broadleaf weeds and feed an established lawn.',
  table: [
    { label: 'Product type', a: 'Broadleaf weed killer + fertilizer', b: 'Crabgrass preventer + broadleaf weed killer + fertilizer' },
    { label: 'Jobs in one bag', a: '2 (feed + kill broadleaf weeds)', b: '3 (feed + kill broadleaf + prevent crabgrass)' },
    { label: 'Weed control type', a: 'Post-emergent only', b: 'Pre-emergent + post-emergent' },
    { label: 'Prevents crabgrass?', a: 'No (not a pre-emergent)', b: 'Yes (pendimethalin barrier, lasts up to ~4 months)' },
    { label: 'Kills broadleaf weeds?', a: 'Yes (dandelion, clover, chickweed, plantain)', b: 'Yes (same broadleaf list)' },
    { label: 'Kills existing crabgrass?', a: 'No', b: 'No (prevents germination only, will not kill emerged crabgrass)' },
    { label: 'Active ingredients', a: '2,4-D + mecoprop (MCPP)', b: 'Pendimethalin + 2,4-D, MCPP, dicamba' },
    { label: 'Fertilizer (NPK, typical)', a: 'Approx 28-0-3 (varies by variant)', b: 'Lower nitrogen than straight Turf Builder; varies by variant (check the bag)' },
    { label: 'What times the application', a: 'Active broadleaf weed growth', b: 'Soil temperature (the pre-emergent window)' },
    { label: 'Best time to apply', a: 'Spring or fall when broadleaf weeds are actively growing, daytime temps 60-85 deg F', b: 'Early spring, once soil holds ~55 deg F for several days, before crabgrass germinates' },
    { label: 'Best lawn state', a: 'Established lawn with active broadleaf weeds and no crabgrass history', b: 'Established lawn with recurring crabgrass plus broadleaf weeds' },
    { label: 'Effect on overseeding', a: 'Wait ~4 to 6 weeks after applying to seed', b: 'Blocks new seed for ~4 months (pre-emergent barrier)' },
    { label: 'New lawns', a: 'Wait until turf is established', b: 'Not on lawns under 4 months old (sod) or 6 months (seed)' },
    { label: 'Regional variants', a: 'Several variants by grass type (incl. Southern, St. Augustine)', b: 'Standard (North/Transition) and Triple Action South' },
    { label: 'Best for', a: 'Broadleaf weed cleanup + feeding on an established lawn', b: 'Spring crabgrass prevention + broadleaf knockdown + feeding in one pass' },
  ],
  pickA: {
    headline: 'Pick Scotts Weed & Feed if...',
    reasons: [
      'Crabgrass is not your problem; you mainly have broadleaf weeds like dandelions, clover, chickweed, or plantain.',
      'Your lawn already has those weeds actively growing and you want to feed the lawn in the same pass.',
      'You missed the early-spring soil-temperature window for a pre-emergent, or you are applying in the fall.',
      'You want the simpler, cheaper bag and do not need crabgrass prevention.',
      'You are not planning to overseed for at least 4 to 6 weeks after applying.',
    ],
  },
  pickB: {
    headline: 'Pick Scotts Triple Action if...',
    reasons: [
      'Crabgrass comes back every summer and you want to stop it before it germinates this year.',
      'You can get it down in early spring, once soil holds about 55 deg F for several days, before crabgrass sprouts.',
      'You also have broadleaf weeds and want one bag to prevent crabgrass, kill broadleaf, and feed all at once.',
      'Your lawn is established (not seeded or sodded within the last 4 months) and you are not overseeding for ~4 months.',
      'You want to match the formula to your region (standard for the North and transition zone, Triple Action South for warm-season lawns).',
    ],
  },
  faqs: [
    {
      question: 'What is the difference between Scotts Weed & Feed and Triple Action?',
      answer:
        'The difference is crabgrass prevention. Both bags feed the lawn and kill broadleaf weeds like dandelion and clover with a 2,4-D and mecoprop herbicide blend. Triple Action adds a third ingredient, the pre-emergent pendimethalin, which stops crabgrass and other annual grassy weeds from germinating. Weed & Feed does not contain a pre-emergent, so it cannot prevent crabgrass. If crabgrass is a recurring problem, Triple Action is the bag; if you only need to clean up broadleaf weeds and feed, Weed & Feed does the job for less money.',
    },
    {
      question: 'Does Scotts Weed & Feed prevent crabgrass?',
      answer:
        'No, standard Scotts Turf Builder Weed & Feed does not prevent crabgrass. It is a post-emergent broadleaf product, meaning it kills weeds that are already growing (dandelions, clover, chickweed) but contains no pre-emergent to stop crabgrass seeds from sprouting. For crabgrass prevention you need a product with a pre-emergent such as Scotts Turf Builder Triple Action (pendimethalin) or Scotts Halts. Apply that in early spring before crabgrass germinates, because once crabgrass is up, a pre-emergent will not touch it.',
    },
    {
      question: 'When should you apply Scotts Triple Action vs Weed & Feed?',
      answer:
        'Apply Triple Action in early spring, as soon as soil temperatures hold around 55 deg F for several days and before crabgrass germinates; this is the only window where the pre-emergent works. Weed & Feed is timed differently: you apply it when broadleaf weeds are actively growing, which can be spring or fall, with daytime temps roughly 60 to 85 deg F. So Triple Action is a calendar-and-soil-temperature decision, while Weed & Feed is a "the weeds are out now" decision.',
    },
    {
      question: 'Can you use Scotts Triple Action and Weed & Feed in the same year?',
      answer:
        'Yes, but you rarely need both, and never within a few weeks of each other. A common program is Triple Action in early spring for crabgrass prevention plus the first feeding, then if broadleaf weeds flare up later in the season you can spot-treat or use Weed & Feed once those weeds are actively growing. Keep applications at least 6 to 8 weeks apart so you do not stack nitrogen or overlap herbicides, and remember both contain broadleaf killers, so doubling up close together stresses the lawn without extra benefit.',
    },
    {
      question: 'Which Scotts product is better for crabgrass?',
      answer:
        'For crabgrass, Triple Action is the better of these two because it contains a pendimethalin pre-emergent that prevents crabgrass for up to about 4 months. Weed & Feed has no crabgrass control at all. Keep in mind that even Triple Action prevents crabgrass rather than killing it; if crabgrass has already emerged, neither bag will remove it and you will need a dedicated post-emergent crabgrass killer instead. The win with Triple Action is getting it down early, before the crabgrass seeds wake up.',
    },
  ],
}

const bahiaVsBermuda: GrassComparisonEntry = {
  slug: 'bahia-vs-bermuda',
  kind: 'grass',
  shortLabel: 'Bahia vs Bermuda',
  h1: 'Bahia vs Bermuda Grass: Which Is Right for Your Lawn?',
  metaTitle: 'Bahia vs Bermuda Grass: Which Should You Plant? (2026)',
  metaDescription:
    'Bahia vs Bermuda grass compared head-to-head: maintenance level, mowing, water and fertilizer needs, soil tolerance, drought, cost, and which fits your lawn.',
  lastModified: '2026-08-05',
  a: { key: 'Bahiagrass', guideSlug: 'bahiagrass', displayName: 'Bahiagrass' },
  b: { key: 'Bermudagrass', guideSlug: 'bermudagrass', displayName: 'Bermudagrass' },
  intro: (
    <>
      <p>
        Bahiagrass and bermudagrass are both warm-season grasses built for the heat of the Gulf
        Coast and Deep South, but they sit at opposite ends of the maintenance scale. Bahia is the
        tough, deep-rooted survivor that thrives on sandy, acidic, low-fertility soil and asks for
        almost nothing from you once it is established. Bermuda is the fine-textured, dense,
        aggressive turf that rewards heavy feeding, frequent mowing, and full sun with a manicured
        carpet that handles hard wear.
      </p>
      <p>
        The decision usually comes down to what kind of lawn you actually want and how much soil
        you are working with. Bahia is the practical pick for large, rural, or sandy Florida and
        Gulf-Coast lots where you want green ground cover that resists drought and poor soil without
        a sprinkler system or a fertilizer schedule. Bermuda is for the homeowner who wants a tight,
        golf-course-style lawn in full sun and is willing to put in the hours to keep it that way.
      </p>
      <p>
        Texture is the other tell. Bahia is coarse and open with a V-shaped seed stalk that shoots
        up fast in summer; bermuda is fine and dense. If you run your hand across each, the
        difference is obvious. We break down every dimension below.
      </p>
    </>
  ),
  verdict:
    'Bahia wins for large, sandy, or low-input lawns where drought tolerance and minimal maintenance matter most. Bermuda wins for manicured, high-traffic, full-sun lawns where you want a fine, dense turf and will do the work to keep it.',
  table: [
    { label: 'Climate zone (USDA)', a: '8-11 (warm-season)', b: '7-10 (warm-season)' },
    { label: 'Sun requirement', a: '6+ hours (tolerates light shade)', b: '6-8 hours full sun' },
    { label: 'Shade tolerance', a: 'Low to medium', b: 'Low' },
    { label: 'Traffic tolerance', a: 'Medium (tough, but open canopy is slow to knit back)', b: 'Very high (recovers fastest)' },
    { label: 'Drought tolerance', a: 'Very high (deep, extensive root system)', b: 'High' },
    { label: 'Mowing height', a: '3 to 4 inches (rotary)', b: '0.5 to 1.5 inches (reel) / 1.5 to 2.5 inches (rotary)' },
    { label: 'Mowing frequency in peak season', a: 'Every 7 to 10 days (more often to cut summer seedheads)', b: 'Every 3 to 5 days' },
    { label: 'Annual nitrogen need', a: '2 to 3 lbs / 1,000 sq ft', b: '4 to 5 lbs / 1,000 sq ft' },
    { label: 'Water need (peak)', a: '0.5 to 0.75 inches / week', b: '1.0 to 1.25 inches / week' },
    { label: 'Winter dormancy', a: 'Full dormancy late Nov to March (4 to 5 months)', b: 'Full dormancy late Nov to early April (4 to 5 months)' },
    { label: 'Green-up speed in spring', a: 'Moderate (3 to 5 weeks)', b: 'Fast (recovers in 2 to 3 weeks)' },
    { label: 'Establishment from sod', a: 'Available but less common; 2 to 4 weeks', b: '2 to 4 weeks' },
    { label: 'Establishment from seed', a: 'Easy and standard (primary method; germinates in 1 to 4 weeks, slow to fill)', b: 'Available (60 to 90 days)' },
    { label: 'Cost per pallet of sod (500 sq ft)', a: '$150 to $250 (most lawns are seeded instead)', b: '$150 to $250' },
    { label: 'Maintenance level', a: 'Low', b: 'High' },
  ],
  pickA: {
    headline: 'Pick Bahiagrass if...',
    reasons: [
      'You have sandy, acidic, or low-fertility soil where most turf struggles. Bahia is one of the few grasses that genuinely thrives in poor ground.',
      'Your lawn is large, rural, or unirrigated and you want drought-tolerant ground cover without installing a sprinkler system.',
      'You want the lowest-input lawn possible: minimal fertilizer, infrequent watering, and no reel mower.',
      'You plan to establish from seed (bahia is one of the easiest warm-season grasses to seed, unlike most bermuda lawns sold as sod).',
      'You are in Florida or the lower Gulf Coast (Zone 8b to 11) and a coarse, functional lawn beats a high-maintenance manicured one.',
    ],
  },
  pickB: {
    headline: 'Pick Bermudagrass if...',
    reasons: [
      'You want a fine-textured, dense, manicured lawn and are willing to mow every 3 to 5 days to keep it that way.',
      'Your lawn gets 6+ hours of full sun with no meaningful shade.',
      'You have kids, dogs, or heavy backyard traffic and need turf that recovers from wear in days through stolons and rhizomes.',
      'You can commit to a real fertilizer and irrigation routine (4 to 5 lbs of nitrogen and about an inch of water per week in peak season).',
      'You want a tight, low-mowed surface for sports or a golf-course look rather than a coarse, open canopy.',
    ],
  },
  faqs: [
    {
      question: 'Is bahia or bermuda better for sandy soil?',
      answer:
        'Bahiagrass is the better choice for sandy soil. It evolved on the sandy, acidic, low-fertility soils of the Gulf Coast and develops a deep, extensive root system that pulls water and nutrients from ground where bermuda would struggle without irrigation and feeding. Bermuda can grow in sand, but it needs far more water and fertilizer to look good there. If your soil is sandy and you do not want to amend or irrigate heavily, choose bahia.',
    },
    {
      question: 'Which grass needs less maintenance, bahia or bermuda?',
      answer:
        'Bahiagrass needs much less maintenance than bermuda. Bahia gets by on 2 to 3 pounds of nitrogen per 1,000 square feet per year, tolerates drought with little supplemental water, and is mowed high with an ordinary rotary mower. Bermuda wants 4 to 5 pounds of nitrogen, about an inch of water a week in summer, and mowing every 3 to 5 days to stay dense and tidy. The trade-off is appearance: bahia is coarse and open, while bermuda gives you a finer, denser turf in exchange for the extra work.',
    },
    {
      question: 'Can you grow bahia and bermuda together in the same lawn?',
      answer:
        'Growing bahia and bermuda together rarely stays balanced. Bermuda is aggressive and spreads quickly through both rhizomes and stolons, so in a sunny, well-fed, well-watered lawn it tends to crowd out bahia over a few seasons. Bahia holds its own better on poor, dry, unirrigated ground where bermuda is weaker. In practice most lawns drift toward one or the other based on how they are managed, so it is better to commit to a single grass than to try to maintain a blend.',
    },
    {
      question: 'Why does my bahia lawn have so many tall seed stalks?',
      answer:
        'Those tall, V-shaped stalks are bahiagrass seedheads, and they are normal. Bahia produces them prolifically through the summer, and they grow back fast after mowing, which is the main reason bahia lawns need cutting every 7 to 10 days in peak season even though the grass itself grows slowly. There is no way to stop seedheads entirely without growth regulators, so most homeowners just mow more often during summer to keep the lawn looking neat. The seedheads are also tough and dull mower blades faster than fine-textured grasses do.',
    },
    {
      question: 'Is bahia or bermuda better for a Florida lawn?',
      answer:
        'It depends on the lawn. Bahiagrass is the better fit for large, rural, sandy, or unirrigated Florida properties because it tolerates poor soil and drought with minimal input, which is why it is so common on Gulf-Coast acreage and roadsides. Bermudagrass is the better fit for smaller, full-sun Florida lawns where the owner wants a fine, dense, manicured surface and is set up to fertilize and irrigate regularly. One caveat for bahia: it can yellow from iron deficiency in high-pH soils, so it performs best on the naturally acidic sandy soils it is suited to.',
    },
  ],
}

const zoysiaVsCentipede: GrassComparisonEntry = {
  slug: 'zoysia-vs-centipede',
  kind: 'grass',
  shortLabel: 'Zoysia vs Centipede',
  h1: 'Zoysia vs Centipede Grass: Which Southern Lawn Is Right for You?',
  metaTitle: 'Zoysia vs Centipede Grass: Which Wins? (2026)',
  metaDescription:
    'Zoysia vs centipede grass compared: maintenance level, fertilizer, traffic tolerance, soil pH, cost, and which warm-season lawn fits your yard best.',
  lastModified: '2026-08-05',
  a: { key: 'Zoysiagrass', guideSlug: 'zoysiagrass', displayName: 'Zoysiagrass' },
  b: { key: 'Centipede', guideSlug: 'centipede', displayName: 'Centipedegrass' },
  intro: (
    <>
      <p>
        Zoysiagrass and centipedegrass are both warm-season Southern lawns, but they answer two
        very different questions. Zoysia is the premium choice: a dense, cushioned carpet that feels
        wonderful underfoot, crowds out weeds, and shrugs off foot traffic better than almost any
        other warm-season grass. Centipede is the famous "lazy man's grass," a slow-growing,
        light apple-green turf that thrives on poor, acidic soil and asks for less fertilizer and
        attention than any other common lawn grass. One rewards effort with a showpiece lawn; the
        other rewards neglect with a perfectly acceptable one.
      </p>
      <p>
        The decision comes down to how much work you want to put in and what your soil and traffic
        demand. Zoysia takes longer to establish, costs more upfront, and wants 2 to 3 pounds of
        nitrogen a year to look its best, but it gives you a thick, traffic-tolerant turf in return.
        Centipede barely needs feeding (1 to 2 pounds of nitrogen and no more), tolerates the
        acidic Coastal Plain soils that other grasses hate, but stays thin under traffic and is slow
        to recover from damage.
      </p>
      <p>
        If your lawn is the heart of family life on decent soil and you do not mind the extra
        feeding, zoysia is the upgrade. If you want a hands-off Southern lawn on acidic soil and
        light foot traffic, centipede is hard to beat on input cost. We break down every dimension
        below.
      </p>
    </>
  ),
  verdict:
    'Zoysia wins for a dense, cushioned, traffic-tolerant lawn that looks premium and crowds out weeds. Centipede wins for the lowest-input Southern lawn on acidic soil, with far less feeding and mowing.',
  table: [
    { label: 'Climate zone (USDA)', a: '6-10 (warm-season, transition-capable)', b: '7-9 (warm, Coastal Plain)' },
    { label: 'Sun requirement', a: '4-6 hours (handles part shade)', b: '6+ hours (light shade only)' },
    { label: 'Shade tolerance', a: 'Medium', b: 'Low to medium' },
    { label: 'Traffic tolerance', a: 'High (but slow to repair)', b: 'Low' },
    { label: 'Drought tolerance', a: 'Very high', b: 'Medium' },
    { label: 'Mowing height', a: '1 to 2 inches', b: '1 to 2 inches' },
    { label: 'Mowing frequency in peak season', a: 'Every 7 to 14 days', b: 'Every 10 to 14 days' },
    { label: 'Annual nitrogen need', a: '2 to 3 lbs / 1,000 sq ft', b: '1 to 2 lbs / 1,000 sq ft (lowest of any lawn grass)' },
    { label: 'Water need (peak)', a: '0.75 to 1.0 inches / week', b: '0.75 to 1.0 inches / week' },
    { label: 'Winter dormancy', a: 'Full dormancy mid-Nov to mid-April (5 months)', b: 'Browns out late Nov to early April (4 to 5 months)' },
    { label: 'Green-up speed in spring', a: 'Slow (4 to 6 weeks)', b: 'Slow (4 to 6 weeks)' },
    { label: 'Establishment from sod', a: '4 to 8 weeks', b: '4 to 8 weeks' },
    { label: 'Establishment from seed', a: 'Limited cultivars; sod usually required', b: 'Available but slow (3 to 12 months to fill in)' },
    { label: 'Cost per pallet of sod (500 sq ft)', a: '$300 to $500', b: '$150 to $300' },
    { label: 'Maintenance level', a: 'Medium', b: 'Low' },
  ],
  pickA: {
    headline: 'Pick Zoysiagrass if...',
    reasons: [
      'You want a dense, cushioned, carpet-like lawn that feels premium underfoot and crowds out most weeds on its own.',
      'You have kids, dogs, or regular backyard traffic and need turf that holds up to wear (even if it repairs slowly).',
      'Your lawn has some partial shade (4 to 6 hours of sun) where centipede would thin out.',
      'You are in the transition zone (Zone 6 or 7) where centipede risks winterkill.',
      'You are willing to feed 2 to 3 times a year and pay more upfront for sod to get a showpiece lawn.',
    ],
  },
  pickB: {
    headline: 'Pick centipedegrass if...',
    reasons: [
      'You want the lowest-maintenance Southern lawn possible (1 to 2 lbs nitrogen per year, mowing every 10 to 14 days).',
      'Your soil is naturally acidic with a pH between 5.0 and 6.0, common across the Coastal Plain from Georgia to East Texas.',
      'Your lawn gets at least 6 hours of direct sun and only light foot traffic.',
      'You want the cheaper path to a Southern lawn (centipede sod runs less than zoysia, and seed is an option).',
      'You would rather accept a thinner, slower-recovering turf than spend weekends feeding and managing the lawn.',
    ],
  },
  faqs: [
    {
      question: 'Is zoysia or centipede easier to maintain?',
      answer:
        'Centipedegrass is easier to maintain than zoysiagrass. Centipede needs only 1 to 2 pounds of nitrogen per 1,000 square feet per year (the lowest of any common lawn grass), tolerates acidic soil without liming, and mows every 10 to 14 days. Zoysia wants 2 to 3 pounds of nitrogen, looks its best with regular feeding, and forms such a dense thatch layer that it occasionally needs dethatching. If your goal is the least possible work, centipede is the clear winner. Zoysia is still moderate-maintenance, just not as hands-off as centipede.',
    },
    {
      question: 'Which grass handles foot traffic better, zoysia or centipede?',
      answer:
        'Zoysiagrass handles foot traffic far better than centipedegrass. Zoysia forms a dense, cushioned turf that resists wear and stays intact under kids, dogs, and regular backyard use; centipede is thin-bladed, sparse, and tears up quickly under the same traffic. The one caveat is recovery speed: zoysia is durable but slow to repair once it is damaged, so badly worn spots can take weeks to fill back in. For a high-traffic family lawn, zoysia is the better pick; centipede is best reserved for low-traffic yards.',
    },
    {
      question: 'Can centipede grow where zoysia struggles?',
      answer:
        'Yes, centipedegrass tolerates poor, acidic soil better than zoysiagrass. Centipede thrives at a soil pH of 5.0 to 6.0 and grows happily on the sandy, low-fertility soils of the Southern Coastal Plain where zoysia would need amending and regular feeding to look good. If you have naturally acidic soil and do not want to fight it, centipede is purpose-built for those conditions. Zoysia is more adaptable across soil types but performs best on fertile, well-drained ground with a near-neutral pH.',
    },
    {
      question: 'Which is cheaper, zoysia or centipede?',
      answer:
        'Centipedegrass is cheaper than zoysiagrass both upfront and over time. Centipede sod runs roughly $150 to $300 per 500 square foot pallet, and centipede can also be established from seed (slowly), which zoysia generally cannot. Zoysia is almost always sold as sod at $300 to $500 per pallet. Centipede also costs less to maintain because it needs about half the fertilizer and fewer mows. Zoysia costs more on every front, but you are paying for a denser, more traffic-tolerant, more premium-looking lawn.',
    },
    {
      question: 'Does zoysia or centipede stay green longer?',
      answer:
        'Both zoysia and centipede are warm-season grasses that go dormant and brown out in winter, and neither stays green much longer than the other in the same climate. Both green up slowly in spring, typically taking 4 to 6 weeks once soil temperatures rise. The bigger appearance difference is in active season: zoysia is a deeper green and forms a denser, more uniform carpet, while centipede has a characteristic lighter, apple-green color and a more open texture. If color and density matter to you, zoysia looks richer; centipede looks acceptable with almost no effort.',
    },
  ],
}

const tenacityVsQuinclorac: BrandComparisonEntry = {
  slug: 'tenacity-vs-quinclorac',
  kind: 'brand',
  shortLabel: 'Tenacity vs Quinclorac',
  h1: 'Tenacity vs Quinclorac: Which Herbicide Should You Use?',
  metaTitle: 'Tenacity vs Quinclorac: Which Weed Killer Wins? (2026)',
  metaDescription:
    'Tenacity (mesotrione) vs quinclorac compared: what each one controls, crabgrass performance, grass safety, surfactant, and whether you can spray at seeding.',
  lastModified: '2026-06-05',
  a: {
    brand: 'Tenacity',
    displayName: 'Tenacity Herbicide (mesotrione)',
    productType: 'Selective systemic herbicide, pre- and post-emergent (liquid concentrate)',
    productsCategory: 'weed-control',
    productKey: 'post-emergent-tenacity',
    buyReason: 'Broad-spectrum control on the widest weed list, safe at seeding.',
  },
  b: {
    brand: 'Quinclorac',
    displayName: 'Quinclorac Post-Emergent Herbicide',
    productType: 'Selective post-emergent herbicide, crabgrass specialist (liquid or dry concentrate)',
    productsCategory: 'weed-control',
    productKey: 'post-emergent-quinclorac',
    buyReason: 'The specialist when crabgrass is your main problem and already established.',
  },
  intro: (
    <>
      <p>
        Tenacity and quinclorac are two of the most popular do-it-yourself lawn herbicide actives,
        and they often get compared because both can take on crabgrass. They are not the same tool,
        though. Tenacity is the brand name for mesotrione, a selective systemic herbicide with both
        pre-emergent and post-emergent activity. It controls a broad list of broadleaf and some
        grassy weeds, and its standout feature is that the label allows application at the time you
        seed many cool-season grasses, which almost no other herbicide can claim. Its signature side
        effect is temporary white bleaching of treated weeds (and sometimes the lawn), which grows
        out over a few weeks.
      </p>
      <p>
        Quinclorac is a post-emergent specialist and one of the most reliable crabgrass killers
        homeowners can buy. It controls crabgrass at multiple growth stages and also handles some
        broadleaf weeds like clover and dandelion, but it has no pre-emergent activity and is not
        labeled for application at seeding. The two also differ on surfactant: Tenacity is paired
        with a nonionic surfactant (NIS) for post-emergent sprays, while quinclorac needs methylated
        seed oil (MSO) to work on crabgrass.
      </p>
      <p>
        The short version: reach for Tenacity when you want broad-spectrum control or you are
        seeding a cool-season lawn at the same time, and reach for quinclorac when crabgrass has
        already emerged and killing it is the whole job. Both are real pesticides with grass-safety
        and timing restrictions that vary by product, so always read and follow the label on the
        jug you actually buy.
      </p>
    </>
  ),
  verdict:
    'Tenacity wins for broad-spectrum control and the unique ability to spray at cool-season seeding, with temporary bleaching as the trade-off. Quinclorac wins when crabgrass has already emerged and you want a proven crabgrass killer. Always follow the product label for rates and grass restrictions.',
  table: [
    { label: 'Active ingredient', a: 'Mesotrione (HPPD inhibitor)', b: 'Quinclorac (quinoline carboxylic acid)' },
    { label: 'Weed timing', a: 'Pre-emergent AND post-emergent', b: 'Post-emergent only (no pre-emergent activity)' },
    { label: 'Best target weeds', a: 'Broad list: crabgrass, nimblewill, creeping bentgrass, clover, dandelion, ground ivy, and many broadleaf weeds (see label)', b: 'Crabgrass (specialist) plus some broadleaf such as clover and dandelion (see label)' },
    { label: 'Crabgrass control', a: 'Yes, usually 2 applications spaced per label', b: 'Yes, one of the strongest options across multiple growth stages' },
    { label: 'At-seeding use', a: 'Yes on many cool-season grasses at seeding (label-dependent)', b: 'No, not labeled for use at seeding; observe reseeding intervals on the label' },
    { label: 'Surfactant needed', a: 'Nonionic surfactant (NIS) for post-emergent sprays', b: 'Methylated seed oil (MSO) for crabgrass control' },
    { label: 'Grass safety', a: 'Safe on labeled cool-season grasses (e.g. KBG, tall/fine fescue, perennial rye); will injure bermudagrass; check label for warm-season', b: 'Tolerated by many lawn grasses but can injure some (e.g. certain fine fescues, St. Augustine, centipede); check label' },
    { label: 'Signature effect', a: 'Temporary white bleaching of treated weeds and turf (grows out)', b: 'No bleaching; weeds yellow and decline over days to weeks' },
    { label: 'Speed of kill', a: 'Slow; bleaching shows in days, full kill over 2 to 3 weeks', b: 'Slow on crabgrass; full results over 1 to 3 weeks' },
    { label: 'Mode of action', a: 'Systemic, blocks carotenoid pigment synthesis', b: 'Systemic; auxin-type on broadleaf plus crabgrass-specific activity' },
    { label: 'Application rate', a: 'Per label (commonly a small per-1,000 sq ft volume)', b: 'Per label (varies by formulation)' },
    { label: 'Best for', a: 'New cool-season seedings and broad-spectrum weed cleanup', b: 'Knocking down crabgrass that has already emerged' },
  ],
  pickA: {
    headline: 'Pick Tenacity (mesotrione) if...',
    reasons: [
      'You are seeding or overseeding a cool-season lawn and want to control weeds at the same time, which the Tenacity label uniquely allows for many grasses.',
      'You need broad-spectrum control across both broadleaf weeds and several grassy weeds (crabgrass, nimblewill, creeping bentgrass) rather than just crabgrass.',
      'You want some pre-emergent activity in addition to post-emergent knockdown from one product.',
      'You are comfortable with temporary white bleaching on treated weeds and possibly the lawn, knowing it grows out.',
      'Your lawn is a labeled cool-season grass (not bermudagrass, which Tenacity injures) and you will read the grass list on the label first.',
    ],
  },
  pickB: {
    headline: 'Pick Quinclorac if...',
    reasons: [
      'Crabgrass has already emerged and killing it is the main job; quinclorac is one of the most reliable crabgrass killers available to homeowners.',
      'You need to control crabgrass that is past the seedling stage, since quinclorac works across multiple growth stages.',
      'You do not want bleaching on your lawn and prefer a weed killer that works without the white cosmetic effect.',
      'You also have some broadleaf weeds like clover or dandelion to clean up alongside the crabgrass.',
      'You are not seeding right now and your grass type is on the quinclorac label (it can injure some grasses such as certain fine fescues, St. Augustine, and centipede).',
    ],
  },
  faqs: [
    {
      question: 'What is the main difference between Tenacity and quinclorac?',
      answer:
        'The main difference is breadth versus specialization. Tenacity (mesotrione) is a broad-spectrum, selective herbicide with both pre-emergent and post-emergent activity, and its label uniquely allows application when seeding many cool-season grasses. Quinclorac is a post-emergent specialist focused on crabgrass, with some broadleaf control, and it is not labeled for use at seeding. Tenacity also causes temporary white bleaching of treated weeds, while quinclorac does not. Choose Tenacity for broad control or seeding situations, and quinclorac when emerged crabgrass is the problem. Follow the label on the product you buy.',
    },
    {
      question: 'Which one is better for crabgrass, Tenacity or quinclorac?',
      answer:
        'Quinclorac is generally regarded as the stronger crabgrass killer, especially once crabgrass has emerged and grown past the seedling stage, because it stays effective across multiple growth stages. Tenacity also controls crabgrass and usually does so over two applications, and it has the advantage of some pre-emergent activity plus broad-spectrum control of other weeds. If crabgrass is the entire problem and it is already up, quinclorac is the more targeted choice. If you want one product for crabgrass plus a range of other weeds, Tenacity is more versatile. Use the rates on the label.',
    },
    {
      question: 'Can I apply Tenacity or quinclorac when I seed new grass?',
      answer:
        'Tenacity is labeled for application at the time of seeding for many cool-season grasses, which is one of its signature advantages and why people use it on new lawns. Quinclorac is not labeled for use at seeding and carries reseeding interval restrictions, so applying it around new seed can interfere with establishment. If you are starting or overseeding a cool-season lawn, Tenacity is the one designed for that job. Always confirm your specific grass type and timing on the product label before applying either one.',
    },
    {
      question: 'What surfactant does each herbicide need?',
      answer:
        'Tenacity calls for a nonionic surfactant (NIS) when used as a post-emergent spray; the surfactant helps the mesotrione stick to and penetrate the weed foliage. Quinclorac is typically mixed with methylated seed oil (MSO) for crabgrass control, which improves uptake on tough grassy weeds. Using the wrong surfactant, or skipping it, is a common reason these products underperform. Check the label for the exact surfactant type and rate, since requirements can vary by formulation and by the weed you are targeting.',
    },
    {
      question: 'Will Tenacity or quinclorac hurt my lawn?',
      answer:
        'Both are selective and safe on the grasses listed on their labels, but neither is safe on every lawn. Tenacity is labeled for many cool-season grasses but will injure bermudagrass, which is actually why some people use it to remove bermuda from cool-season turf. Quinclorac is tolerated by many lawn grasses but can injure some, including certain fine fescues and warm-season grasses like St. Augustine and centipede. Tenacity also causes temporary white bleaching of the lawn that grows out. Before spraying, read the grass-safety list on the label and confirm your grass type is approved.',
    },
  ],
}

const prodiamineVsDithiopyr: BrandComparisonEntry = {
  slug: 'prodiamine-vs-dithiopyr',
  kind: 'brand',
  shortLabel: 'Prodiamine vs Dithiopyr',
  h1: 'Prodiamine vs Dithiopyr: Which Pre-Emergent Should You Use?',
  metaTitle: 'Prodiamine vs Dithiopyr: Which Pre-Emergent Wins (2026)',
  metaDescription:
    'Prodiamine vs dithiopyr: soil residual length, early post-emergent crabgrass control, application timing, cost per 1,000 sq ft, and which pre-emergent your lawn needs.',
  lastModified: '2026-06-05',
  a: {
    brand: 'Prodiamine',
    displayName: 'Prodiamine (Barricade and generics)',
    productType: 'Pre-emergent herbicide (dinitroaniline, long residual)',
    productsCategory: 'weed-control',
    productKey: 'pre-emergent-barricade',
    buyReason: 'Longest residual per application, so one pass covers a whole season.',
  },
  b: {
    brand: 'Dithiopyr',
    displayName: 'Dithiopyr (Dimension and generics)',
    productType: 'Pre-emergent herbicide with early post-emergent activity',
    productsCategory: 'weed-control',
    productKey: 'pre-emergent-dimension',
    buyReason: 'Pre-emergent that also catches crabgrass just after it sprouts.',
  },
  intro: (
    <>
      <p>
        Prodiamine and dithiopyr are two of the most widely used pre-emergent herbicides for
        stopping crabgrass and other annual weeds, and homeowners often want to know which one to
        put down. Both are applied before weeds take hold and both form a barrier in the top layer
        of soil, but they are not identical tools. Prodiamine (sold as Barricade and generics) is a
        pure pre-emergent with the longest soil residual of the common options, up to about 6
        months per season at higher labeled rates, and it is usually the cheapest per 1,000 square
        feet.
      </p>
      <p>
        Dithiopyr (sold as Dimension and generics) does something prodiamine cannot: alongside its
        pre-emergent action it has early post-emergent activity, meaning it can still kill young
        crabgrass that has already germinated, up to roughly its early tiller stage. That
        forgiveness comes at the cost of a shorter residual, so it generally protects for a few
        months rather than most of the season.
      </p>
      <p>
        The real decision is about timing and forgiveness. If you can get a single application down
        early, before soil temperatures push crabgrass to germinate, prodiamine gives you the
        longest, cheapest season-long barrier. If you think you may have already missed that
        window, dithiopyr buys you a second chance on weeds that are just coming up. Whichever you
        choose, follow the product label for rates, timing, and any seeding restrictions.
      </p>
    </>
  ),
  verdict:
    'Prodiamine wins for the longest soil residual and lowest cost per 1,000 sq ft, making it the best single early-spring application for season-long crabgrass prevention. Dithiopyr wins when you may have missed the pre-emergent window, because it can also kill young crabgrass that has already emerged, in exchange for a shorter residual.',
  table: [
    { label: 'Chemical class', a: 'Dinitroaniline (microtubule inhibitor)', b: 'Pyridine (microtubule inhibitor)' },
    { label: 'Mode of action', a: 'Pre-emergent only', b: 'Pre-emergent plus early post-emergent' },
    {
      label: 'Post-emergent crabgrass reach',
      a: 'None; will not kill any weed that is already up',
      b: 'Young crabgrass only, through the early tiller stage (per label)',
    },
    {
      label: 'Soil residual',
      a: 'Longest; up to about 6 months per season at higher labeled rates',
      b: 'Shorter; commonly about 3 to 4 months (per label)',
    },
    {
      label: 'Application window forgiveness',
      a: 'Strict; must be down before weeds germinate',
      b: 'More forgiving; can catch crabgrass that has just emerged',
    },
    {
      label: 'Best use',
      a: 'Single early-spring application for season-long prevention',
      b: 'Backup when the strict pre-emergent window was missed',
    },
    { label: 'Cost per 1,000 sq ft', a: 'Lowest; very low use rate per label', b: 'Low, typically a bit higher than prodiamine' },
    {
      label: 'Seeding safety',
      a: 'Not for use at seeding; follow label wait intervals',
      b: 'Not for use at seeding; follow label wait intervals',
    },
    {
      label: 'Common formulations',
      a: 'Sprayable concentrate (WDG) and granular',
      b: 'Sprayable concentrate, plus granular and on-fertilizer crabgrass preventers',
    },
    {
      label: 'Common brands',
      a: 'Barricade; generic prodiamine 65 WDG',
      b: 'Dimension; generic dithiopyr; many granular crabgrass preventers',
    },
    {
      label: 'Target weeds (per label)',
      a: 'Crabgrass, goosegrass, foxtail, annual bluegrass, and other listed annuals',
      b: 'Crabgrass, foxtail, and other listed annual weeds',
    },
  ],
  pickA: {
    headline: 'Pick prodiamine (Barricade) if...',
    reasons: [
      'You can apply early, before soil temperatures push crabgrass to germinate, and want one application to last the whole season.',
      'You want the longest soil residual available from a single pre-emergent, up to about 6 months at higher labeled rates.',
      'You want the lowest cost per 1,000 sq ft; prodiamine\'s very low use rate makes it the most economical option.',
      'You only need prevention and have no weeds up yet, so early post-emergent activity is not a factor.',
      'You are not seeding this season, since prodiamine is not for use at seeding and has label wait intervals before and after.',
    ],
  },
  pickB: {
    headline: 'Pick dithiopyr (Dimension) if...',
    reasons: [
      'You think you may have missed the strict pre-emergent window and want forgiveness, because dithiopyr can still kill young crabgrass that has already emerged.',
      'You want both pre-emergent prevention and early post-emergent control of crabgrass through its early tiller stage (per label).',
      'A slightly shorter residual is an acceptable trade for that wider application window.',
      'You prefer a product also sold on fertilizer or as a granular crabgrass preventer for an easy spreader application.',
      'You understand it will not control crabgrass once it has tillered and matured, so timing still matters.',
    ],
  },
  faqs: [
    {
      question: 'What is the main difference between prodiamine and dithiopyr?',
      answer:
        'The main difference is that prodiamine is a pre-emergent only with the longest soil residual, while dithiopyr is a pre-emergent that also has early post-emergent activity on young crabgrass. Prodiamine must be applied before crabgrass germinates and then prevents it for up to about 6 months at higher labeled rates. Dithiopyr can be applied slightly later and still kill crabgrass that has already emerged, up to its early tiller stage, but its residual is shorter. Always follow the product label for rates and timing.',
    },
    {
      question: 'Can dithiopyr kill crabgrass that has already come up?',
      answer:
        'Yes, but only young crabgrass. Dithiopyr has early post-emergent activity and can control crabgrass that has germinated up to roughly its early tiller stage, which many labels describe as before it reaches about the 3- to 4-leaf stage. Once crabgrass has tillered and matured, dithiopyr will no longer control it and you would need a dedicated post-emergent crabgrass herbicide. Prodiamine has no post-emergent activity at all and will not kill any weed that is already up. Check the specific product label for the exact growth stage it controls.',
    },
    {
      question: 'Which is cheaper, prodiamine or dithiopyr?',
      answer:
        'Prodiamine is generally the cheaper of the two per 1,000 square feet. Its very low use rate and long residual mean a single early-spring application covers a lot of area for a small amount of product, which is why prodiamine (Barricade) is often the most economical pre-emergent available. Dithiopyr usually costs a bit more per 1,000 sq ft, and granular or on-fertilizer dithiopyr products cost more still. If pure cost per area is the priority and you only need prevention, prodiamine wins.',
    },
    {
      question: 'How long does each pre-emergent last?',
      answer:
        'Prodiamine lasts the longest, up to about 6 months per season at higher labeled rates, which is why it is popular for a single early-spring application. Dithiopyr has a shorter residual, commonly cited around 3 to 4 months. Actual barrier life for both depends on the rate you apply, soil temperature, rainfall, and irrigation, so use the label rate that matches the protection window you want. Higher labeled rates generally extend residual within the allowed range.',
    },
    {
      question: 'Can I use prodiamine or dithiopyr when seeding a new lawn?',
      answer:
        'No, neither prodiamine nor dithiopyr is meant for use at seeding. Both work by stopping seedlings from establishing roots, so they will prevent your desirable grass seed from establishing just as they prevent weeds. Each label specifies wait intervals before and after seeding, and prodiamine in particular requires waiting until new grass is well established. If you are seeding this season, use a seeding-safe pre-emergent such as mesotrione or siduron per its label, or wait out the residual before applying either of these.',
    },
  ],
}

const sundayVsDiy: BrandComparisonEntry = {
  slug: 'sunday-lawn-care-vs-diy',
  kind: 'brand',
  shortLabel: 'Sunday Lawn Care vs DIY',
  h1: 'Sunday Lawn Care vs. Doing It Yourself: Is the Subscription Worth It?',
  metaTitle: 'Sunday Lawn Care Alternative: Subscription vs DIY (2026)',
  metaDescription:
    'Sunday Lawn Care vs the DIY route compared: cost per year, what you actually get, soil testing, product control, and when a subscription beats buying your own.',
  lastModified: '2026-06-14',
  a: {
    brand: 'Sunday',
    displayName: 'Sunday Lawn Care Subscription',
    productType: 'Done-for-you nutrient subscription (mailed liquid pouches)',
  },
  b: {
    brand: 'DIY',
    displayName: 'DIY with WhatGrassIsThis.com tools',
    productType: 'Free lawn tools + your own store-bought products',
    productsCategory: 'fertilizer',
    // Sunday (side a) is a mail-order subscription with no Amazon SKU, so it stays
    // bare. The DIY side's honest analog to Sunday's all-natural liquid pouches is a
    // retail hose-end liquid feed you buy once instead of subscribing.
    productKey: 'fertilizer-neighbors-envy',
    buyReason: 'An all-in-one hose-end liquid feed you buy once at retail instead of subscribing to mailed pouches.',
    badge: { label: 'DIY starting point', tone: 'green', tier: 'editor' },
  },
  intro: (
    <>
      <p>
        Sunday Lawn Care is a subscription service that builds a plan from satellite imagery and a
        mailed-in soil test, then ships you 1 to 4 seasonal pouches of all-natural liquid nutrients
        you spray on with a hose. It is genuinely convenient: the thinking is done for you and the
        products show up when it is time to apply them. Most lawns land between $129 and $229 a year,
        and larger lawns can run up toward $450.
      </p>
      <p>
        The DIY route is the same job done with free tools instead of a recurring box. WhatGrassIsThis.com
        identifies your grass from a photo, and free calculators tell you exactly how much fertilizer,
        water, and mowing height your specific lawn and ZIP code need. You then buy the products yourself
        at retail, in any brand and form you like, which usually costs less per year than a subscription.
        The trade is effort: you do the buying and the timing, though Premium reminders and unlimited care plans
        ($47.99 a year) automate most of that. The honest split is convenience versus cost and
        control, and which one wins depends on how hands-on you want to be.
      </p>
    </>
  ),
  verdict:
    'Sunday wins if you want a hands-off, all-natural plan that arrives on schedule with zero decisions. The DIY route wins on cost, product choice, and breadth, since the free tools also cover weeds, disease, mowing, and watering that a feeding subscription does not.',
  table: [
    { label: 'Model', a: 'Annual subscription, auto-shipped', b: 'Free tools, you buy products as needed' },
    { label: 'Typical cost per year', a: '$129 to $229 (up to ~$450 for large lawns)', b: '$0 for tools; ~$40 to $120 in products you choose ($47.99/yr optional premium)' },
    { label: 'Grass identification', a: 'Not a focus (assumes you know)', b: 'Free photo ID, no signup' },
    { label: 'Soil test', a: 'Mail-in kit included, lab results in 2 to 3 weeks', b: 'Upload your own extension test (~$15 to $20) into the free Soil Test Analyzer' },
    { label: 'What it plans', a: 'Feeding and nutrients', b: 'Feeding, watering, mowing height, aeration, weed and herbicide timing, problem diagnosis' },
    { label: 'Products', a: 'Sunday liquid pouches only', b: 'Any brand, granular or liquid, bought at retail' },
    { label: 'Product form', a: 'Hose-end liquid', b: 'Your choice' },
    { label: 'Personalization', a: 'Satellite + soil test + climate', b: 'Your grass, ZIP, lawn size, soil test, and goals across every calculator' },
    { label: 'Weed control', a: 'Limited to what is shipped', b: 'Herbicide Timing Calculator + product picks for your weed and grass type' },
    { label: 'Disease and pest help', a: 'Not included', b: '3 diagnoses/month on Free; unlimited on Premium' },
    { label: 'Reminders', a: 'Shipments arrive on schedule', b: 'Smart seasonal reminders on premium tier' },
    { label: 'Effort required', a: 'Low (spray when it arrives)', b: 'Medium (you buy and apply on your own timing)' },
    { label: 'Lock-in', a: 'Recurring subscription', b: 'None' },
    { label: 'Best for', a: 'Hands-off homeowners who value all-natural and convenience', b: 'Cost-conscious DIYers who want control and full-lawn guidance' },
  ],
  pickA: {
    headline: 'Pick Sunday if...',
    reasons: [
      'You want a hands-off plan where the products and timing are decided for you and shipped automatically.',
      'You prefer all-natural, ready-to-spray liquid nutrients over measuring out granular products.',
      'You want a lab soil test included without sourcing one yourself.',
      'Convenience is worth paying $129 to $229 a year, and you do not want to research products.',
      'Your main goal is feeding the lawn, not diagnosing weeds, disease, or pests.',
    ],
  },
  pickB: {
    headline: 'Pick the DIY route if...',
    reasons: [
      'You want to spend less and are comfortable buying your own products at retail.',
      'You want control over which brand and form you use, granular or liquid, organic or synthetic.',
      'You need help with more than feeding: weeds, mowing height, watering, aeration, and problem diagnosis.',
      'You want free tools that personalize to your grass type, ZIP, and lawn size with no subscription.',
      'You are willing to handle timing yourself, or use Premium reminders and unlimited care plans for $47.99 a year.',
    ],
  },
  faqs: [
    {
      question: 'Is Sunday Lawn Care worth it?',
      answer:
        'Sunday is worth it if you value convenience and an all-natural, done-for-you plan over cost. At $129 to $229 a year for most lawns, you are paying for the soil test, the satellite-built plan, and pouches that arrive on schedule so you never have to decide what to buy. If you are comfortable buying your own fertilizer and following a free schedule, you can usually cover the same feeding for $40 to $120 a year and also get guidance on weeds, watering, and mowing that Sunday does not include. The question is whether the time saved is worth the premium.',
    },
    {
      question: 'What is a cheaper alternative to Sunday Lawn Care?',
      answer:
        'The cheapest alternative is doing it yourself with free tools and store-bought products. WhatGrassIsThis.com identifies your grass from a photo for free and its calculators tell you exactly how much fertilizer, water, and what mowing height your lawn needs based on your grass type and ZIP code. You then buy the products at retail in any brand you want, which usually runs $40 to $120 a year versus $129 to $229 for a Sunday subscription. Free includes three lawn diagnoses per month. The optional Premium tier ($47.99 a year) adds unlimited diagnoses, reminders, and unlimited care plans.',
    },
    {
      question: 'Does Sunday Lawn Care handle weeds and lawn diseases?',
      answer:
        'Sunday is primarily a feeding subscription, and weed control is limited to whatever products are included in your plan. It does not diagnose lawn diseases or pests. If weeds, disease, or insects are your main problem, a feeding subscription will not solve them. The DIY route covers these directly: the Herbicide Timing Calculator tells you when to apply pre- and post-emergents for your grass type, and Free includes three photo diagnoses per month for disease, pests, and nutrient deficiencies.',
    },
    {
      question: 'Can I get a personalized lawn plan without a subscription?',
      answer:
        'Yes. You do not need a subscription to get a plan tailored to your lawn. WhatGrassIsThis.com builds recommendations from your grass type, ZIP code, lawn size, and soil test results using free calculators, and the free tier includes a personalized care plan each month. Unlike a subscription, nothing auto-ships and you are not locked in. The difference is that you buy the products yourself rather than having them mailed, which is what keeps the cost down.',
    },
    {
      question: 'How does Sunday compare for the price?',
      answer:
        'For the price, Sunday buys you convenience and curation, not necessarily better results. The nutrients are sound and all-natural, but a knowledgeable DIYer can match the feeding with retail products for roughly half the cost. Where Sunday earns its premium is the hands-off experience: no decisions, no measuring, no timing to track. Where the DIY route wins is breadth and cost, since the same free tools also handle the weeds, mowing, watering, and diagnosis that fall outside a feeding subscription.',
    },
  ],
}

const perennialRyegrassVsTallFescue: GrassComparisonEntry = {
  slug: 'perennial-ryegrass-vs-tall-fescue',
  kind: 'grass',
  shortLabel: 'Perennial Ryegrass vs Tall Fescue',
  h1: 'Perennial Ryegrass vs Tall Fescue: Which Cool-Season Grass Wins?',
  metaTitle: 'Perennial Ryegrass vs Tall Fescue: Comparison (2026)',
  metaDescription:
    'Perennial ryegrass vs tall fescue compared: germination speed, heat and drought tolerance, wear, shade, overseeding use, and which cool-season grass fits your lawn.',
  lastModified: '2026-06-19',
  a: {
    key: 'Perennial Ryegrass',
    guideSlug: 'perennial-ryegrass',
    displayName: 'Perennial Ryegrass',
  },
  b: { key: 'Tall Fescue', guideSlug: 'tall-fescue', displayName: 'Tall Fescue' },
  intro: (
    <>
      <p>
        Perennial ryegrass and tall fescue are both cool-season bunch grasses, and they get
        compared constantly because they overlap so much: both germinate fast, both take heavy
        traffic, and both show up in the same premium northern seed bags. The difference that
        matters is staying power in heat. Perennial ryegrass is the sprinter, up and green in a
        week, with the best wear tolerance of any cool-season grass, but it thins out under
        prolonged summer heat. Tall fescue is the marathoner, slightly slower to establish but
        rooted three feet deep and built to ride out hot, dry summers that would burn ryegrass
        back.
      </p>
      <p>
        For a permanent single-species lawn in the transition zone or anywhere with real summer
        heat, tall fescue is almost always the smarter base. Perennial ryegrass earns its keep in
        three jobs: fast cover when you need green now, the nurse component in a cool-season blend,
        and winter overseeding of dormant warm-season lawns. Many of the best lawns use both, with
        tall fescue as the backbone and a small fraction of ryegrass for quick establishment.
      </p>
    </>
  ),
  verdict:
    'Tall fescue wins for a permanent lawn that survives summer heat and drought with deep roots. Perennial ryegrass wins for the fastest germination, top wear tolerance, and winter overseeding of dormant warm-season turf.',
  table: [
    { label: 'Climate zone (USDA)', a: '5-7 (cool-season)', b: '4-8 (cool-season, transition)' },
    { label: 'Sun requirement', a: '5+ hours (some shade tolerance)', b: '4-6 hours (handles part shade)' },
    { label: 'Shade tolerance', a: 'Low to medium', b: 'Medium' },
    { label: 'Traffic tolerance', a: 'Very high (best wear of cool-season)', b: 'High (deep roots)' },
    { label: 'Drought tolerance', a: 'Low to medium', b: 'High (3-foot roots)' },
    { label: 'Heat tolerance', a: 'Medium (thins above 90 deg)', b: 'High (handles summers to 95)' },
    { label: 'Cold tolerance', a: 'Medium (can winterkill in severe cold)', b: 'High (survives Zone 4)' },
    { label: 'Germination speed', a: 'Fastest (5 to 10 days)', b: 'Fast (7 to 14 days)' },
    { label: 'Mowing height', a: '1.5 to 2.5 inches', b: '3 to 4 inches' },
    { label: 'Annual nitrogen need', a: '3 to 5 lbs / 1,000 sq ft', b: '2 to 4 lbs / 1,000 sq ft' },
    { label: 'Water need (peak)', a: '1.25 to 1.5 inches / week', b: '1 to 1.25 inches / week' },
    { label: 'Spreading habit', a: 'Bunch grass (does not spread)', b: 'Bunch grass (does not spread)' },
    { label: 'Recovery from damage', a: 'Slow (needs overseeding)', b: 'Slow (needs overseeding)' },
    { label: 'Blade width', a: 'Fine to medium (2-4 mm), glossy underside', b: 'Coarse (4-8 mm), prominent veins' },
    { label: 'Longevity / persistence', a: 'Shorter-lived; thins in heat', b: 'Long-lived; persistent' },
    { label: 'Maintenance level', a: 'Medium', b: 'Medium' },
  ],
  pickA: {
    headline: 'Pick perennial ryegrass if...',
    reasons: [
      'You need the fastest possible green-up; it germinates in 5 to 10 days, the quickest of any cool-season grass.',
      'You are overseeding a dormant warm-season lawn (bermuda or zoysia) for winter color.',
      'You want top-tier wear tolerance for a high-traffic or sports lawn.',
      'You are building a cool-season seed blend and want a fast-establishing nurse component.',
      'Your summers stay mild and you rarely see prolonged heat above 90 degrees.',
    ],
  },
  pickB: {
    headline: 'Pick tall fescue if...',
    reasons: [
      'You live in the transition zone or anywhere with hot, dry summers; its deep roots handle heat ryegrass cannot.',
      'You want a permanent single-species lawn that persists for years without thinning out each summer.',
      'You want lower water and fertilizer needs across the season.',
      'Your lawn has partial shade from trees.',
      'You want a tougher, more drought-proof lawn and can accept slightly slower germination.',
    ],
  },
  faqs: [
    {
      question: 'Is perennial ryegrass or tall fescue better for a lawn?',
      answer:
        'For a permanent lawn in most climates, tall fescue is the better single choice because its deep roots survive summer heat and drought that thin perennial ryegrass out. Perennial ryegrass is better when you need fast establishment, maximum wear tolerance, or a winter overseed on a dormant warm-season lawn. The strongest lawns often blend them: tall fescue as the persistent base with a small fraction of perennial ryegrass for quick cover.',
    },
    {
      question: 'Can you mix perennial ryegrass and tall fescue?',
      answer:
        'Yes, and it is a common and effective blend. Keep perennial ryegrass to roughly 10 to 20 percent of the seed mix; it germinates first and protects the seedbed while the slower tall fescue establishes underneath. Going heavier on ryegrass backfires, because it dominates early and then thins out in the first hot summer, leaving gaps. Tall fescue should always be the majority of a heat-tolerant cool-season blend.',
    },
    {
      question: 'Which germinates faster, perennial ryegrass or tall fescue?',
      answer:
        'Perennial ryegrass germinates faster. It emerges in 5 to 10 days under good conditions, while tall fescue takes 7 to 14 days. That speed is exactly why ryegrass is used as a nurse grass in blends and for quick winter overseeding; it gets green cover down before slower grasses catch up.',
    },
    {
      question: 'Does perennial ryegrass come back every year?',
      answer:
        'Perennial ryegrass is a true perennial and does return year to year, but it is shorter-lived and less heat-tolerant than tall fescue. In the transition zone or in hot summers it often thins badly and can behave almost like an annual, needing regular overseeding to stay thick. In cooler northern climates it persists much better.',
    },
    {
      question: 'Is perennial ryegrass good for overseeding bermuda in winter?',
      answer:
        'Yes, perennial ryegrass is the standard choice for winter overseeding of dormant warm-season lawns like bermuda. It germinates quickly, gives a green winter lawn while the bermuda is brown and dormant, and then usually declines as heat returns and the bermuda greens up and outcompetes it, though a clean spring transition sometimes needs irrigation and mowing management. Tall fescue is not used this way because it does not transition out cleanly.',
    },
    {
      question: 'How do you tell perennial ryegrass from tall fescue?',
      answer:
        'Look at blade texture and shine. Perennial ryegrass has fine-to-medium blades with a distinctly glossy underside and grows in a tighter, more uniform clump. Tall fescue has wider, coarser blades with prominent veins and a duller surface. Both are bunch grasses that do not spread, so neither fills in bare spots on its own.',
    },
  ],
}

const peatMossVsTopsoil: BrandComparisonEntry = {
  slug: 'peat-moss-vs-topsoil',
  kind: 'brand',
  shortLabel: 'Peat Moss vs Topsoil',
  h1: 'Peat Moss vs Topsoil: Which Should You Use on Your Lawn?',
  metaTitle: 'Peat Moss vs Topsoil: Which to Use (2026)',
  metaDescription:
    'Peat moss vs topsoil compared: what each is for, pH, water retention, leveling, seeding, cost, and which one your lawn project actually needs.',
  lastModified: '2026-06-19',
  a: {
    brand: 'Peat Moss',
    displayName: 'Sphagnum Peat Moss',
    productType: 'Organic, acidic, moisture-holding soil amendment',
    productsCategory: 'fertilizer',
    productKey: 'soil-peat-moss',
    buyReason: 'Holds moisture and loosens heavy soil; best worked into a seedbed.',
  },
  b: {
    brand: 'Topsoil',
    displayName: 'Bagged Topsoil',
    productType: 'Screened mineral soil for leveling and fill',
    productsCategory: 'fertilizer',
    productKey: 'soil-topsoil',
    buyReason: 'Screened mineral soil for filling low spots and leveling before seeding.',
  },
  intro: (
    <>
      <p>
        Peat moss and topsoil get thrown around as if they are interchangeable bags of dirt, but
        they do almost opposite jobs. Peat moss is an organic amendment: partially decomposed
        sphagnum moss that holds many times its weight in water, lightens heavy clay, and gives
        seed a moist bed to germinate in. It adds almost no nutrients and is strongly acidic.
        Topsoil is screened mineral soil, the sand, silt, and clay you use to add volume, fill low
        spots, and build grade. It has body and stays put; peat moss decomposes and compresses.
      </p>
      <p>
        The right choice is entirely about the job. If you are amending poor soil or topdressing a
        fresh seeding to hold moisture, peat moss (or compost) is the tool. If you are leveling
        ruts, filling holes, or raising a low area, topsoil is the tool. Reaching for the wrong one
        is a common and frustrating mistake: peat moss used to level a lawn will sink as it breaks
        down, and topsoil spread thin over new seed often cakes and crusts. For routine
        topdressing of an established lawn, many turf pros skip both in favor of screened compost.
      </p>
    </>
  ),
  verdict:
    'Peat moss wins for amending soil and topdressing new seed to hold moisture. Topsoil wins for leveling, filling, and adding volume. They solve different problems and are not substitutes.',
  table: [
    { label: 'Primary use', a: 'Soil amendment + seed topdressing', b: 'Leveling, filling, building grade' },
    { label: 'Material', a: 'Partially decomposed sphagnum moss (organic)', b: 'Screened mineral soil (sand / silt / clay)' },
    { label: 'pH', a: 'Very acidic (3.5 to 4.5)', b: 'Variable (often near neutral)' },
    { label: 'Nutrient content', a: 'Very low', b: 'Low to moderate (varies by source)' },
    { label: 'Water retention', a: 'Very high (holds 10 to 20x its weight)', b: 'Moderate' },
    { label: 'Weight', a: 'Light', b: 'Heavy' },
    { label: 'Good for leveling low spots', a: 'No (decomposes and compresses)', b: 'Yes' },
    { label: 'Good for amending clay or sand', a: 'Yes (improves both)', b: 'Limited' },
    { label: 'Good for starting seed', a: 'Yes (thin layer over seed)', b: 'Fair (quality dependent)' },
    { label: 'Risk when fully dry', a: 'Can turn hydrophobic and shed water', b: 'Low (can crust or shed water if very fine or compacted)' },
    { label: 'Sustainability', a: 'Concern (slow-to-renew peat bogs)', b: 'Lower concern' },
    { label: 'Cost per cubic foot', a: 'Higher', b: 'Lower' },
    { label: 'Best for', a: 'Amending soil and starting seed', b: 'Leveling and filling' },
  ],
  pickA: {
    headline: 'Pick peat moss if...',
    reasons: [
      'You are amending sandy or heavy clay soil to improve moisture retention and structure.',
      'You are topdressing a newly seeded area with a thin layer to hold moisture during germination.',
      'You are establishing acid-loving plants or genuinely need to lower soil pH.',
      'You want a lightweight amendment that is easy to spread and rake in.',
      'You are mixing a seed-starting or raised-bed blend rather than filling volume.',
    ],
  },
  pickB: {
    headline: 'Pick topsoil if...',
    reasons: [
      'You need to level low spots, fill ruts or holes, or build up grade.',
      'You are establishing a new lawn area and need a volume of soil to work with.',
      'You want a budget-friendly bulk material for filling.',
      'You are building new garden beds and need body, not just amendment.',
      'You need a material that stays in place rather than decomposing and sinking.',
    ],
  },
  faqs: [
    {
      question: 'Should I use peat moss or topsoil for overseeding?',
      answer:
        'For topdressing seed, a thin layer of peat moss holds moisture far better than topsoil and gives faster, more even germination. Poor or unscreened topsoil tends to cake and crust over seed; screened topsoil can work in a thin layer, but compost is usually better. The best topdressing for overseeding is usually screened compost, which holds moisture like peat moss but also adds nutrients and beneficial biology.',
    },
    {
      question: 'Can you level a lawn with peat moss?',
      answer:
        'No, peat moss is a poor leveling material. It decomposes and compresses over a season or two, so any low spot you fill with peat moss will sink and reappear. For leveling, use topsoil, a sand and soil mix, or a sand and compost blend that holds its volume. Peat moss belongs mixed into the soil as an amendment, not piled on top to change grade.',
    },
    {
      question: 'Is peat moss or topsoil better for grass seed?',
      answer:
        'Peat moss is the better seed cover because it keeps the seedbed consistently moist, which is the single biggest factor in germination. Topsoil only works well if it is high quality and screened fine, and even then it dries and crusts faster than peat moss. Compost performs as well as peat moss and adds nutrients, so it is often the top pick of the three for starting seed.',
    },
    {
      question: 'What is the difference between peat moss and topsoil?',
      answer:
        'Peat moss is an organic soil amendment: acidic, lightweight, nutrient-poor, and prized for holding water and improving soil structure. Topsoil is mineral soil (sand, silt, and clay) used to add volume, fill, and level. Peat moss changes how soil behaves; topsoil changes how much soil you have. They are complementary, not interchangeable.',
    },
    {
      question: 'Is peat moss bad for the environment?',
      answer:
        'Peat moss is harvested from bogs that regenerate very slowly, so it is considered a non-renewable resource and its harvest releases stored carbon. Many gardeners now prefer coconut coir or compost as renewable alternatives that do similar jobs. If sustainability matters to you, compost is the best all-around substitute for both peat moss (as an amendment) and topsoil (as a topdressing).',
    },
    {
      question: 'Do you need to mix peat moss into the soil?',
      answer:
        'For amending, yes; till peat moss 2 to 3 inches into the top layer of soil so it improves structure throughout the root zone rather than forming a water-shedding crust on top. The exception is topdressing new seed, where you apply just a thin quarter-inch layer over the seed to hold moisture. Never leave a thick layer of dry peat moss sitting on the surface, because it can repel water once it dries out.',
    },
  ],
}

const annualVsPerennialRyegrass: GrassComparisonEntry = {
  slug: 'annual-ryegrass-vs-perennial-ryegrass',
  kind: 'grass',
  shortLabel: 'Annual vs Perennial Ryegrass',
  h1: 'Annual Ryegrass vs Perennial Ryegrass: One Winter or a Permanent Lawn?',
  metaTitle: 'Annual vs Perennial Ryegrass: Which Should You Plant?',
  metaDescription:
    'Annual ryegrass is a cheap one-winter overseed that dies in summer; perennial ryegrass is permanent turf. Compare cost, lifespan, and looks to pick right.',
  lastModified: '2026-07-11',
  a: {
    key: 'Annual Ryegrass',
    guideSlug: 'annual-ryegrass',
    displayName: 'Annual Ryegrass',
  },
  b: {
    key: 'Perennial Ryegrass',
    guideSlug: 'perennial-ryegrass',
    displayName: 'Perennial Ryegrass',
  },
  intro: (
    <>
      <p>
        Annual ryegrass and perennial ryegrass share a name, sit side by side on the seed shelf,
        and could not have more different jobs. Annual ryegrass is a one-season rental: the
        cheapest seed in the store, up and green in under a week, and dead by early summer. That
        is not a flaw, it is the product. Southern homeowners throw it over dormant bermuda or
        zoysia in fall to keep the lawn green all winter, then let the heat clear it out right as
        the warm-season grass wakes up.
      </p>
      <p>
        Perennial ryegrass is real, permanent turf: fine-bladed, glossy dark green, wear-tough
        enough that sports fields are built on it, and it comes back every year in Zones 4 to 7.
        It costs more per pound and expects actual care, because it is a lawn, not a cover crop.
        The mix-up matters in both directions. Plant annual rye thinking it is permanent and you
        will reseed a dead lawn in June. Overseed a southern lawn with perennial rye and it can
        linger into spring, competing with your bermuda exactly when it needs the sun.
      </p>
    </>
  ),
  verdict:
    'Annual ryegrass wins for cheap winter color over a dormant southern lawn that must die off cleanly in spring. Perennial ryegrass wins any time you want the grass to still be there next year.',
  table: [
    { label: 'Season type', a: 'Cool-season annual (one season)', b: 'Cool-season perennial (permanent)' },
    { label: 'Climate zone (USDA)', a: 'Winter overseed in Zones 7-10', b: '4-7 (permanent lawns)' },
    { label: 'Lifespan', a: 'One fall-to-spring season, dies in summer heat', b: 'Comes back year after year' },
    { label: 'Sun requirement', a: '4+ hours (winter sun is weaker)', b: '6+ hours full sun' },
    { label: 'Shade tolerance', a: 'Medium (as winter cover)', b: 'Low to medium' },
    { label: 'Traffic tolerance', a: 'Medium (thin winter stand)', b: 'High (sports-field standard)' },
    { label: 'Drought tolerance', a: 'Low (irrelevant in winter)', b: 'Medium' },
    { label: 'Heat tolerance', a: 'None (dies above ~85 sustained)', b: 'Low to medium (struggles past Zone 7)' },
    { label: 'Cold tolerance', a: 'Good for a winter annual', b: 'High (survives Zone 4 winters)' },
    { label: 'Blade appearance', a: 'Coarser, lighter green', b: 'Fine, glossy dark green' },
    { label: 'Germination speed', a: '5 to 10 days', b: '5 to 10 days' },
    { label: 'Mowing height', a: '1.5 to 2.5 inches (over dormant turf)', b: '2 to 3 inches' },
    { label: 'Annual nitrogen need', a: '1 to 2 lbs / 1,000 sq ft (fall/winter)', b: '2 to 4 lbs / 1,000 sq ft' },
    { label: 'Spreading habit', a: 'Bunch grass (does not spread)', b: 'Bunch grass (does not spread)' },
    { label: 'Seed cost', a: 'Cheapest lawn seed sold (~$1-2/lb)', b: '2 to 3 times annual rye per pound' },
    { label: 'Spring transition', a: 'Dies off cleanly as heat arrives', b: 'Can linger and compete with warm-season turf' },
    { label: 'Maintenance level', a: 'Low (mow and light feeding)', b: 'Medium' },
  ],
  pickA: {
    headline: 'Pick annual ryegrass if...',
    reasons: [
      'You have a bermuda, zoysia, or centipede lawn that goes brown in winter and you want it green from November to April for about the cost of a bag of fertilizer.',
      'You want the overseed gone in spring so it does not fight your warm-season grass as it wakes up.',
      'You need fast temporary cover on bare soil, like erosion control on a new build lot.',
      'You are seeding into cool fall soil and need germination within a week.',
      'You would rather reseed for $20 each fall than maintain a second permanent grass.',
    ],
  },
  pickB: {
    headline: 'Pick perennial ryegrass if...',
    reasons: [
      'You live in Zones 4 to 7 and want a permanent lawn, not a seasonal cover.',
      'You want the fine, glossy, dark green look that annual rye never delivers.',
      'Your lawn takes real traffic from kids, dogs, or games and needs sports-field toughness.',
      'You are overseeding a thin northern lawn and want the new grass to still be there next summer.',
      'You are blending with Kentucky bluegrass or fescue for a premium cool-season mix.',
    ],
  },
  faqs: [
    {
      question: 'What is the difference between annual and perennial ryegrass?',
      answer:
        'Lifespan is the core difference. Annual ryegrass (Lolium multiflorum) completes its life cycle in one season: it germinates in fall, stays green through winter, and dies when summer heat arrives. Perennial ryegrass (Lolium perenne) is permanent turf that comes back every year in Zones 4 to 7. Annual rye is also coarser and lighter green, while perennial rye is fine-bladed, glossy, and dark. They are different species, not the same grass at different ages.',
    },
    {
      question: 'Will annual ryegrass come back every year?',
      answer:
        'No. Annual ryegrass dies when sustained summer heat arrives and does not regrow from the roots. A small fraction of plants may drop seed that sprouts the following fall, but it is thin and patchy, never a lawn. If you want winter color every year over a dormant southern lawn, plan to overseed with fresh annual rye each fall. It is cheap enough that this is the normal routine, not a workaround.',
    },
    {
      question: 'Which is better for overseeding a bermuda lawn in winter?',
      answer:
        'Annual ryegrass is the better winter overseed for bermuda for most homeowners. It is a third the price of perennial rye, germinates in about a week, and most importantly it dies off cleanly in late spring right as bermuda breaks dormancy. Perennial rye makes a prettier winter lawn and golf courses use it for exactly that reason, but in a home lawn it can survive into early summer and compete with bermuda for sun during green-up, which weakens the permanent grass.',
    },
    {
      question: 'Can you plant annual ryegrass as a permanent lawn?',
      answer:
        'No. Annual ryegrass cannot be a permanent lawn anywhere in the US. In the South it dies in summer heat; in the North it winter-kills or dies the following summer. Anyone selling "quick lawn" seed that is mostly annual ryegrass is selling one season of green. If you need a permanent cool-season lawn, use perennial ryegrass, tall fescue, Kentucky bluegrass, or a blend of them. Annual rye is only the right answer for temporary cover.',
    },
    {
      question: 'How fast does annual ryegrass germinate?',
      answer:
        'Annual ryegrass germinates in 5 to 10 days when soil temperatures are between 50 and 65 degrees, and it is often visible in under a week in warm fall soil. That is as fast as lawn grass gets, which is why it doubles as erosion-control cover on bare soil. Perennial ryegrass is nearly as quick at 5 to 10 days. Both are dramatically faster than Kentucky bluegrass, which can take 2 to 4 weeks.',
    },
    {
      question: 'How much annual ryegrass seed do I need for overseeding?',
      answer:
        'Overseed a dormant warm-season lawn at 5 to 10 pounds of annual ryegrass per 1,000 square feet. Use the low end for a light green tint and the high end for a dense winter lawn. A 25 pound bag covers roughly 2,500 to 5,000 square feet. Seed in fall once soil temperatures drop below about 70 degrees, water lightly every day for the first week, and you will have green cover within two weeks.',
    },
    {
      question: 'How do I get rid of annual ryegrass in spring?',
      answer:
        'Usually you do not have to do anything: sustained temperatures in the mid 80s kill annual ryegrass on their own. To speed the transition and give your bermuda or zoysia a cleaner start, drop your mowing height in late spring and reduce watering, which stresses the rye out faster. Avoid spraying broad herbicides during green-up. The one mistake to avoid is fertilizing heavily in late spring, which pushes the rye to hang on longer.',
    },
    {
      question: 'Is annual or perennial ryegrass cheaper?',
      answer:
        'Annual ryegrass is the cheapest lawn seed sold, typically 1 to 2 dollars per pound, and often less in 50 pound farm-store bags. Perennial ryegrass runs roughly 2 to 3 times that. For a one-winter overseed the cost difference is the whole point: covering 5,000 square feet with annual rye costs about the price of a pizza. If the grass needs to survive past June, though, cheap seed that dies is the expensive option, and perennial rye earns its price.',
    },
  ],
}

const blendVsKentuckyBluegrass: GrassComparisonEntry = {
  slug: 'mixed-cool-season-blend-vs-kentucky-bluegrass',
  kind: 'grass',
  shortLabel: 'Cool-Season Mix vs Kentucky Bluegrass',
  h1: 'Mixed Cool-Season Blend vs Kentucky Bluegrass: Mix or Single Species?',
  metaTitle: 'Cool-Season Mix vs Kentucky Bluegrass: Which to Seed?',
  metaDescription:
    'A cool-season mix is forgiving and establishes fast; pure Kentucky bluegrass is the uniform showpiece. Compare looks, risk, and upkeep to pick your seed.',
  lastModified: '2026-07-11',
  a: {
    key: 'Mixed Cool-Season Blend',
    guideSlug: 'mixed-cool-season-blend',
    displayName: 'Mixed Cool-Season Blend',
  },
  b: {
    key: 'Kentucky Bluegrass',
    guideSlug: 'kentucky-bluegrass',
    displayName: 'Kentucky Bluegrass',
  },
  intro: (
    <>
      <p>
        Walk the seed aisle in any northern state and this is the actual choice on the shelf: a
        bag labeled sun and shade mix, which is Kentucky bluegrass plus perennial ryegrass plus
        fescue in one blend, or a bag of straight Kentucky bluegrass. The mix is how most
        northern lawns get planted, and for good reason. The ryegrass is up in a week and holds
        the soil, the fescues carry the shady and dry corners, and the bluegrass slowly knits the
        whole thing together with its underground rhizomes.
      </p>
      <p>
        Pure bluegrass is the specialist play. Seeded alone it takes two to four weeks just to
        germinate and a full season to fill in, and it wants more water, more fertilizer, and
        more sun than a mix. The payoff is the thing no blend can match: a single-species stand
        with perfectly uniform color and texture, the deep blue-green carpet lawns are judged
        against. The real question is whether you are planting a lawn to live on or a lawn to
        show off. Most people want the first and assume they want the second.
      </p>
    </>
  ),
  verdict:
    'The mixed blend wins for most northern lawns: faster establishment, built-in insurance across sun, shade, and drought, and less demanded of you. Pure Kentucky bluegrass wins when uniform showpiece appearance is the goal and you will pay for it in water, fertilizer, and patience.',
  table: [
    { label: 'Climate zone (USDA)', a: '3-7 (cool-season)', b: '3-7 (cool-season)' },
    { label: 'What is in the bag', a: 'KBG + perennial rye + fine or tall fescue', b: 'Single species (often 2-3 KBG cultivars)' },
    { label: 'Sun requirement', a: 'Handles full sun to part shade in one bag', b: '6+ hours full sun' },
    { label: 'Shade tolerance', a: 'Medium to high (fescue fraction carries shade)', b: 'Low' },
    { label: 'Traffic tolerance', a: 'High (rye toughness + KBG self-repair)', b: 'High (self-repairs)' },
    { label: 'Drought tolerance', a: 'Medium (fescues buffer dry spells)', b: 'Medium (browns first in drought)' },
    { label: 'Cold tolerance', a: 'Very high', b: 'Very high (winter survival in Zone 3)' },
    { label: 'Uniformity of look', a: 'Good, but texture and color vary subtly', b: 'Excellent (the single-species showpiece)' },
    { label: 'Germination speed', a: '5 to 10 days (ryegrass fraction first)', b: '14 to 28 days' },
    { label: 'Establishment time', a: 'Usable lawn in 3 to 4 weeks', b: 'A full season to a dense stand' },
    { label: 'Mowing height', a: '2.5 to 3.5 inches', b: '2.5 to 3.5 inches' },
    { label: 'Annual nitrogen need', a: '2 to 4 lbs / 1,000 sq ft', b: '3 to 5 lbs / 1,000 sq ft' },
    { label: 'Water need (peak)', a: '1 to 1.5 inches / week', b: '1.5 inches / week' },
    { label: 'Spreading habit', a: 'KBG fraction spreads; rye and fescue bunch', b: 'Self-repairs through rhizomes' },
    { label: 'Seeding risk', a: 'Low (something in the bag suits every spot)', b: 'Higher (slow start, one species, one bet)' },
    { label: 'Maintenance level', a: 'Medium', b: 'High' },
  ],
  pickA: {
    headline: 'Pick the mixed blend if...',
    reasons: [
      'Your lawn has a bit of everything: sunny stretches, tree shade, a dry slope. One bag covers all of it.',
      'You want visible grass within two weeks and a usable lawn within a month, not a season-long dirt patch.',
      'You are a first-time seeder and want the forgiving option that still looks great.',
      'You want lower water and fertilizer bills than a pure bluegrass lawn demands.',
      'Your lawn is for kids, dogs, and cookouts more than for admiring from the curb.',
    ],
  },
  pickB: {
    headline: 'Pick pure Kentucky bluegrass if...',
    reasons: [
      'You want the single most uniform, postcard-perfect cool-season lawn possible and will trade effort for it.',
      'Your whole lawn gets 6+ hours of direct sun with little or no shade.',
      'You can commit to regular irrigation and 4 or more fertilizer applications a year.',
      'You value self-repair: a pure KBG stand fills its own bare spots through rhizomes.',
      'You are patient enough for a slow first season in exchange for a decade of dense turf.',
    ],
  },
  faqs: [
    {
      question: 'Is a grass seed mix better than pure Kentucky bluegrass?',
      answer:
        'For most northern lawns, yes. A mixed cool-season blend spreads risk: the perennial ryegrass establishes fast and takes traffic, the fescues handle shade and drier soil, and the Kentucky bluegrass fraction slowly adds density and self-repair. Pure bluegrass beats the mix on one axis only, perfectly uniform appearance, and it costs more water, more fertilizer, more sun, and a much slower start to get it. Choose the mix for a living lawn, pure KBG for a showpiece.',
    },
    {
      question: 'What is actually in a sun and shade grass seed mix?',
      answer:
        'A typical northern sun and shade mix is 30 to 50 percent fine fescue (the shade worker), 20 to 40 percent perennial ryegrass (the fast establisher), and 10 to 30 percent Kentucky bluegrass (the spreader that knits the stand together). Ratios vary by brand and price. Check the seed label on the back of the bag: it lists exact percentages by weight, plus germination rates and weed seed content, and it is the only honest part of the packaging.',
    },
    {
      question: 'Will a mixed lawn look patchy compared to pure bluegrass?',
      answer:
        'A quality mix looks uniform from the curb but shows subtle texture and shade variation up close, because ryegrass, fescue, and bluegrass have different blade widths and greens. Whether that bothers you is the real test of which seed to buy. Over the years the mix also self-sorts: bluegrass thickens in the sunny areas while fescue takes over the shade, which reads as a healthy lawn, not a patchy one. Only a single-species stand delivers true one-texture uniformity.',
    },
    {
      question: 'How much faster does a mix establish than Kentucky bluegrass?',
      answer:
        'Dramatically faster. The perennial ryegrass in a mix germinates in 5 to 10 days and is mowable in about three weeks, while pure Kentucky bluegrass takes 14 to 28 days just to germinate and often a full season to form a dense stand. That speed is not only cosmetic: the fast rye canopy shades out weeds and holds soil against rain while the slower bluegrass develops underneath it. A pure KBG seeding spends weeks as open dirt inviting crabgrass.',
    },
    {
      question: 'Can I overseed my mixed lawn with Kentucky bluegrass to make it more uniform?',
      answer:
        'You can push a mixed lawn toward bluegrass over several seasons by overseeding with KBG each fall, feeding at the higher rate bluegrass likes, and keeping the lawn well watered, since those conditions favor bluegrass over the fescues. But you will never fully convert it: the existing rye and fescue persist wherever conditions favor them, especially shade. If you truly want a pure bluegrass look, a renovation (kill, then reseed) is the honest path.',
    },
    {
      question: 'Does a cool-season mix need less water than Kentucky bluegrass?',
      answer:
        'Yes, moderately less. A mix gets by on about 1 to 1.5 inches of water per week in summer versus a steady 1.5 inches for pure bluegrass, because the fescue fraction has deeper roots and better drought tolerance. Just as important, a mix degrades more gracefully when you underwater: the fescues stay green after the bluegrass has gone brown and dormant. A pure KBG lawn is all-or-nothing, which is why it browns first on unirrigated lawns in July.',
    },
    {
      question: 'Why is Kentucky bluegrass seed more expensive than a mix?',
      answer:
        'Bluegrass seed is slower and costlier to produce, and pure KBG bags often carry multiple named cultivars bred for color and disease resistance. Mixes dilute that cost with cheaper ryegrass and fescue seed. Per pound the difference looks small, but bluegrass seed is also much finer, so a pound goes further; compare price per 1,000 square feet of coverage on the label, not per pound. Establishment cost tells the same story: the mix needs less water to get going.',
    },
    {
      question: 'Which should I use to repair bare spots, a mix or straight bluegrass?',
      answer:
        'Use a mix for repairs in almost every case, even on a mostly bluegrass lawn. Bare spots need fast cover before weeds claim them, and the ryegrass in a mix germinates in a week while bluegrass seed sits for three. If the surrounding lawn is bluegrass, the KBG in the repaired patch plus the rhizomes creeping in from the edges will blend it in within a season or two. The exception is a showpiece pure-KBG lawn where texture match matters more than speed.',
    },
  ],
}

// ---------------------------------------------------------------------------
// Topic comparisons (kind: 'topic').
//
// Photo records are copied out of data/grass-reference-photos.ts and
// data/weeds/index.ts rather than imported, because those files key on guide
// slug / weed slug and a topic side is neither. Copying keeps each entry
// self-describing so ComparePhotoCredits can attribute it with no lookup.
// Keep the credit/license/sourceUrl fields in sync if a source photo is
// re-sourced there.
// ---------------------------------------------------------------------------

const CRABGRASS_PHOTO: ReferencePhoto = {
  src: '/examples/weeds/crabgrass-hero.jpg',
  caption: 'Crabgrass sprawling at a pavement edge',
  credit: 'earl woolsey, cropped',
  license: 'CC BY 4.0',
  sourceUrl: 'https://www.inaturalist.org/observations/51704992',
}

const POA_ANNUA_PHOTO: ReferencePhoto = {
  src: '/examples/weeds/annual-bluegrass-poa-annua-hero.jpg',
  caption: 'Annual bluegrass seeding below mowing height',
  credit: null,
  license: 'CC0',
  sourceUrl: 'https://www.inaturalist.org/observations/30809648',
}

const NUTSEDGE_PHOTO: ReferencePhoto = {
  src: '/examples/weeds/nutsedge-yellow-hero.jpg',
  caption: 'Yellow nutsedge with its golden seed head',
  credit: 'Stephen Thorpe, cropped',
  license: 'CC BY 4.0',
  sourceUrl: 'https://www.inaturalist.org/observations/73343857',
}

const WHITE_CLOVER_PHOTO: ReferencePhoto = {
  src: '/examples/weeds/white-clover-hero.jpg',
  caption: 'White clover with its pom-pom bloom in turf',
  credit: 'zebedeugalinha, cropped',
  license: 'CC BY 4.0',
  sourceUrl: 'https://www.inaturalist.org/observations/353381818',
}

const BERMUDAGRASS_PHOTO: ReferencePhoto = {
  src: '/examples/bermudagrass-example.jpg',
  caption: 'Established bermudagrass turf',
  credit: 'V C Balakrishnan, cropped',
  license: 'CC BY-SA 4.0',
  sourceUrl:
    'https://commons.wikimedia.org/wiki/File:Cynodon_dactylon_(1).JPG_-_Photo_by_V_C_Balakrishnan.jpg',
}

const KENTUCKY_BLUEGRASS_PHOTO: ReferencePhoto = {
  src: '/examples/kentucky-bluegrass-example.jpg',
  caption: 'Established Kentucky bluegrass lawn',
  credit: 'Pilot138-17, cropped',
  license: 'CC BY-SA 4.0',
  sourceUrl: 'https://commons.wikimedia.org/wiki/File:Kentucky_Blue_Grass.jpg',
}

const TALL_FESCUE_CLUMP_PHOTO: ReferencePhoto = {
  src: '/examples/tall-fescue-habit.jpg',
  caption: 'Tall fescue growth habit, an upright clump',
  credit: null,
  license: 'CC0',
  sourceUrl: 'https://www.inaturalist.org/observations/46413391',
}

const TALL_FESCUE_TURF_PHOTO: ReferencePhoto = {
  src: '/examples/tall-fescue-example.jpg',
  caption: 'Coarse, ribbed tall fescue blades',
  credit: 'wild_wind, cropped',
  license: 'CC BY 4.0',
  sourceUrl: 'https://www.inaturalist.org/observations/63524956',
}

// Sides below have no reference photo or weed hero to borrow: three of them are
// lawn CONDITIONS rather than species, and the other two are organisms with no
// page of their own yet. They are sourced and cropped by
// scripts/fetch-compare-side-photos.ts into /examples/compare/. `caption` is the
// alt text on both the compare landing card and the versus panel, so it names
// the diagnostic feature rather than repeating the side label.
//
// Because the script crops and downscales every file, these are adapted works.
// CC BY and CC BY-SA both require the adaptation to be indicated, so the note
// travels inside `credit`, which is the field ComparePhotoCredits renders. CC0
// files carry no such condition and take no note.

const DALLISGRASS_PHOTO: ReferencePhoto = {
  src: '/examples/compare/dallisgrass-clump.jpg',
  caption: 'Dallisgrass clump, wide coarse blades radiating from one crown in turf',
  credit: 'Vince Hale',
  license: 'CC0',
  sourceUrl: 'https://www.inaturalist.org/observations/230352931',
}

const OXALIS_PHOTO: ReferencePhoto = {
  src: '/examples/compare/oxalis-foliage.jpg',
  caption: 'Oxalis with heart-shaped notched leaflets and a five-petal yellow flower',
  credit: 'botany08, cropped',
  license: 'CC BY 4.0',
  sourceUrl: 'https://www.inaturalist.org/observations/171847140',
}

// Paired on purpose: the dormant frame browns evenly across the whole lawn with
// the canopy intact, the dead frame is patchy inside green turf. Re-source them
// together or the comparison stops teaching anything.
const DORMANT_LAWN_PHOTO: ReferencePhoto = {
  src: '/examples/compare/dormant-lawn.jpg',
  caption: 'Dormant lawn browned evenly edge to edge with the grass canopy still intact',
  credit: 'Famartin, cropped',
  license: 'CC BY-SA 4.0',
  sourceUrl:
    'https://commons.wikimedia.org/wiki/File:2022-07-26_10_33_24_Brown,_dry_lawn_after_an_extended_period_of_hot_and_dry_weather_along_Seven_Oaks_Lane_in_the_Mountainview_section_of_Ewing_Township,_Mercer_County,_New_Jersey.jpg',
}

const DEAD_TURF_PHOTO: ReferencePhoto = {
  src: '/examples/compare/dead-turf-patch.jpg',
  caption: 'Straw-colored dead patches with hard edges sitting inside otherwise green turf',
  credit: 'Scot Nelson',
  license: 'CC0',
  sourceUrl: 'https://commons.wikimedia.org/wiki/File:Brown_Patch.jpg',
}

const ARTIFICIAL_TURF_PHOTO: ReferencePhoto = {
  src: '/examples/compare/artificial-turf-yard.jpg',
  caption: 'Installed artificial turf yard, one flat uniform green with a hard paver edge',
  credit: 'Sarah Stierch, cropped',
  license: 'CC BY 4.0',
  sourceUrl:
    'https://commons.wikimedia.org/wiki/File:Artificial_turf_yard_in_Sonoma,_California_-_Sarah_Stierch.jpg',
}

const dormantVsDeadGrass: TopicComparisonEntry = {
  slug: 'dormant-vs-dead-grass',
  kind: 'topic',
  topicGroup: 'diagnostic',
  shortLabel: 'Dormant vs Dead Grass',
  h1: 'Dormant vs Dead Grass: How to Tell the Difference',
  metaTitle: 'Dormant vs Dead Grass: How to Tell the Difference',
  metaDescription:
    'Brown lawn? Dormant grass is alive and waiting; dead grass will not come back. Use the tug test, crown color, and patch shape to tell which one you have.',
  lastModified: '2026-08-18',
  authorSlug: 'sarah-mitchell',
  directAnswer:
    'Tug a handful. Dormant grass resists and the crowns stay firm, so it greens back up within about two weeks of steady water. Dead grass lifts out with no resistance and leaves sharp-edged patches, and it only comes back if you reseed or lay sod.',
  a: {
    displayName: 'Dormant Grass',
    sub: 'Alive, waiting out heat, cold, or drought',
    photo: DORMANT_LAWN_PHOTO,
    link: { href: '/diagnose', label: 'Diagnose my brown patch', icon: 'diagnose' },
  },
  b: {
    displayName: 'Dead Grass',
    sub: 'Crown and roots gone, will not recover',
    photo: DEAD_TURF_PHOTO,
    link: { href: '/tools/overseeding-calculator', label: 'Plan the reseed', icon: 'calculator' },
  },
  toolCta: {
    href: '/tools/watering-schedule',
    label: 'Build my watering schedule',
    blurb:
      'Get a deep, infrequent watering plan for your grass type and ZIP so a dormant lawn wakes up instead of dying.',
  },
  intro: (
    <>
      <p>
        Dormant grass is alive and brown on purpose, while dead grass is gone and will not green up
        again no matter how much you water it. The fastest way to tell them apart is the tug test:
        grab a handful of the brown turf and pull firmly. Dormant grass resists, and the blades tear
        before the plant leaves the soil. Dead turf lifts out in your hand like a piece of old
        carpet, because the roots that anchored it are gone.
      </p>
      <p>
        The second clue is shape. Dormancy is a whole-lawn response to weather, so it browns evenly
        across every area that gets the same sun and water. Death is local, so it shows up as
        patches, rings, streaks, or the outline of whatever caused it. A lawn that goes uniformly
        straw-colored in a July heat wave or after the first hard frost is behaving normally. A lawn
        with sharp-edged brown spots in the middle of green turf has a problem worth diagnosing, and
        the cause matters because grubs, disease, and a fertilizer spill all look similar from
        standing height and need completely different fixes.
      </p>
    </>
  ),
  verdict:
    'Dormant grass passes the tug test, browns evenly across the whole lawn, and greens back up within about two weeks of steady water. Dead grass pulls out with no resistance, shows sharp patch edges, and only comes back if you reseed or lay sod.',
  table: [
    {
      label: 'Tug test',
      a: 'Crowns hold. Blades tear before the plant lifts out.',
      b: 'The patch lifts out in one handful with no resistance.',
    },
    {
      label: 'Color pattern',
      a: 'Even straw or tan across everything with the same sun and water',
      b: 'Irregular patches, rings, or streaks sitting inside green turf',
    },
    {
      label: 'Crown at the base of a plant',
      a: 'Pull a plant apart and the crown is firm, pale green or white',
      b: 'Crown is brown and brittle, and crumbles between your fingers',
    },
    {
      label: 'Edges of the brown area',
      a: 'Fades gradually into the greener parts of the lawn',
      b: 'Sharp boundary, often an outline you can trace with a finger',
    },
    {
      label: 'Roots',
      a: 'White roots still anchored in the soil',
      b: 'Roots gone, rotted, or chewed off just below the surface',
    },
    {
      label: 'Timing',
      a: 'Predictable. Peak summer heat, a long dry spell, or the first hard frost.',
      b: 'Fast and out of season. Often visible within a week or two.',
    },
    {
      label: 'Response to water',
      a: 'Greens up within about two weeks of steady deep watering',
      b: 'No change at all, however long you water it',
    },
    {
      label: 'How much of the lawn',
      a: 'The whole lawn, or every sunny area at once',
      b: 'Spots and shapes: a ring, a dog track, a mower path, one bed edge',
    },
    {
      label: 'Usual cause',
      a: 'Normal seasonal triggers: heat, cold, or drought',
      b: 'Grubs, disease, a chemical or fuel spill, urine, scalping, or drought that ran too long',
    },
    {
      label: 'What to do next',
      a: 'Water deeply if you want it green, or leave it alone and stay off it',
      b: 'Rake out the dead material, then reseed, plug, or sod the bare soil',
    },
    {
      label: 'Verdict',
      a: 'Alive. Time or water fixes it, and nothing you buy speeds it up much.',
      b: 'Gone. Only reseeding, plugs, or sod bring that area back.',
    },
  ],
  pickA: {
    headline: 'It is probably dormant if...',
    reasons: [
      'The whole lawn turned the same shade of straw at roughly the same time.',
      'A firm tug tears the blades but does not lift the plant out of the soil.',
      'It happened during a heat wave, a long dry spell, or right after the first hard frost.',
      'Pulling a plant apart shows a firm, pale crown and white roots still in the ground.',
      'Shaded or irrigated parts of the yard are still green while the exposed parts are not.',
    ],
  },
  pickB: {
    headline: 'It is probably dead if...',
    reasons: [
      'The brown area has a sharp edge, a ring, or a shape you can trace.',
      'The turf lifts out in a sheet or handful with no root resistance at all.',
      'Two weeks of steady watering changed nothing.',
      'The crowns at the base of the plants are brown, dry, and crumble.',
      'You can name the event that caused it: a spill, a dog, a scalped pass, grubs, or disease.',
    ],
  },
  faqs: [
    {
      question: 'Is my grass dead or just dormant?',
      answer:
        'Do the tug test before you do anything else. Grab a handful of the brown turf and pull firmly. If the blades tear but the plant stays anchored, the crown and roots are alive and the lawn is dormant. If the whole patch lifts out with no resistance, that area is dead. Back the tug test up with two more checks: dormancy browns evenly across the whole lawn, while dead areas are patchy with sharp edges, and dormant crowns are pale green and firm at the base while dead crowns are brown and crumbly.',
    },
    {
      question: 'How do you do the tug test on a lawn?',
      answer:
        'Pick a spot in the middle of the brown area, close your hand around a small handful of blades near the soil, and pull straight up with steady pressure. Living dormant turf holds on, and you end up with torn blade tips in your hand. Dead turf comes away as a clump or a mat with soil falling off it. Repeat the test in three or four places, because most lawns are a mix. Then pull one plant apart at the base and look at the crown, the short white or pale-green section right where the blades meet the roots. Firm and pale means alive; brown and papery means gone.',
    },
    {
      question: 'How long can grass stay dormant?',
      answer:
        'Healthy turf tolerates several weeks of dormancy in a normal summer or winter without dying, and cool-season lawns in particular are built to shut down and wait. The risk starts when a drought runs long enough that the crowns dry out. If you want insurance during an extended dry spell, water occasionally rather than either soaking the lawn back into growth or letting it go completely bone dry, and follow the guidance your local extension service publishes for your grass type. Winter dormancy is not a concern at all: warm-season lawns go tan for months every year and green up on schedule.',
    },
    {
      question: 'Will watering bring dormant grass back?',
      answer:
        'Yes, if it is genuinely dormant. Water deeply and infrequently, aiming for about an inch a week including rain, and expect green to show up within one to two weeks rather than overnight. Light daily sprinkles do not work, because they wet the top of the soil and never reach the roots that need to wake up. If two weeks of proper watering produces no change anywhere in the patch, stop watering it and start planning a reseed, because you are watering dead soil.',
    },
    {
      question: 'Does dormant grass still need to be watered?',
      answer:
        'It does not need to be watered to survive a normal dormancy, but it does need you to stay off it. Traffic on dormant turf crushes crowns that have no ability to repair themselves until they green up, and that is how a dormant lawn turns into a dead one. If you want to keep the crowns comfortable through a long drought, an occasional deep soak is better than frequent light watering. If you want the lawn green rather than merely alive, you are back to a full watering schedule.',
    },
    {
      question: 'Can dormant grass turn into dead grass?',
      answer:
        'Yes, and that is the main reason the distinction matters. Dormancy is a survival state with a limit. If the drought outlasts the reserves in the crowns, or if the lawn takes heavy foot traffic, mower passes, or a dog run while it is dormant, those crowns die and that area does not come back. The practical rule is simple: while a lawn is brown and dormant, do not mow it, do not park on it, do not fertilize it, and keep the play and the traffic somewhere else.',
    },
    {
      question: 'How long does it take for reseeded areas to green up?',
      answer:
        'Longer than most people expect. After you rake out the dead material and get seed in contact with soil, you are usually looking at a couple of weeks before you see the first green fuzz and a full season before that patch matches the rest of the lawn in density. Timing matters more than product choice. Cool-season seed wants the late-summer to early-fall window, and warm-season lawns repair from plugs or sod in late spring. Our overseeding calculator gives you both the seed quantity and the window for your ZIP.',
    },
  ],
}

const crabgrassVsBermudagrass: TopicComparisonEntry = {
  slug: 'crabgrass-vs-bermudagrass',
  kind: 'topic',
  topicGroup: 'lookalike',
  shortLabel: 'Crabgrass vs Bermudagrass',
  h1: 'Crabgrass vs Bermudagrass: How to Tell Them Apart',
  metaTitle: 'Crabgrass vs Bermudagrass: How to Tell Them Apart',
  metaDescription:
    'Crabgrass vs bermudagrass, told apart by blade width, growth habit, and the pull test. One is a summer annual weed, the other is a permanent lawn grass.',
  lastModified: '2026-08-18',
  authorSlug: 'sarah-mitchell',
  directAnswer:
    'Pull on the clump. Crabgrass lifts out in one shallow piece and its blades are wide and pale, while bermudagrass will not budge because it runs by stolons above ground and rhizomes below. Crabgrass dies at frost; bermuda goes tan and returns from the same roots.',
  a: {
    displayName: 'Crabgrass',
    sub: 'Summer annual weed that dies at frost',
    photo: CRABGRASS_PHOTO,
    link: { href: '/weeds/crabgrass', label: 'See how to kill crabgrass', icon: 'weed' },
  },
  b: {
    displayName: 'Bermudagrass',
    sub: 'Warm-season lawn grass that returns every year',
    photo: BERMUDAGRASS_PHOTO,
    link: { href: '/guides/bermudagrass', label: 'Read the Bermudagrass guide', icon: 'guide' },
  },
  toolCta: {
    href: '/tools/herbicide-timing',
    label: 'Time my crabgrass pre-emergent',
    blurb:
      'Enter your ZIP and grass type for the pre-emergent and post-emergent windows that actually work in your climate.',
  },
  intro: (
    <>
      <p>
        The fastest way to tell crabgrass from bermudagrass is to pull on it: crabgrass lifts out as
        one shallow clump with the stems radiating from a single point, while bermudagrass refuses to
        budge because it runs sideways above ground and below ground and roots again at every node.
        The blades give it away too. Crabgrass blades are wide, flat, and noticeably lighter green
        than the lawn around them. Bermuda blades are narrow and fine, and a bermuda lawn reads as a
        dense mat rather than a set of separate clumps.
      </p>
      <p>
        This is worth getting right because the two plants live on completely different schedules.
        Crabgrass is a summer annual. Every plant you see grew from seed this spring, and every one
        of them dies at the first hard frost, which means a well-timed spring pre-emergent breaks the
        whole cycle. Bermudagrass is a perennial that comes back from the same roots for years. If
        bermuda is your lawn, that is exactly what you want. If bermuda has crept into a cool-season
        lawn, treating it like crabgrass will waste your money, because there is nothing seasonal
        about it to prevent.
      </p>
    </>
  ),
  verdict:
    'If it pulls out as a single flat clump with wide pale blades, it is crabgrass and spring pre-emergent timing solves it. If it will not pull, spreads by runners, and greens back up from the same spot every year, it is bermudagrass and you are dealing with a permanent grass, not a seasonal weed.',
  table: [
    {
      label: 'Blade width and color',
      a: 'Wide, flat blades, clearly lighter green than the surrounding lawn',
      b: 'Narrow, fine blades in a denser, deeper blue-green mat',
    },
    {
      label: 'Growth habit',
      a: 'Stems radiate from one central point like crab legs, pressed to the ground',
      b: 'A spreading mat with no single center, filling in sideways',
    },
    {
      label: 'Does it pull out?',
      a: 'Yes. Lifts as one shallow rosette with a single root mass.',
      b: 'No. Runners re-root at every node and the plant fights back.',
    },
    {
      label: 'How it spreads',
      a: 'Seed only, starting fresh every spring',
      b: 'Above-ground stolons, below-ground rhizomes, and seed',
    },
    {
      label: 'Seed head',
      a: 'Three to five or more finger-like spikes off the stem tip in late summer',
      b: 'Three to seven finger-like spikes off one point. Both grasses are named for those fingers, so the seed head on its own cannot separate them.',
    },
    {
      label: 'Collar, where the blade meets the stem',
      a: 'A tall, papery, see-through ligule stands up against the stem, and on large crabgrass the lower blade and sheath are noticeably hairy.',
      b: 'A fringe of fine white hairs runs across the collar instead of a papery ligule.',
    },
    {
      label: 'Where it shows up first',
      a: 'Driveway and sidewalk edges, and thin sunny spots where soil warms fastest',
      b: 'Everywhere at once if it is your lawn, or creeping in from a border if it is not',
    },
    {
      label: 'Season',
      a: 'Germinates in spring, peaks in summer, dies at the first hard frost',
      b: 'Greens up in late spring, goes tan in winter, returns from the same roots',
    },
    {
      label: 'What winter does to it',
      a: 'Kills it outright and leaves bare spots that reseed next year',
      b: 'Turns it brown and dormant, not dead',
    },
    {
      label: 'Is it a weed?',
      a: 'Yes, in every lawn',
      b: 'Depends. It is the lawn across most of the South and a weed inside a fescue lawn.',
    },
    {
      label: 'What to do about it',
      a: 'Time a spring pre-emergent, spot-treat young plants, and mow tall to shade the soil',
      b: 'If it is your lawn, feed and mow it. If it is invading cool-season turf, expect a long project.',
    },
    {
      label: 'Verdict',
      a: 'A one-season weed. Prevention timing beats spraying.',
      b: 'A permanent grass. It is either your lawn or a renovation decision.',
    },
  ],
  pickA: {
    headline: 'It is crabgrass if...',
    reasons: [
      'It lifts out of the soil in one piece with a shallow root ball.',
      'The blades are wide, flat, and lighter green than everything around them.',
      'The stems fan out from one central point and lie flat against the ground.',
      'It appeared this spring along the driveway or a thin sunny patch, and it was not there last November.',
      'It disappears completely after the first hard frost and leaves a bare spot behind.',
    ],
  },
  pickB: {
    headline: 'It is bermudagrass if...',
    reasons: [
      'You cannot pull it up, and every piece you tear off has roots at the joints.',
      'The blades are fine and narrow, and the patch is a dense mat rather than a clump.',
      'You can see runners crossing bare soil, sidewalks, or bed edges.',
      'The same area greened back up this spring after going tan all winter.',
      'Bend a blade back where it joins the stem and you find a fringe of fine white hairs rather than a tall papery collar. Do not use the seed head for this, because both plants throw the same finger-like spikes.',
    ],
  },
  faqs: [
    {
      question: 'How do I know if it is crabgrass or bermuda?',
      answer:
        'Pull on it. Crabgrass comes out as a single shallow clump with the stems radiating from one point, and the blades are wide and noticeably lighter than the lawn. Bermudagrass will not pull, because it spreads by above-ground runners and underground rhizomes that root again at every joint, and its blades are much finer. If you are still unsure in the middle of summer, wait for winter: crabgrass dies at the first hard frost and leaves a bare spot, while bermuda simply goes tan and comes back from the same roots in late spring.',
    },
    {
      question: 'Will crabgrass killer hurt my bermuda lawn?',
      answer:
        'It depends entirely on the product, and the label is the only place that answer exists. Some post-emergent crabgrass products are labeled for use on established bermudagrass and some are not, and the same active ingredient can be safe on bermuda and damaging on St. Augustine or centipede. Read the label for your exact grass type before you mix anything, follow the rate printed there, and never assume a product that was fine on a neighbor lawn is fine on yours. Our herbicide timing tool will tell you when to treat; the label tells you what and how much.',
    },
    {
      question: 'Does crabgrass come back every year?',
      answer:
        'The plants do not, but the seed does. Crabgrass is a summer annual, so every plant dies at frost. What survives is the seed bank in your soil, and a single mature plant drops thousands of seeds that stay viable for years. That is why skipping pre-emergent for one spring can undo two good seasons. Two or three consecutive years of well-timed prevention plus a thicker lawn is what actually starves the seed bank down to stragglers.',
    },
    {
      question: 'Why does my bermuda lawn look like crabgrass in summer?',
      answer:
        'Two things usually cause this. First, bermuda that is mowed too high gets stemmy and coarse and starts to look like a weed. Second, real crabgrass is genuinely easy to miss inside a bermuda lawn, because both are warm-season and both peak in July. Look at the color contrast in flat morning light: crabgrass clumps read distinctly paler than the bermuda around them, and they sit slightly proud of the canopy after a mow. Then pull one. Bermuda holds; crabgrass does not.',
    },
    {
      question: 'Can I just pull bermudagrass out like crabgrass?',
      answer:
        'No, and trying usually makes it worse. Bermuda spreads with stolons on the surface and rhizomes underground, and every fragment you leave behind can start a new plant. Pulling a patch typically breaks the runners into pieces and gives you more plants than you started with. Crabgrass is the opposite: hand-pulling genuinely works because the whole plant comes up as one shallow unit, and it is a real option for a handful of clumps before they set seed.',
    },
    {
      question: 'How do I get rid of bermudagrass in a fescue lawn?',
      answer:
        'Honestly, this is one of the hardest problems in home lawn care and there is no quick answer. Bermuda that is established inside cool-season turf spreads underground, so spot-pulling and spot-spraying rarely finish the job, and most homeowners end up choosing between living with it, containing it with a hard edge, or renovating the area outright. Whatever route you take, start by confirming the ID: a lot of what gets called bermuda in a fescue lawn turns out to be crabgrass, which is a far easier fix.',
    },
  ],
}

const poaAnnuaVsKentuckyBluegrass: TopicComparisonEntry = {
  slug: 'poa-annua-vs-kentucky-bluegrass',
  kind: 'topic',
  topicGroup: 'lookalike',
  shortLabel: 'Poa Annua vs Kentucky Bluegrass',
  h1: 'Poa Annua vs Kentucky Bluegrass: Telling the Two Bluegrasses Apart',
  metaTitle: 'Poa Annua vs Kentucky Bluegrass: How to Tell Them Apart',
  metaDescription:
    'Poa annua and Kentucky bluegrass share the same boat-shaped blade tip. Color, seed heads, and what happens in June heat are what actually separate them.',
  lastModified: '2026-08-18',
  authorSlug: 'sarah-mitchell',
  directAnswer:
    'Poa annua is the lighter-green patch that seeds whitish heads below mowing height and browns out in early summer, and its control window is late summer. Kentucky bluegrass is darker, holds color through July, and thickens year over year.',
  a: {
    displayName: 'Annual Bluegrass (Poa Annua)',
    sub: 'Cool-season weed that seeds all winter and dies in June',
    photo: POA_ANNUA_PHOTO,
    link: {
      href: '/weeds/annual-bluegrass-poa-annua',
      label: 'See how to control poa annua',
      icon: 'weed',
    },
  },
  b: {
    displayName: 'Kentucky Bluegrass',
    sub: 'Cool-season lawn grass that lives for years',
    photo: KENTUCKY_BLUEGRASS_PHOTO,
    link: {
      href: '/guides/kentucky-bluegrass',
      label: 'Read the Kentucky Bluegrass guide',
      icon: 'guide',
    },
  },
  toolCta: {
    href: '/tools/herbicide-timing',
    label: 'Time my fall pre-emergent',
    blurb:
      'Poa annua germinates in fall, so the prevention window is late summer. Get the dates for your ZIP.',
  },
  intro: (
    <>
      <p>
        Poa annua and Kentucky bluegrass both have the boat-shaped blade tip that gives bluegrasses
        away, so that classic ID trick will not separate them. What does: poa annua is a lighter,
        lime-green patch sitting inside a darker lawn, it pushes whitish seed heads even below
        mowing height, and it dies abruptly when real summer heat arrives. Kentucky bluegrass is
        darker, deeper-rooted, keeps its color through the summer, and does not seed at three inches
        of mowing height.
      </p>
      <p>
        The calendar is the other tell, and it is the useful one. Poa annua germinates in early fall,
        looks lush and innocent through winter and spring, seeds heavily, then browns out in June and
        leaves the thin patches that finally make owners notice it. Kentucky bluegrass spreads by
        rhizomes and fills those patches back in over years. That timing difference is also why
        control efforts fail so often: people spray poa in April when the plant has already done its
        work, when the window that actually matters is a pre-emergent application in late summer
        before fall germination begins.
      </p>
    </>
  ),
  verdict:
    'If the patch is lighter green than the lawn, seeds whitish heads below mowing height, and browns out in early summer, it is poa annua and your control window is late summer. If it is darker, holds color through July, and thickens over the years, it is Kentucky bluegrass and it is the lawn you want.',
  table: [
    {
      label: 'Blade tip',
      a: 'Boat-shaped, same as Kentucky bluegrass. Not a useful tell on its own.',
      b: 'Boat-shaped. Shared with poa annua and with rough bluegrass.',
    },
    {
      label: 'Color against the lawn',
      a: 'Lighter lime or yellow-green patches that stand out in cool months',
      b: 'Deeper blue-green, and it is the color the patches stand out against',
    },
    {
      label: 'Texture',
      a: 'Softer and finer, often visibly lusher than the turf around it',
      b: 'Medium fine, uniform across the whole stand',
    },
    {
      label: 'Seed heads',
      a: 'Whitish green heads produced nearly year-round, even below mower height',
      b: 'Only if you let it grow well past mowing height, and only in late spring',
    },
    {
      label: 'What happens in June',
      a: 'Annual types brown and die abruptly, leaving thin or bare patches',
      b: 'Holds through summer heat, and may go dormant in drought without dying',
    },
    {
      label: 'Roots',
      a: 'Shallow and fibrous, which is why heat kills it so fast',
      b: 'Deeper, with underground rhizomes that knit the stand together',
    },
    {
      label: 'How it spreads',
      a: 'Seed, in enormous quantities, from a plant that lives about one year',
      b: 'Rhizomes plus seed, filling in bare spots on its own over seasons',
    },
    {
      label: 'When it germinates',
      a: 'Early fall, as soil temperatures fall back through the low 70s',
      b: 'Planted in late summer or early fall, then lives for years',
    },
    {
      label: 'Where it shows up first',
      a: 'Compacted, wet, or heavily trafficked areas, and thin winter turf',
      b: 'Wherever it was seeded or sodded, spreading outward from there',
    },
    {
      label: 'What to do about it',
      a: 'Apply pre-emergent in late summer before fall germination, then thicken the lawn',
      b: 'Feed, mow tall, and let the rhizomes close the gaps poa leaves behind',
    },
    {
      label: 'Verdict',
      a: 'A winter weed with a summer death date. Win it in September.',
      b: 'The lawn grass you are trying to protect. Density is the defense.',
    },
  ],
  pickA: {
    headline: 'It is poa annua if...',
    reasons: [
      'The patches are clearly lighter green than the rest of the lawn in cool months.',
      'You see whitish seed heads on grass that is only an inch or two tall.',
      'The same patches brown out and thin dramatically as soon as summer heat arrives.',
      'It showed up in the wettest, most compacted, or most trafficked parts of the yard.',
      'It is thriving in a warm-season lawn during winter dormancy, when nothing else is growing.',
    ],
  },
  pickB: {
    headline: 'It is Kentucky bluegrass if...',
    reasons: [
      'The color is a deep blue-green that holds through the summer.',
      'It is not seeding at mowing height, and it never has.',
      'Thin spots close on their own over a season or two without you reseeding them.',
      'A plug pulled out of the lawn shows rhizomes running sideways under the surface.',
      'It came out of a bag or a pallet you or a previous owner deliberately put down.',
    ],
  },
  faqs: [
    {
      question: 'Is poa annua the same as Kentucky bluegrass?',
      answer:
        'No, though they are cousins and share the boat-shaped blade tip that makes people mix them up. Kentucky bluegrass is a long-lived perennial lawn grass that spreads by rhizomes and holds a deep blue-green color through the summer. Poa annua is annual bluegrass, a weed that germinates in fall, seeds heavily all winter and spring at any mowing height, and dies out when real heat arrives. The practical test is the calendar: if the patch dies in June, it was poa.',
    },
    {
      question: 'Why does poa annua die in the summer?',
      answer:
        'Because that is the end of its life cycle, not a problem you caused. The common annual biotypes germinate in early fall, grow through the cool months, put everything into seed production in spring, and finish. Their shallow root system means the first stretch of real summer heat and dry soil pushes them over the edge fast. The brown patches you see in June are last September germination reaching its scheduled end. Some perennial biotypes do persist in irrigated lawns, and those patches need spot renovation rather than waiting for heat to clear them.',
    },
    {
      question: 'When should I apply pre-emergent for poa annua?',
      answer:
        'Late summer into early fall, as soil temperatures drop back through roughly 70 degrees, before fall germination starts. This is the single most common mistake in poa control: spring pre-emergent does nothing about a weed that germinated the previous September. Our herbicide timing tool takes your ZIP and grass type and gives you the fall window with a countdown, so you are not guessing from a calendar date that was written for a different climate.',
    },
    {
      question: 'Can I mow poa annua low enough to stop it seeding?',
      answer:
        'No, and this is what makes it so persistent. Poa annua sets viable seed heads below almost any mowing height, including putting-green height, so cutting short never interrupts reproduction. It only makes the seed heads more obvious. Mowing higher is still worth doing, but for a different reason: a taller, denser canopy shades the soil surface and gives fall seed fewer openings to germinate into.',
    },
    {
      question: 'Will poa annua take over my lawn?',
      answer:
        'It can dominate the cool months and then leave you with the damage in summer, which is the worst of both. It moves into compacted, wet, and thin areas first, so pressure tends to track drainage and traffic more than luck. Warm-season lawns get hit hardest because poa exploits winter dormancy, greening up and seeding freely while the bermuda or zoysia around it sleeps. Fall pre-emergent plus summer density work usually turns a speckled lawn into a clean one over two to three consistent years.',
    },
    {
      question: 'How do I tell poa annua from rough bluegrass?',
      answer:
        'Both are lighter green than a Kentucky bluegrass lawn, which is why they get confused. The difference is habit and season. Poa annua grows in small clumps, seeds constantly at mowing height, and dies out in early summer heat. Rough bluegrass spreads by above-ground stolons into wide shiny patches, prefers shade and damp ground, and goes dormant and straw-colored in summer rather than disappearing. If the patch is still there next spring in exactly the same shape, it was not annual poa.',
    },
  ],
}

const nutsedgeVsGrass: TopicComparisonEntry = {
  slug: 'nutsedge-vs-grass',
  kind: 'topic',
  topicGroup: 'lookalike',
  shortLabel: 'Nutsedge vs Lawn Grass',
  h1: 'Nutsedge vs Grass: Why That Fast-Growing Weed Ignores Your Weed Killer',
  metaTitle: 'Nutsedge vs Grass: Why Weed Killer Does Not Work',
  metaDescription:
    'Nutsedge is a sedge, not a grass, so normal lawn herbicides do nothing to it. Roll the stem between your fingers: three edges means sedge, and a different product.',
  lastModified: '2026-08-18',
  authorSlug: 'sarah-mitchell',
  directAnswer:
    'Roll the stem between your fingers. Three edges means nutsedge, which shrugs off normal lawn herbicides and needs a sedge-specific product plus a drainage fix. A round or flat stem means grass, and it grows at the same pace as the rest of the lawn.',
  a: {
    displayName: 'Yellow Nutsedge',
    sub: 'A sedge, which is neither a grass nor a broadleaf',
    photo: NUTSEDGE_PHOTO,
    link: { href: '/weeds/nutsedge-yellow', label: 'See how to kill nutsedge', icon: 'weed' },
  },
  b: {
    displayName: 'Lawn Grass',
    sub: 'Your turf, whichever species it is',
    photo: TALL_FESCUE_TURF_PHOTO,
    link: { href: '/guides', label: 'Find my grass type guide', icon: 'guide' },
  },
  toolCta: {
    href: '/tools/watering-schedule',
    label: 'Fix the watering that feeds it',
    blurb:
      'Nutsedge pressure is a moisture map of your yard. Get a deeper, less frequent schedule for your grass type and ZIP.',
  },
  intro: (
    <>
      <p>
        Nutsedge is not a grass and not a broadleaf, which is exactly why the weed killer in your
        garage did nothing to it. Roll a stem between your fingers: a sedge stem is triangular and
        you can feel three distinct edges, while every lawn grass has a round or flattened stem. That
        one test settles the ID in about three seconds, and it is the difference between buying the
        right product and wasting a season on the wrong one.
      </p>
      <p>
        The other giveaway is speed. Nutsedge grows visibly faster than the turf around it, so a few
        days after you mow there are glossy yellow-green spikes standing above an otherwise flat
        lawn. The leaves come off the stem in threes rather than the two ranks a grass produces, and
        the whole plant has a waxy shine that turf does not. Where you find it matters too: nutsedge
        concentrates in damp, poorly drained, or over-irrigated ground, so a patch is usually telling
        you something about your soil and your sprinklers before it tells you anything about weeds.
      </p>
    </>
  ),
  verdict:
    'If the stem is triangular, the leaves come in threes, and the plant outgrows the lawn within days of mowing, it is nutsedge and it needs a sedge-specific product plus a drainage fix. If the stem is round or flat and it grows at the same pace as everything else, it is grass.',
  table: [
    {
      label: 'Stem cross-section',
      a: 'Triangular. Roll one between your fingers and you feel three edges.',
      b: 'Round or flattened, and it rolls smoothly',
    },
    {
      label: 'Leaf arrangement',
      a: 'Three ranks off the stem, arranged in a spiral of threes',
      b: 'Two ranks, alternating on opposite sides of the stem',
    },
    {
      label: 'Blade color and finish',
      a: 'Glossy, waxy, yellow-green and lighter than the turf',
      b: 'Matte finish, and whatever green your grass type normally is',
    },
    {
      label: 'Growth speed after mowing',
      a: 'Stands above the canopy within a few days of every mow',
      b: 'Grows evenly with the rest of the lawn',
    },
    {
      label: 'Where it shows up',
      a: 'Damp, low, poorly drained, or over-irrigated spots',
      b: 'Wherever it was seeded or sodded',
    },
    {
      label: 'Seed head',
      a: 'Spiky golden umbrella of spikelets if it is left unmowed',
      b: 'Species-specific panicle or spike, and only above mowing height',
    },
    {
      label: 'How it spreads',
      a: 'Underground tubers, called nutlets, that survive winter and hand-pulling',
      b: 'Seed, and in many species stolons or rhizomes',
    },
    {
      label: 'What happens when you pull it',
      a: 'The shoot snaps off and the tuber sends up another one',
      b: 'The plant comes up, or the runners stay and regrow deliberately',
    },
    {
      label: 'Response to normal weed killer',
      a: 'None. Broadleaf and grass herbicides are not built for sedges.',
      b: 'Not applicable. It is the plant you are protecting.',
    },
    {
      label: 'What actually works',
      a: 'A sedge-specific herbicide such as a halosulfuron or sulfentrazone product, at the label rate, on young plants, plus drainage and irrigation fixes',
      b: 'Mow at the right height for the species and water deeply but less often',
    },
    {
      label: 'Verdict',
      a: 'A third category of weed entirely. Right product, plus a drier site.',
      b: 'The lawn. Density and correct watering are what keep sedge out.',
    },
  ],
  pickA: {
    headline: 'It is nutsedge if...',
    reasons: [
      'The stem feels triangular when you roll it between your fingers.',
      'It is standing above the mowed lawn two or three days after you cut.',
      'The blades are glossy and yellow-green rather than matte and turf-colored.',
      'It is concentrated in the wettest, lowest, or most heavily irrigated part of the yard.',
      'Your normal broadleaf weed killer flattened the dandelions and did nothing at all to this.',
    ],
  },
  pickB: {
    headline: 'It is just grass if...',
    reasons: [
      'The stem is round or flattened, with no edges to feel.',
      'The leaves come off the stem in two ranks, alternating side to side.',
      'It grows at the same pace as the rest of the lawn between mows.',
      'The color and finish match the turf around it rather than shining against it.',
      'It is spread evenly across the yard instead of clustering in the damp spots.',
    ],
  },
  faqs: [
    {
      question: 'Why does nutsedge grow faster than my grass?',
      answer:
        'Because it is not competing on the same terms. Sedges put energy into fast vertical growth from an underground tuber that already holds a reserve, so they can shoot up while turf is still recovering from the mow. That is why the classic sign of nutsedge is a lawn that looks flat on Saturday and speckled with spikes by Tuesday. The speed is a symptom of the tuber system, and it is also why cutting it back never helps: you are trimming the top of a plant whose bank account is underground.',
    },
    {
      question: 'Why does weed killer not kill nutsedge?',
      answer:
        'Standard lawn herbicides are formulated for two plant families, grasses and broadleaves. Sedges are a third lineage with different biology, so a product that wipes out dandelions and crabgrass leaves nutsedge glossy and thriving. You need a sedge-specific active ingredient, typically a halosulfuron or sulfentrazone product, applied while the plants are young. Whatever you buy, follow the rate and the grass-type restrictions on the label exactly, because sedge products vary a lot in which turf species they are safe on.',
    },
    {
      question: 'Can I just pull nutsedge out?',
      answer:
        'Only very early, and only if there is not much of it. Each plant sets small tubers on its root system through the summer, and each tuber can resprout several times. Once a plant is past roughly the five-leaf stage, pulling the shoot usually stimulates more shoots than it removes, because you break the top off and leave the tuber intact. Early-season pulling before tubers form does genuinely help. Established patches are a two-season project no matter what you do.',
    },
    {
      question: 'Does nutsedge mean my lawn is too wet?',
      answer:
        'Usually, yes. Nutsedge pressure maps almost perfectly onto moisture: low spots, poorly drained soil, over-irrigated zones, and anywhere a downspout dumps. Before you buy anything, walk the patch after a rain and see where the water sits. Cutting irrigation frequency in favor of deeper, less frequent watering, filling low areas, and fixing downspout discharge removes the conditions the tubers need, and it makes the herbicide work you do far more durable.',
    },
    {
      question: 'Is nutsedge the same as crabgrass?',
      answer:
        'No, and they need different products. Crabgrass is a true grass, so it has round stems, two-ranked leaves, and a sprawling flat habit, and it responds to grass-weed herbicides and spring pre-emergent timing. Nutsedge is a sedge, so it has triangular stems, three-ranked leaves, an upright habit, and it ignores both. If a product is labeled for crabgrass and you are looking at a triangular stem, you are about to spend money on the wrong problem.',
    },
    {
      question: 'Will nutsedge come back next year?',
      answer:
        'If you only treated the tops, yes. The tubers overwinter in the soil and sprout again when it warms, which is why nutsedge control is measured in seasons rather than weekends. Expect visible progress the first year and real control the second, provided you pair the sedge-specific product with the drainage and irrigation changes. A patch treated once and then re-watered on the same schedule will be back on time.',
    },
  ],
}

const dallisgrassVsCrabgrass: TopicComparisonEntry = {
  slug: 'dallisgrass-vs-crabgrass',
  kind: 'topic',
  topicGroup: 'lookalike',
  shortLabel: 'Dallisgrass vs Crabgrass',
  h1: 'Dallisgrass vs Crabgrass: Why Pre-Emergent Only Stops One of Them',
  metaTitle: 'Dallisgrass vs Crabgrass: How to Tell Them Apart',
  metaDescription:
    'Dallisgrass is a perennial that returns from a crown every year, so pre-emergent does not stop it. Crabgrass is a summer annual. Here is how to tell which you have.',
  lastModified: '2026-08-18',
  authorSlug: 'sarah-mitchell',
  directAnswer:
    'Clumps that return to the same spots every year and ignored your pre-emergent are dallisgrass, and digging the crown out is the reliable fix. Clumps that are new each spring, sprawl flat, and vanish at the first frost are crabgrass, which spring pre-emergent timing prevents.',
  a: {
    displayName: 'Dallisgrass',
    sub: 'Perennial clump that returns from the same crown',
    photo: DALLISGRASS_PHOTO,
    link: { href: '/weed-identifier', label: 'Identify my grassy weed', icon: 'weed' },
  },
  b: {
    displayName: 'Crabgrass',
    sub: 'Summer annual that dies at the first frost',
    photo: CRABGRASS_PHOTO,
    link: { href: '/weeds/crabgrass', label: 'See how to kill crabgrass', icon: 'weed' },
  },
  toolCta: {
    href: '/tools/herbicide-timing',
    label: 'Time my pre-emergent',
    blurb:
      'Pre-emergent works on crabgrass and not on dallisgrass. Get the spring window for your ZIP before you spend on either.',
  },
  intro: (
    <>
      <p>
        The difference that matters is lifespan: dallisgrass is a perennial that regrows from the
        same crown every spring, so a pre-emergent barrier does nothing to it, while crabgrass is a
        summer annual that must start from seed each year and is therefore stopped cold by
        well-timed pre-emergent. If you have been applying crabgrass preventer for three springs and
        the same clumps keep reappearing in the same spots, you are almost certainly looking at
        dallisgrass.
      </p>
      <p>
        Visually, dallisgrass forms coarse yellow-green circular clumps that stand taller than the
        mowed lawn within a couple of days of cutting, and older clumps thin out in the middle so
        the patch reads like a donut. Its seed head carries three to six finger-like spikes off a
        central stem, often with small black spots. Crabgrass sits much lower, sprawls flat with
        stems radiating from one point, and pulls out easily as a shallow rosette. Age is the other
        clue: crabgrass clumps are new every year, while a dallisgrass clump gets wider and more
        established each season in exactly the same place.
      </p>
    </>
  ),
  verdict:
    'If the same coarse clumps come back in the same spots year after year and pre-emergent never touched them, it is dallisgrass and digging out the crown is the reliable fix. If the clumps are new each spring, sprawl flat, and vanish at frost, it is crabgrass and spring pre-emergent timing solves it.',
  table: [
    {
      label: 'Life cycle',
      a: 'Perennial. Returns from the same crown for years.',
      b: 'Summer annual. Starts from seed every spring and dies at frost.',
    },
    {
      label: 'Does pre-emergent stop it?',
      a: 'No. The plant is already alive underground when you apply it.',
      b: 'Yes. This is the single most effective thing you can do about crabgrass.',
    },
    {
      label: 'Shape of the patch',
      a: 'Coarse circular clump, often thinning to a donut in the middle as it ages',
      b: 'Flat sprawling rosette with stems radiating from one central point',
    },
    {
      label: 'Height between mows',
      a: 'Stands clearly above the mowed lawn within a day or two of cutting',
      b: 'Stays low and pressed to the ground, more sideways than upward',
    },
    {
      label: 'Blade texture and color',
      a: 'Coarse, wide, yellow-green, and stiffer than the turf',
      b: 'Wide, flat, and lighter green, but softer and lower',
    },
    {
      label: 'Seed head',
      a: 'Three to six finger-like spikes off a central stem, often with small black spots',
      b: 'Finger-like spikes splaying off the stem tips, no black spotting',
    },
    {
      label: 'Root system',
      a: 'Short thick rhizomes forming a tough crown that survives digging if you miss part of it',
      b: 'Shallow fibrous roots that lift as one unit',
    },
    {
      label: 'Does it pull out?',
      a: 'No. You dig it, and you take soil with it.',
      b: 'Yes, easily, especially while young and the soil is moist.',
    },
    {
      label: 'Same spot next year?',
      a: 'Yes, and wider than last year',
      b: 'Only if seed from last year germinates there again',
    },
    {
      label: 'What actually works',
      a: 'Dig out the whole crown plus a few inches of surrounding soil, then reseed the bare spot',
      b: 'Well-timed spring pre-emergent, spot treatment while young, and a denser lawn',
    },
    {
      label: 'Verdict',
      a: 'A perennial you have to remove. Prevention products do not apply.',
      b: 'An annual you can prevent. Timing beats spraying.',
    },
  ],
  pickA: {
    headline: 'It is dallisgrass if...',
    reasons: [
      'The same clumps come back in the same spots every year and get wider each season.',
      'You have applied crabgrass pre-emergent and it made no difference to them.',
      'The clumps stand noticeably taller than the lawn two days after you mow.',
      'Older patches are thinning in the middle and read as rings rather than solid clumps.',
      'The seed head has finger-like spikes off a central stem, sometimes with small black spots.',
    ],
  },
  pickB: {
    headline: 'It is crabgrass if...',
    reasons: [
      'The clumps were not there last November and appeared over the course of this spring.',
      'They lie flat with stems fanning out from a single center point.',
      'A firm pull lifts the whole plant out with a shallow root ball.',
      'They showed up first along the driveway, the sidewalk, or a thin sunny strip.',
      'The first hard frost wipes them out completely and leaves bare soil behind.',
    ],
  },
  faqs: [
    {
      question: 'Will pre-emergent stop dallisgrass?',
      answer:
        'No, and this is the most expensive misunderstanding in grassy weed control. Pre-emergent works by stopping seeds from establishing, which is exactly right for a summer annual like crabgrass. Dallisgrass is a perennial: the plant survives winter in a crown of short thick rhizomes and simply resumes growing in spring, with no germination step for the barrier to interrupt. If you have been applying preventer faithfully and the same clumps keep returning, the product is not failing, the diagnosis is.',
    },
    {
      question: 'How do I get rid of dallisgrass?',
      answer:
        'For a handful of clumps, physical removal is the most reliable answer available to a homeowner: dig out the entire crown plus a few inches of the surrounding soil so no rhizome fragments are left, then reseed or plug the bare spot promptly so nothing else colonizes it. For anything larger, talk to your local extension office about what is labeled for your specific turf species, because selective control options are limited and vary by grass type. Do not improvise a mix, and follow the label rate on anything you do apply.',
    },
    {
      question: 'Is dallisgrass worse than crabgrass?',
      answer:
        'Harder to deal with, yes. Crabgrass is annoying but it is on a one-year clock and a single well-timed pre-emergent application breaks the cycle. Dallisgrass is permanent until you remove it, gets larger every season, and shrugs off the entire prevention playbook that works on crabgrass. The upside is that dallisgrass usually arrives as a few discrete clumps rather than a general infestation, which makes digging a realistic option if you catch it early.',
    },
    {
      question: 'Does crabgrass come back in the same spot every year?',
      answer:
        'It looks that way, but the mechanism is different. Crabgrass plants die at frost. What comes back is seed from those plants germinating in the same warm, thin, sunny spots the following spring, which is why the pattern repeats along driveways and sidewalk edges where soil warms first. Dallisgrass genuinely regrows from the same living crown. The tell is size: a returning crabgrass patch starts small each spring, while a dallisgrass clump picks up where it left off and keeps expanding.',
    },
    {
      question: 'How can I tell dallisgrass from bahiagrass?',
      answer:
        'They are close relatives and both throw finger-like seed heads, which is why they get confused in the South. Bahiagrass produces a distinctive Y-shaped seed head with usually two spikes, sometimes three, and it is commonly a deliberate lawn grass in Florida and the Gulf Coast. Dallisgrass throws three to six spikes off a central stem, often with small black spots, and grows as a coarse circular clump inside another lawn rather than as the lawn itself. If you are not sure, our weed identifier will take a photo and narrow it down.',
    },
    {
      question: 'Can I stop dallisgrass by mowing more often?',
      answer:
        'Mowing will not remove it, but a good mowing and density program does slow it down. Dallisgrass needs open soil and light to establish new seedlings, so a thick lawn mowed at the right height for its species is genuinely fewer new clumps per year. What mowing cannot do is touch the crowns of the plants you already have. Treat mowing as the thing that stops it spreading and digging as the thing that removes it.',
    },
  ],
}

const tallFescueVsCrabgrass: TopicComparisonEntry = {
  slug: 'tall-fescue-vs-crabgrass',
  kind: 'topic',
  topicGroup: 'lookalike',
  shortLabel: 'Tall Fescue Clumps vs Crabgrass',
  h1: 'Tall Fescue Clumps vs Crabgrass: Which One Is In Your Lawn?',
  metaTitle: 'Tall Fescue Clumps vs Crabgrass: Which Is It?',
  metaDescription:
    'Coarse clumps in a lawn are often tall fescue, not crabgrass. Color, posture, and root depth tell them apart, and only one of them responds to crabgrass killer.',
  lastModified: '2026-08-18',
  authorSlug: 'sarah-mitchell',
  directAnswer:
    'Tall fescue clumps stand upright, read the same green as the lawn or darker, root deep, and are there all year. Crabgrass sprawls flat, reads lighter and yellower, and pulls out shallow. Crabgrass products only work on the crabgrass; fescue clumps have to be dug out and reseeded.',
  a: {
    displayName: 'Tall Fescue Clumps',
    sub: 'A lawn grass growing in the wrong place',
    photo: TALL_FESCUE_CLUMP_PHOTO,
    link: { href: '/guides/tall-fescue', label: 'Read the Tall Fescue guide', icon: 'guide' },
  },
  b: {
    displayName: 'Crabgrass',
    sub: 'Summer annual weed that dies at frost',
    photo: CRABGRASS_PHOTO,
    link: { href: '/weeds/crabgrass', label: 'See how to kill crabgrass', icon: 'weed' },
  },
  toolCta: {
    href: '/tools/overseeding-calculator',
    label: 'Plan the patch reseed',
    blurb:
      'Removing a fescue clump leaves a hole. Get the seed quantity and the right window for your ZIP so it closes cleanly.',
  },
  intro: (
    <>
      <p>
        If the clump is darker or equal in color to the lawn, stands upright, and refuses to pull
        out, it is tall fescue, not crabgrass, and no crabgrass product will touch it. Crabgrass is
        the opposite on every count: lighter than the lawn, sprawling flat rather than standing up,
        and easy to lift out as a single shallow rosette. People reach for crabgrass killer on
        fescue clumps constantly, and it is a wasted application every time.
      </p>
      <p>
        The confusing part is that tall fescue is a perfectly good lawn grass. It is simply out of
        place: a few coarse, deep-rooted clumps sitting inside a finer bluegrass or ryegrass stand,
        usually from contaminated seed or an old pasture-type fescue that survived a renovation.
        Because it is a desirable species, nothing selective will remove it from a cool-season lawn,
        so the honest fix is physical. Dig the clump, take the roots, and reseed the hole. Season
        matters more than product choice here, and the tell is timing: fescue clumps are there in
        April and still there in December, while crabgrass arrives in June and is gone after the
        first hard frost.
      </p>
    </>
  ),
  verdict:
    'Tall fescue clumps are upright, coarse, the same green or darker than the lawn, deep-rooted, and present all year. Crabgrass is flat, lighter, shallow, and seasonal. Only crabgrass responds to crabgrass products; fescue clumps have to be dug out and reseeded.',
  table: [
    {
      label: 'Color against the lawn',
      a: 'Same green or darker than the surrounding turf',
      b: 'Noticeably lighter and yellower than the surrounding turf',
    },
    {
      label: 'Posture',
      a: 'Upright, standing above the canopy in a tight bunch',
      b: 'Sprawling flat with stems pressed to the ground',
    },
    {
      label: 'Blade texture',
      a: 'Coarse, stiff, with visible ribs running the length of the blade',
      b: 'Wide and flat but soft, with no pronounced ribbing',
    },
    {
      label: 'Does it pull out?',
      a: 'No. Deep roots, and you end up digging.',
      b: 'Yes. Lifts as one shallow rosette, especially when young.',
    },
    {
      label: 'Growth habit',
      a: 'Bunch type. It widens slowly from the crown and does not run.',
      b: 'Radiating stems from one point, spreading sideways across the surface',
    },
    {
      label: 'Life cycle',
      a: 'Perennial. The same clump is there every season.',
      b: 'Summer annual. New plants each spring, dead at the first hard frost.',
    },
    {
      label: 'When it appears',
      a: 'Visible year-round, and often greenest in spring and fall',
      b: 'Germinates in spring, obvious from midsummer, gone by late fall',
    },
    {
      label: 'What winter does',
      a: 'Nothing. It stays green longer than most of the lawn.',
      b: 'Kills it, leaving a bare spot where the clump was',
    },
    {
      label: 'Response to crabgrass killer',
      a: 'None worth having, and some products will damage the lawn around it',
      b: 'Works well on young plants when applied at the label rate',
    },
    {
      label: 'What actually works',
      a: 'Dig the clump with its roots, then reseed the hole with your lawn seed',
      b: 'Spring pre-emergent, spot treatment while young, and mowing tall',
    },
    {
      label: 'Verdict',
      a: 'A good grass in the wrong spot. Remove it by hand.',
      b: 'A real weed on a one-year clock. Prevent it in spring.',
    },
  ],
  pickA: {
    headline: 'It is tall fescue if...',
    reasons: [
      'The clump is as dark as the lawn or darker, never paler.',
      'It stands upright in a tight bunch instead of sprawling flat.',
      'The blades feel coarse and stiff, with ribs you can see along the length.',
      'You cannot pull it out, and getting it up means a shovel.',
      'It was there last winter, and it will be there next winter.',
    ],
  },
  pickB: {
    headline: 'It is crabgrass if...',
    reasons: [
      'The clump is lighter and yellower than the grass around it.',
      'It lies flat with stems fanning out from one center point.',
      'A firm pull brings up the whole plant with a shallow root ball.',
      'It showed up this summer and was not there in the spring.',
      'The first hard frost kills it outright and leaves bare soil.',
    ],
  },
  faqs: [
    {
      question: 'Is that clump tall fescue or crabgrass?',
      answer:
        'Look at color and posture first, because they split cleanly. Tall fescue clumps are the same green as the lawn or darker, and they stand upright in a tight bunch. Crabgrass is lighter and yellower, and it sprawls flat with stems radiating from a single point. Then pull. Crabgrass lifts out with a shallow root ball; tall fescue holds because its roots run deep. If you still cannot decide, wait for the first hard frost: crabgrass dies and leaves a bare spot, and tall fescue is still there and still green.',
    },
    {
      question: 'Will crabgrass killer kill tall fescue clumps?',
      answer:
        'No, and the application is worse than wasted. Post-emergent crabgrass products are designed to hit a specific annual grass without harming desirable turf, and tall fescue is desirable turf, so it survives. Meanwhile you have put a herbicide on a lawn for no benefit and taken on whatever risk the label carries for your grass type. If the clumps are fescue, put the sprayer away and get a shovel.',
    },
    {
      question: 'How do I get rid of tall fescue clumps in my lawn?',
      answer:
        'Physical removal is the practical answer for a cool-season lawn, because nothing selective removes one desirable grass from another. Dig each clump out with its root mass, take a couple of inches of surrounding soil so you do not leave crowns behind, then fill and reseed the hole with the seed your lawn actually is. Do it in the fall overseeding window if you can, so the new seed germinates fast and nothing else colonizes the bare patch first.',
    },
    {
      question: 'Why does tall fescue grow in clumps in my lawn?',
      answer:
        'Tall fescue is a bunch-type grass, which means it widens slowly from a crown rather than running sideways with stolons or rhizomes. In a lawn that is mostly tall fescue, that is invisible because all the bunches sit together. In a bluegrass or ryegrass lawn, each fescue plant reads as a distinct coarse clump. The usual source is contaminated seed or an older pasture-type fescue that survived a renovation. Modern turf-type tall fescue is much finer, which is why an old clump can look coarse next to a newer fescue lawn too.',
    },
    {
      question: 'Should I just leave tall fescue clumps alone?',
      answer:
        'That is a legitimate choice, especially if there are only a few. Tall fescue is drought-tolerant, deep-rooted, and stays green when finer grasses struggle, so the clumps are not hurting the lawn, they are just a texture mismatch. What they do not do is blend in over time, and each clump slowly widens. If the look bothers you, remove them while there are a handful. If it does not, they are harmless.',
    },
    {
      question: 'Does tall fescue die in winter like crabgrass?',
      answer:
        'No. Tall fescue is a cool-season perennial and it is one of the last grasses in a lawn to lose color in fall, so a clump that is still standing green in December is a strong ID by itself. Crabgrass has no winter at all: the first hard frost kills the plants outright and leaves a bare spot that reseeds itself the following spring. If you want a free ID with zero effort, note where the clumps are in September and go back and look in January.',
    },
  ],
}

const cloverVsOxalis: TopicComparisonEntry = {
  slug: 'clover-vs-oxalis',
  kind: 'topic',
  topicGroup: 'lookalike',
  shortLabel: 'Clover vs Oxalis',
  h1: 'Clover vs Oxalis: Three Leaflets, Two Very Different Weeds',
  metaTitle: 'Clover vs Oxalis: How to Tell Them Apart',
  metaDescription:
    'Clover and oxalis both have three leaflets. Round leaflets with a pale band and white flowers means clover; heart-shaped leaflets and yellow flowers means oxalis.',
  lastModified: '2026-08-18',
  authorSlug: 'sarah-mitchell',
  directAnswer:
    'Look at the leaflet and the flower. Rounded leaflets with a pale band and white pom-pom flowers are white clover, and feeding the lawn is usually the first move. Heart-shaped notched leaflets with small yellow flowers are oxalis, and a thicker lawn plus early pulling is the fix.',
  a: {
    displayName: 'White Clover',
    sub: 'Nitrogen-fixing perennial with white pom-pom flowers',
    photo: WHITE_CLOVER_PHOTO,
    link: { href: '/weeds/white-clover', label: 'See the white clover guide', icon: 'weed' },
  },
  b: {
    displayName: 'Oxalis (Yellow Woodsorrel)',
    sub: 'Heart-shaped leaflets and small yellow flowers',
    photo: OXALIS_PHOTO,
    link: { href: '/weed-identifier', label: 'Identify my broadleaf weed', icon: 'weed' },
  },
  toolCta: {
    href: '/tools/fertilizer-calculator',
    label: 'Feed the lawn first',
    blurb:
      'Heavy clover is usually a nitrogen problem. Get the feeding rate for your grass type, lawn size, and ZIP before you spray anything.',
  },
  intro: (
    <>
      <p>
        Look at the leaflet shape and the flower color and the answer is immediate: clover has three
        rounded leaflets, often with a pale crescent watermark, and white to pinkish pom-pom flowers,
        while oxalis has three heart-shaped leaflets with a notch at the tip and small bright-yellow
        five-petal flowers. Both grow low, both have three leaflets, and both get called clover by
        just about everyone, which is why the two get treated with the same products and the same
        disappointing results.
      </p>
      <p>
        The reason to separate them is that they are telling you different things. Heavy white clover
        is usually a nitrogen report: clover fixes its own nitrogen out of the air, so it thrives
        exactly where underfed grass is losing, and a regular feeding program often shrinks it with
        no spraying at all. Oxalis is an opportunist that moves into thin, open turf and reseeds
        aggressively from little capsules that fling seed several feet when they dry. Clover you can
        legitimately choose to keep. Oxalis is a weed by any reasonable definition, and the fix for
        it is density plus early hand-pulling.
      </p>
    </>
  ),
  verdict:
    'Round leaflets with a pale band and white pom-pom flowers means white clover, and feeding the lawn is usually the first move. Heart-shaped notched leaflets with yellow flowers means oxalis, and the fix is a thicker lawn plus pulling it before the seed capsules dry.',
  table: [
    {
      label: 'Leaflet shape',
      a: 'Three rounded, oval leaflets',
      b: 'Three heart-shaped leaflets, each notched at the tip',
    },
    {
      label: 'Leaf marking',
      a: 'Often a pale crescent watermark across each leaflet',
      b: 'No watermark, and foliage often carries a purple or red tinge',
    },
    {
      label: 'Flower',
      a: 'White to pinkish globe, like a small pom-pom, and full of bees',
      b: 'Small bright-yellow flower with five petals',
    },
    {
      label: 'How it spreads',
      a: 'Creeping stolons that root at every node, plus hard seed',
      b: 'Seed capsules that split and fling seed several feet when they dry',
    },
    {
      label: 'Growth habit',
      a: 'Low mats that expand outward from the edges',
      b: 'Small upright clumps scattered through thin turf',
    },
    {
      label: 'What it says about your soil',
      a: 'Low nitrogen. Clover feeds itself, so it wins where grass is hungry.',
      b: 'Thin turf and open soil, more than a nutrient problem',
    },
    {
      label: 'Drought behavior',
      a: 'Stays green through dry spells that brown the grass around it',
      b: 'Persists in dry thin areas but does not stay lush',
    },
    {
      label: 'Season',
      a: 'Perennial, most obvious from late spring through summer bloom',
      b: 'Flowers from late spring into autumn and keeps reseeding',
    },
    {
      label: 'Is it worth keeping?',
      a: 'Sometimes. It fixes nitrogen, feeds pollinators, and stays green in drought.',
      b: 'Not really. It offers nothing the lawn wants and reseeds hard.',
    },
    {
      label: 'First move',
      a: 'Feed the lawn on a real schedule and see how much of it recedes on its own',
      b: 'Hand-pull while soil is moist and before capsules mature, then thicken the turf',
    },
    {
      label: 'Verdict',
      a: 'A soil diagnosis you can also choose to live with.',
      b: 'A reseeding opportunist. Density and early removal are the answer.',
    },
  ],
  pickA: {
    headline: 'It is white clover if...',
    reasons: [
      'The three leaflets are rounded rather than notched at the tip.',
      'You can see a pale crescent watermark across the leaflets.',
      'The flowers are white or slightly pink globes and the bees are all over them.',
      'The patch spreads as a low mat with runners rooting along the ground.',
      'It stays green through a dry spell that has browned the grass around it.',
    ],
  },
  pickB: {
    headline: 'It is oxalis if...',
    reasons: [
      'Each leaflet is heart-shaped with a clear notch at the tip.',
      'The flowers are small, bright yellow, and have five petals.',
      'The foliage carries a purple or reddish cast rather than plain green.',
      'It grows as scattered small upright clumps instead of a spreading mat.',
      'You can find slim upright seed capsules that pop open when you touch them dry.',
    ],
  },
  faqs: [
    {
      question: 'Is it clover or oxalis in my lawn?',
      answer:
        'Check the tip of a leaflet and the color of the flower. Clover leaflets are rounded and often carry a pale crescent watermark, and the flowers are white or pinkish globes. Oxalis leaflets are heart-shaped with a distinct notch at the tip, and the flowers are small and bright yellow with five petals. If there are no flowers yet, the leaflet notch settles it on its own, and oxalis foliage frequently has a purple or reddish tint that clover does not.',
    },
    {
      question: 'Is clover or oxalis harder to kill?',
      answer:
        'Oxalis is generally the more stubborn of the two in a home lawn, because it reseeds constantly from capsules that fling seed several feet and it keeps producing them from late spring into autumn. Clover is a perennial that spreads by runners and seed, which sounds worse, but clover responds to something oxalis does not: feeding the grass. Take clover nitrogen advantage away with a real fertilization program and many lawns watch it recede over two seasons with no herbicide at all.',
    },
    {
      question: 'Does clover mean my soil is bad?',
      answer:
        'It usually means your soil is low in nitrogen, which is a very fixable thing rather than bad soil. Clover is a legume, so it partners with root bacteria to pull nitrogen out of the air and feed itself, while grass has to get nitrogen from the soil. In an underfed lawn that gap widens every season and the balance tips toward clover. That is why heavy clover is best read as a report rather than an invasion, and why fertilizing is the first move, not the last.',
    },
    {
      question: 'Should I keep clover in my lawn?',
      answer:
        'It is a legitimate choice and plenty of people make it deliberately. Clover stays green through drought that browns everything around it, supports pollinators, and quietly fertilizes the grass next to it. Some owners now overseed micro-clover on purpose for exactly those reasons. The trade-offs are the bee traffic in bare feet, the patchy two-tone look next to fine turf, and the fact that it flowers. If a uniform grass stand is the goal, feed the lawn first and treat spraying as the second step.',
    },
    {
      question: 'How do I get rid of oxalis?',
      answer:
        'Start with density and timing. Hand-pull while the soil is moist and, most importantly, before the seed capsules mature, because a plant left to dry out will scatter the next generation for you. Raise the mowing height so germinating seedlings sit in shade, and keep the lawn fed so thin patches close. If you do reach for a product, choose one labeled for oxalis and for your specific grass type, apply at the rate on the label, and expect to repeat it, because the seed bank in the soil is what you are actually fighting.',
    },
    {
      question: 'Is oxalis the same as shamrock?',
      answer:
        'The plant sold as a potted shamrock is usually an ornamental oxalis, so the family is the same even though the lawn weed and the gift-shop plant are different species. Yellow woodsorrel, the one in your turf, is a small weedy oxalis with yellow flowers and heart-shaped leaflets. True clover is a different genus entirely, with rounded leaflets and white flowers, and it is what most people picture when they think of a shamrock. Three leaflets alone never identifies any of them.',
    },
  ],
}

/**
 * Cost figures for the turf and lawn-service comparisons resolve from the same
 * data modules the calculators use, so a page and its calculator cannot drift
 * apart. `data/artificial-turf-costs.ts` drives
 * /tools/artificial-turf-cost-calculator; `data/lawn-care-costs.ts` drives the
 * /costs hub and the lawn cost estimator.
 *
 * Two figures on these pages have no in-repo source and stay literal, each with
 * a dated citation at its use site: Sunday's subscription price and TruGreen's
 * annual service range.
 */
const addTurfCost = (...ranges: TurfCostRange[]): TurfCostRange =>
  ranges.reduce((sum, r) => ({ low: sum.low + r.low, high: sum.high + r.high }), { low: 0, high: 0 })

const turfPerSqFt = (range: TurfCostRange) => `$${range.low.toFixed(2)} to $${range.high.toFixed(2)}`

const turfTotal = (range: TurfCostRange, squareFeet: number) =>
  `$${Math.round(range.low * squareFeet).toLocaleString('en-US')} to $${Math.round(range.high * squareFeet).toLocaleString('en-US')}`

const TURF_BASE_AND_INFILL = addTurfCost(TURF_BASE_PREP_COST, TURF_INFILL_COST)

/** Standard grade installed by a pro: turf, base, weed barrier, infill, labor. */
const TURF_PRO_PER_SQ_FT = turfPerSqFt(
  addTurfCost(TURF_MATERIAL_COSTS.standard, TURF_BASE_AND_INFILL, TURF_PRO_LABOR_COST),
)
/** Same materials with the labor line swapped for tool rental. */
const TURF_DIY_PER_SQ_FT = turfPerSqFt(
  addTurfCost(TURF_MATERIAL_COSTS.standard, TURF_BASE_AND_INFILL, TURF_DIY_TOOL_COST),
)
const TURF_PRO_PER_1000_SQ_FT = turfTotal(
  addTurfCost(TURF_MATERIAL_COSTS.standard, TURF_BASE_AND_INFILL, TURF_PRO_LABOR_COST),
  1000,
)
const TURF_REMOVAL_PER_SQ_FT = turfPerSqFt(TURF_REMOVAL_COST)

const WATER_PER_MONTH = formatRange(rangeAcrossRegions((c) => c.maintenance.waterPerMonth))
/** Basic NPK at the low end through a premium blend at the top. */
const FERTILIZER_PER_1000_SQ_FT = formatRange(
  spanRanges(
    rangeAcrossRegions((c) => c.fertilizer.basic),
    rangeAcrossRegions((c) => c.fertilizer.premium),
  ),
)
const WEED_CONTROL_PER_1000_SQ_FT = formatRange(rangeAcrossRegions((c) => c.treatments.weedControl))
const MOWING_PER_VISIT = formatRange(rangeAcrossRegions((c) => c.maintenance.mowingPerVisit))
const COOL_SEASON_SEED_PER_1000_SQ_FT = formatRange(rangeAcrossRegions((c) => c.seed.coolSeason))

/**
 * Sod has no cost module. The band is the span of the sod pallet prices quoted
 * elsewhere in this file, $150 to $500 per 500 sq ft pallet, which is $0.30 to
 * $1.00 per sq ft.
 */
const SOD_PER_SQ_FT = '$0.30 to $1.00'
const SOD_PER_1000_SQ_FT = '$300 to $1,000'

/** Yearly Premium at App Store parity, from PRICING.YEARLY in lib/auth/feature-flags. */
const PREMIUM_YEARLY_PRICE = WEB_DISCOUNT.yearly.appStoreLabel

/**
 * Competitor prices, which no module in this repo owns. Sources, both dated,
 * to re-check before quoting either on a new page:
 *
 * - Sunday, $129 to $229/yr on a 3,000 sq ft lawn and up toward $450 on a large
 *   one: obsidian/inbox/competitor-feature-gap-2026-05-16.md (2026-05-16) and
 *   obsidian/research/competitive-analysis-master.md ($112 to $450/yr).
 * - TruGreen, $450 to $2,000+/yr: the same 2026-05-16 snapshot, corroborated by
 *   obsidian/research/competitor-teardowns/trugreen-teardown.md (2026-05-29).
 *
 * The visible sourcing note on each page cites the 2026-05-16 snapshot, because
 * that is the dated document both bands are read from. The teardown is an IPA
 * deconstruction of the TruGreen app, so it is not what a reader should be
 * pointed at for a price.
 */
const SUNDAY_YEARLY_PRICE = '$129 to $229'
const SUNDAY_LARGE_LAWN_PRICE = '$450'
const TRUGREEN_YEARLY_PRICE = '$450 to $2,000'

/**
 * Outbound link to a source a reader can open. Matches the site's non-affiliate
 * outbound convention (`noopener noreferrer nofollow`, as used by the grass
 * atlas source links and ComparePhotoCredits); the affiliate `sponsored` token
 * is deliberately absent because none of these are paid placements.
 *
 * Styled inline rather than through a `cmp-*` class: the compare stylesheet has
 * no `.cmp-prose a` rule, so an unstyled anchor would fall through to the UA
 * blue. `color: inherit` plus an underline mirrors `.cmp-credits a`.
 */
function SourceLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer nofollow"
      style={{ color: 'inherit', textDecoration: 'underline' }}
    >
      {children}
    </a>
  )
}

/**
 * One-line provenance note for the dollar figures on a page.
 *
 * Rendered as the closing element of an entry's `intro`, which is the last slot
 * before the verdict card and the comparison table. The compare template takes
 * no dedicated sourcing field and lives outside this module, so a shared
 * component here is the reusable form available: one definition, four call
 * sites, each stating only the provenance its own page actually has.
 */
function SourceNote({ children }: { children: ReactNode }) {
  return (
    <p style={{ fontSize: '0.86rem', lineHeight: 1.65, color: 'var(--cmp-gray-500)' }}>
      {children}
    </p>
  )
}

/**
 * US App Store listing URLs for the apps these pages make claims about.
 *
 * Verified against the iTunes lookup API on 19 August 2026, matching both the
 * seller and the bundle id to what the copy describes: PictureThis is
 * cn.danatech.xingseus from Glority Global Group Ltd., and My Lawn is
 * com.scotts.lawnapp from The Scotts Miracle-Gro Company (version 10.3.1,
 * released 1 July 2026).
 *
 * These are load bearing, not decorative. The ownership disclosure on both app
 * entries promises the reader that every claim about the other app links to
 * that publisher's own listing, so a page that renders no link makes the
 * disclosure false. `__tests__/data/comparisons-app-entries.test.ts` asserts the
 * link is present rather than leaving it to review.
 */
const PICTURETHIS_LISTING_URL =
  'https://apps.apple.com/us/app/picturethis-plant-identifier/id1252497129'
const MY_LAWN_LISTING_URL =
  'https://apps.apple.com/us/app/my-lawn-a-guide-to-lawn-care/id372269879'

const artificialTurfVsNaturalGrass: TopicComparisonEntry = {
  slug: 'artificial-turf-vs-natural-grass',
  kind: 'topic',
  topicGroup: 'landscape',
  shortLabel: 'Artificial Turf vs Natural Grass',
  h1: 'Artificial Turf vs Natural Grass: Cost, Upkeep, and the Honest Trade-offs',
  metaTitle: 'Artificial Turf vs Natural Grass: Cost and Upkeep',
  metaDescription:
    'Artificial turf costs thousands up front and almost nothing per year. A real lawn is cheap to start and costs every season. Here is where the two actually cross.',
  lastModified: '2026-08-18',
  directAnswer:
    'Artificial turf makes sense if you are staying a decade or more, water is expensive where you live, and you want the yard to stop asking for your weekends. Natural grass wins on up-front cost, on how it feels and cools in summer, and any time you might move first.',
  a: {
    displayName: 'Artificial Turf',
    sub: 'High up-front cost, near-zero annual upkeep',
    photo: ARTIFICIAL_TURF_PHOTO,
    link: { href: '/tools/lawn-size-calculator', label: 'Measure my yard first', icon: 'calculator' },
  },
  b: {
    displayName: 'Natural Grass',
    sub: 'Cheap to establish, costs you every season',
    photo: KENTUCKY_BLUEGRASS_PHOTO,
    link: {
      href: '/tools/lawn-cost-estimator',
      label: 'Estimate my yearly lawn cost',
      icon: 'calculator',
    },
  },
  toolCta: {
    href: '/tools/artificial-turf-cost-calculator',
    label: 'Price my turf install',
    blurb:
      'Get a low-to-high install range for your square footage, turf grade, and region, with DIY and professional numbers side by side.',
  },
  intro: (
    <>
      <p>
        Artificial turf costs far more up front and almost nothing per year, while natural grass is
        cheap to install and costs you every single season in water, fertilizer, and time, so the
        right answer depends almost entirely on how long you plan to stay. A professional
        standard-grade turf install runs roughly {TURF_PRO_PER_SQ_FT} per square foot once you add
        turf, base, infill, and labor, which puts a 1,000 square foot yard somewhere around{' '}
        {TURF_PRO_PER_1000_SQ_FT}. Seeding or sodding the same area is a small fraction of that.
      </p>
      <p>
        After that the arrow flips. A turf yard costs you a rinse, an occasional infill top-up, and
        nothing else. A real lawn costs water at roughly {WATER_PER_MONTH} a month in season,
        fertilizer at about {FERTILIZER_PER_1000_SQ_FT} per 1,000 square feet per application, weed
        control at about {WEED_CONTROL_PER_1000_SQ_FT} per 1,000 square feet, plus mowing, whether
        that is your Saturday or {MOWING_PER_VISIT} per visit for
        someone else. Those running costs are what eventually catch up with the install bill, and in
        practice many homeowners find the two cross somewhere around year 7 to 12, sooner in dry
        regions with expensive water and watering restrictions.
      </p>
      <SourceNote>
        Where these figures come from: turf install, DIY, and removal from our artificial turf cost
        model, which publishes national low to high ranges rather than quotes; seeding and running
        costs from our regional lawn cost dataset across 13 US regions; and the sod band from the
        pallet prices quoted on our grass comparison pages.
      </SourceNote>
    </>
  ),
  verdict:
    'Artificial turf wins if you are staying put for a decade or more, water is expensive where you live, and you want the yard to stop asking for your weekends. Natural grass wins on up-front cost, on how it feels and cools in summer, and any time you might move before the install pays itself back.',
  table: [
    {
      label: 'Up-front cost per sq ft',
      a: `About ${TURF_PRO_PER_SQ_FT} installed by a pro at standard grade, or about ${TURF_DIY_PER_SQ_FT} as a DIY material and rental cost`,
      // Both sides have to be per sq ft or the row invites a 1,000x misread. Seed
      // is quoted per 1,000 sq ft everywhere else, so it gets converted here.
      b: `About ${SOD_PER_SQ_FT} per sq ft in sod, or about a penny to a penny and a half per sq ft in seed, which is ${COOL_SEASON_SEED_PER_1000_SQ_FT} per 1,000 sq ft`,
    },
    {
      label: 'Typical 1,000 sq ft yard',
      a: `About ${TURF_PRO_PER_1000_SQ_FT} professionally installed at standard grade`,
      b: `About ${COOL_SEASON_SEED_PER_1000_SQ_FT} in seed, or roughly ${SOD_PER_1000_SQ_FT} in sod`,
    },
    {
      label: 'Old lawn removal',
      a: `About ${TURF_REMOVAL_PER_SQ_FT} per sq ft for a pro to strip and haul it away`,
      b: 'Not applicable',
    },
    {
      label: 'Lifespan',
      a: 'Commonly 8 to 18 years depending on grade and traffic. Check the warranty on the exact product.',
      b: 'Indefinite if you maintain it, and repairable one patch at a time',
    },
    {
      label: 'Annual running cost',
      a: 'Close to zero. Occasional rinsing and an infill top-up.',
      b: `Water around ${WATER_PER_MONTH} a month in season, fertilizer about ${FERTILIZER_PER_1000_SQ_FT} per 1,000 sq ft per application, weed control about ${WEED_CONTROL_PER_1000_SQ_FT} per 1,000 sq ft`,
    },
    {
      label: 'Mowing',
      a: 'None, ever',
      b: `Weekly in season, or about ${MOWING_PER_VISIT} per visit if you hire it out`,
    },
    {
      label: 'Water use',
      a: 'Only for rinsing',
      b: 'Roughly an inch a week including rain during the growing season',
    },
    {
      label: 'Surface temperature in full sun',
      a: 'Gets noticeably hotter than a living lawn on a hot afternoon. Rinse it or stay off it.',
      b: 'Stays cooler than turf, pavement, or decking because the plants transpire',
    },
    {
      label: 'Pets',
      a: 'Durable and mud-free, but urine needs rinsing or the smell builds in the infill',
      b: 'Urine burns spots that need reseeding, and heavy use makes mud',
    },
    {
      label: 'Kids and sports',
      a: 'Playable year-round with no wear patches, on a firmer surface',
      b: 'Softer underfoot, but wears into bare paths where traffic concentrates',
    },
    {
      label: 'Drainage',
      a: 'Depends entirely on the base. A rushed base is the main cause of puddling and rippling.',
      b: 'Soil-dependent, and improvable with aeration and topdressing',
    },
    {
      label: 'Look over time',
      a: 'Uniform and consistent, though the fibers flatten in traffic lanes and the color does not change with the season',
      b: 'Changes with the season, and improves with care or declines with neglect',
    },
    {
      label: 'Resale and HOA',
      a: 'Polarizing. Some buyers value it, some do not, and some HOAs restrict it. Check before you buy.',
      b: 'Universally accepted, and a well-kept lawn is a standard curb-appeal asset',
    },
    {
      label: 'End of life',
      a: 'Has to be removed and disposed of, and most turf is not easily recycled',
      b: 'Nothing to dispose of. You renovate in place.',
    },
    {
      label: 'Verdict',
      a: 'Buy time back and pay for it now. Best over a long stay in a dry, expensive-water region.',
      b: 'Cheap to start and paid for in seasons. Best if you might move, or you want a cooler, softer yard.',
    },
  ],
  pickA: {
    headline: 'Pick artificial turf if...',
    reasons: [
      'You expect to stay in the house long enough for a payback measured in years, not months.',
      'Water is expensive where you live, or watering restrictions make a real lawn hard to keep alive.',
      'The area is small, shaded, or high-traffic and grass has repeatedly failed there.',
      'You want the yard to stop asking for weekends, and near-zero maintenance is worth a five-figure decision.',
      'You have dogs and mud is the actual problem you are solving, and you are willing to rinse it.',
    ],
  },
  pickB: {
    headline: 'Stick with natural grass if...',
    reasons: [
      'You might move within the next several years, so the install would never pay itself back.',
      'You want the coolest, softest surface in the yard on a hot afternoon.',
      'Up-front budget matters more than the running cost, and seed or sod fits it easily.',
      'You actually enjoy the mowing and the maintenance, or you do not mind paying for it.',
      'Your HOA restricts synthetic turf, or you care about the environmental trade-off at end of life.',
    ],
  },
  faqs: [
    {
      question: 'Is artificial turf cheaper than real grass?',
      answer:
        `Not up front, and not for years afterward. Professional standard-grade turf runs roughly ${TURF_PRO_PER_SQ_FT} per square foot installed, so a 1,000 square foot yard lands around ${TURF_PRO_PER_1000_SQ_FT}, while seeding or sodding that same area costs a small fraction of it. Turf gets cheaper over time because the annual cost drops close to zero, while a real lawn keeps spending on water, fertilizer, weed control, and mowing every season. Where they cross depends on your water bill and how you value your Saturdays. All of these figures are planning estimates, so get local quotes before you budget.`,
    },
    {
      question: 'How long does artificial turf last?',
      answer:
        'Commonly somewhere in the 8 to 18 year range, and the spread is mostly about grade, traffic, and how well the base was built. Heavier premium turf with a longer warranty tends to last at the top of that range, which is why it can cost less per year than an economy roll you replace sooner. Check the actual warranty on the product you are quoted rather than a general number, and ask the installer to itemize base depth, because a rushed base is the most common reason turf fails early.',
    },
    {
      question: 'Does artificial turf get hot?',
      answer:
        'Yes, noticeably hotter than a living lawn in direct sun. Grass cools itself by transpiring water, and synthetic fibers cannot do that, so on a hot cloudless afternoon a turf yard can be uncomfortable for bare feet and paws in a way a real lawn is not. The practical fixes are shade over the areas people actually use, a quick rinse before play, and cooler-running infill options if your installer offers them. If summer barefoot comfort is a priority for you, this is the trade-off to weigh hardest.',
    },
    {
      question: 'Is fake grass bad for dogs?',
      answer:
        'Not inherently, and plenty of dog owners install it specifically to solve mud and worn paths. The two real issues are heat and odor. Turf in full sun can be too hot for paws on a summer afternoon, so provide shade and rinse it before use. Urine drains through but the residue stays in the infill, so a turf dog run needs regular rinsing and sometimes an enzyme cleaner, or the smell builds up in warm weather. Plan the rinsing into the routine and it works well.',
    },
    {
      question: 'Do you have to maintain artificial turf?',
      answer:
        'Far less than a lawn, but it is not zero. Expect to rinse it, blow or sweep off leaves and debris so nothing composts into the fibers, brush high-traffic lanes so they stand back up, and top up infill occasionally as it settles and migrates. Weeds can still sprout in windblown soil that collects on the surface, especially at edges. Compared with mowing, watering, feeding, and weed control, it is a rounding error, which is the whole point.',
    },
    {
      question: 'Does artificial turf hurt home resale value?',
      answer:
        'It is polarizing rather than automatically negative. Some buyers see a solved problem and a yard with no upkeep, especially in dry regions. Others see a surface they will have to pay to remove. Local norms matter more than any national rule, and so do HOA and municipal rules, some of which restrict synthetic turf outright. Check your HOA documents and local ordinances before you commit, and if resale is a live concern, consider turfing a defined area rather than the whole front yard.',
    },
    {
      question: 'How long until artificial turf pays for itself?',
      answer:
        `Many homeowners land somewhere around year 7 to 12 versus the running cost of a maintained lawn, and it comes sooner in dry regions where water is expensive or restricted. Run your own numbers rather than trusting the range: add up what you actually spend per year on water, fertilizer, weed control, and mowing, then divide the install quote by it. If you are paying someone ${MOWING_PER_VISIT} a visit to mow on top of a summer water bill, the payback arrives much faster than if you mow yourself and rarely irrigate.`,
    },
  ],
}

const sundayVsScotts: BrandComparisonEntry = {
  slug: 'sunday-vs-scotts-lawn-care',
  kind: 'brand',
  shortLabel: 'Sunday vs Scotts Lawn Care',
  h1: 'Sunday vs Scotts: Mailed Subscription or a Retail Program You Buy Yourself?',
  metaTitle: 'Sunday vs Scotts Lawn Care: Which Program Wins?',
  metaDescription:
    'Sunday mails you liquid nutrient pouches on a schedule. Scotts sells a granular program you buy and apply yourself. Cost, coverage, and effort compared.',
  lastModified: '2026-08-18',
  directAnswer:
    'Sunday mails you the products and the timing for one annual price. Scotts costs less per pound and sits on every big-box shelf, but you own the spreader, the calendar, and the call on which step goes down when.',
  a: {
    brand: 'Sunday',
    displayName: 'Sunday Lawn Care Subscription',
    productType: 'Done-for-you nutrient subscription (mailed liquid pouches)',
  },
  b: {
    brand: 'Scotts',
    displayName: 'Scotts four-step retail program',
    productType: 'Retail granular fertilizer program you buy and apply yourself',
    productsCategory: 'fertilizer',
    productKey: 'fertilizer-scotts-turf-builder',
    buyReason: 'The core feeding step of the Scotts program, bought once at retail instead of on a subscription.',
    badge: { label: 'Retail starting point', tone: 'green', tier: 'editor' },
  },
  intro: (
    <>
      <p>
        Sunday and Scotts are the same job packaged two completely different ways: Sunday decides
        what your lawn needs and mails it to you on a schedule, while Scotts sells you the products
        and leaves the plan, the timing, and the spreader work to you. Sunday builds its plan from
        satellite imagery and a mailed-in soil test, then ships 1 to 4 seasonal pouches of
        all-natural liquid nutrients you spray on with a hose. Most lawns land at{' '}
        {SUNDAY_YEARLY_PRICE} a year, and larger lawns can run up toward {SUNDAY_LARGE_LAWN_PRICE}.
      </p>
      <p>
        Scotts is the opposite trade. You buy granular bags off a shelf, you own a spreader, and you
        decide when each step goes down. There is no recurring charge and no lock-in, and the
        products are widely available and easy to replace, but nothing arrives to remind you and the
        program does not know anything about your soil. Scotts publishes plans rather than a single
        price, and what a season costs depends entirely on your lawn size and which steps you buy,
        so check the current plan page rather than budgeting from a number someone quoted you. The
        real question is whether you want a decision made for you or a cheaper shelf you control.
      </p>
      <SourceNote>
        Where these figures come from: the Sunday prices are from our 16 May 2026 competitor pricing
        snapshot, and there is no Scotts figure on this page at all, because Scotts publishes plans
        rather than one annual price.
      </SourceNote>
    </>
  ),
  verdict:
    'Sunday wins if you want the thinking, the timing, and the shipping handled for one predictable annual price. Scotts wins on cost per pound and availability, as long as you are willing to own the spreader, the calendar, and the decision about which step to put down when.',
  table: [
    {
      label: 'Model',
      a: 'Annual subscription, auto-shipped in seasonal boxes',
      b: 'Retail bags you buy per step, whenever you want them',
    },
    {
      label: 'Typical cost per year',
      a: `${SUNDAY_YEARLY_PRICE}, up to about ${SUNDAY_LARGE_LAWN_PRICE} for large lawns`,
      b: 'Varies by lawn size and how many steps you buy. Check the current Scotts plan page.',
    },
    {
      label: 'Who applies it',
      a: 'You do, with a hose-end sprayer they include',
      b: 'You do, with a broadcast or drop spreader you own',
    },
    {
      label: 'Product form',
      a: 'All-natural liquid concentrate in pouches',
      b: 'Granular, mostly synthetic, in bags',
    },
    {
      label: 'What it covers',
      a: 'Feeding and nutrients, with limited weed products in some plans',
      b: 'Feeding, plus weed and crabgrass steps depending on which bags you buy',
    },
    {
      label: 'Soil test',
      a: 'Mail-in kit included, with lab results in a couple of weeks',
      b: 'Not included. You can send your own to a local extension lab.',
    },
    {
      label: 'Personalization',
      a: 'Satellite imagery, your soil test, and local climate',
      b: 'None built in. You match the product to your grass type yourself.',
    },
    {
      label: 'Grass identification',
      a: 'Not a focus. The plan assumes you know.',
      b: 'Not included, though the bags carry grass-type warnings you must read',
    },
    {
      label: 'Scheduling and reminders',
      a: 'The box arriving is the reminder',
      b: 'Entirely on you, or on a free reminder tool',
    },
    {
      label: 'Availability',
      a: 'Ships to you, and only from Sunday',
      b: 'Every big-box store and most hardware stores carry it',
    },
    {
      label: 'Product control',
      a: 'Sunday formulas only',
      b: 'Anything on the shelf, including cheaper store brands with the same analysis',
    },
    {
      label: 'Lock-in and cancellation',
      a: 'Recurring subscription you have to cancel',
      b: 'None. You simply stop buying bags.',
    },
    {
      label: 'Effort required',
      a: 'Low. Spray what arrives when it arrives.',
      b: 'Medium. You plan the calendar, buy the bags, and calibrate the spreader.',
    },
    {
      label: 'Best for',
      a: 'Owners who want an all-natural plan with no decisions to make',
      b: 'Owners who want the cheapest path per pound and do not mind running the calendar',
    },
    {
      label: 'Verdict',
      a: 'Pay for the decisions and the delivery.',
      b: 'Pay less for the product and do the deciding yourself.',
    },
  ],
  pickA: {
    headline: 'Pick Sunday if...',
    reasons: [
      'You want the plan, the products, and the timing decided for you and shipped automatically.',
      'You prefer all-natural liquid nutrients over measuring out granular products.',
      'You want a lab soil test included without sourcing one yourself.',
      'You will genuinely skip applications if nothing shows up to prompt you.',
      'Feeding the lawn is the goal, not diagnosing weeds, disease, or pests.',
    ],
  },
  pickB: {
    headline: 'Pick the Scotts program if...',
    reasons: [
      'You want the lowest cost per pound of product and are comfortable shopping for it.',
      'You already own a spreader and do not mind calibrating it.',
      'You want to buy a single step this month and skip the next one without cancelling anything.',
      'You like being able to walk into a store today and have the product in hand this afternoon.',
      'You are happy to run your own calendar, or to use a free reminder tool for it.',
    ],
  },
  faqs: [
    {
      question: 'Is Sunday better than Scotts?',
      answer:
        'They are optimized for different things. Sunday is better if you want the decisions removed: it builds a plan from satellite data and a soil test, ships what you need when you need it, and uses all-natural liquid formulas. Scotts is better if you want the cheapest path to the same nutrients and are willing to run the program yourself, because retail granular fertilizer costs less per pound and is available everywhere. Neither one identifies your grass, diagnoses disease, or tells you when to treat weeds, which is the gap the free tools here fill.',
    },
    {
      question: 'How much does Scotts lawn care cost per year?',
      answer:
        'It depends on your lawn size and how many steps of the program you buy, and Scotts prices by product rather than by a single annual plan, so the honest answer is to check the current plan page for your square footage. The useful way to think about it is per 1,000 square feet per application. Retail granular fertilizer generally runs in the same range as any other bagged product of the same analysis, which is why cost per pound of nitrogen is a fairer comparison than sticker price on the bag.',
    },
    {
      question: 'Can I cancel a Sunday subscription?',
      answer:
        'Yes, it is a subscription and you can cancel it, though as with any auto-ship service you want to do it ahead of the next shipment rather than after it lands. That is the structural difference from a retail program: with Scotts you simply stop buying bags and nothing happens, while with Sunday you have to actively end the arrangement. If you are unsure you will stick with a subscription, that difference is worth weighing before the first box ships.',
    },
    {
      question: 'Is Sunday all-natural fertilizer as effective as Scotts?',
      answer:
        'Both deliver nutrients, but they get there differently and they behave differently. Sunday uses liquid all-natural formulas applied more frequently at lighter amounts, which tends to give steady, gentler feeding. Scotts uses granular products, often with slow-release nitrogen, applied less often at higher amounts. For a typical home lawn either approach produces a good stand of grass if the timing is right. Timing and consistency matter more than the label on either bag, which is why the reminder side of the problem is worth solving whichever you pick.',
    },
    {
      question: 'Do I need a spreader for Scotts products?',
      answer:
        'Yes, for the granular ones, and using one properly matters more than most people expect. A broadcast spreader set to the wrong rate is how lawns get striped, burned, or underfed. Every Scotts bag prints settings for common spreader models, so match the setting on your machine to the bag rather than guessing, and walk at a steady pace. Sunday avoids this entirely by shipping liquid concentrate you attach to a hose, which is a real convenience advantage if you do not own a spreader.',
    },
    {
      question: 'Which is better for a small lawn?',
      answer:
        'For a genuinely small lawn, retail bags usually win on cost, because a subscription priced for an average yard is expensive per square foot when the yard is tiny, and a single bag of granular fertilizer covers a small lawn for a whole season. Sunday still wins on convenience and on the included soil test. If the small lawn is also the reason you do not own a spreader, the hose-end liquid approach may be worth the premium on its own.',
    },
  ],
}

const sundayVsTruGreen: BrandComparisonEntry = {
  slug: 'sunday-vs-trugreen',
  kind: 'brand',
  shortLabel: 'Sunday vs TruGreen',
  h1: 'Sunday vs TruGreen: Spray It Yourself or Have a Technician Do It?',
  metaTitle: 'Sunday vs TruGreen: Subscription or Pro Service?',
  metaDescription:
    'Sunday mails you nutrients to apply yourself. TruGreen sends a technician to treat your lawn. Cost, coverage, contracts, and who each one actually suits.',
  lastModified: '2026-08-18',
  directAnswer:
    'Sunday mails you nutrients and you do the spraying. TruGreen sends a technician who does the whole thing and typically costs several times more. Pick on whether your lawn is simply hungry or has problems you have not been able to diagnose.',
  a: {
    brand: 'Sunday',
    displayName: 'Sunday Lawn Care Subscription',
    productType: 'Mailed nutrient subscription you apply yourself (fertilizer)',
  },
  b: {
    brand: 'TruGreen',
    displayName: 'TruGreen professional lawn service',
    productType: 'Technician-applied professional lawn treatment service',
  },
  intro: (
    <>
      <p>
        The split is simple: Sunday sends you the products and you do the spraying, while TruGreen
        sends a person who does the whole thing for you, and the price difference reflects exactly
        that. Sunday builds a plan from satellite imagery and a mailed-in soil test, then ships 1 to
        4 seasonal pouches of all-natural liquid nutrients. Most lawns land at{' '}
        {SUNDAY_YEARLY_PRICE} a year, and larger lawns can run up toward {SUNDAY_LARGE_LAWN_PRICE}.
        TruGreen is a national done-for-you service with technicians who physically treat your lawn
        on a schedule, and it typically runs {TRUGREEN_YEARLY_PRICE} or more a year depending on
        lawn size and which services you add.
      </p>
      <p>
        The other real difference is scope. Sunday is a feeding subscription: nutrients, a soil test,
        and limited weed products in some plans. TruGreen is a treatment service that also covers
        weed control, and depending on the plan, disease, insects, aeration, and overseeding, with a
        technician who looks at the lawn each visit. If your problem is that the grass is hungry,
        Sunday is the cheaper fit. If your problem is weeds, disease, or grubs you have not been able
        to solve, the service is buying you a diagnosis and the labor, not just the chemistry.
      </p>
      <SourceNote>
        Where these figures come from: the Sunday and TruGreen bands are both from our 16 May 2026
        competitor pricing snapshot, and our own Premium price is read live from the pricing config
        rather than typed into this page.
      </SourceNote>
    </>
  ),
  verdict:
    'Sunday is the cheaper choice for a lawn that mainly needs feeding and an owner who does not mind spraying. TruGreen costs several times more and earns it only when you want the labor gone and the problems beyond feeding handled by somebody who shows up.',
  table: [
    {
      label: 'Model',
      a: 'Mailed subscription you apply yourself',
      b: 'Scheduled visits from a technician who applies everything',
    },
    {
      label: 'Typical cost per year',
      a: `${SUNDAY_YEARLY_PRICE}, up to about ${SUNDAY_LARGE_LAWN_PRICE} for large lawns`,
      b: `Typically ${TRUGREEN_YEARLY_PRICE} or more depending on lawn size and services`,
    },
    {
      label: 'Who applies it',
      a: 'You, with a hose-end sprayer',
      b: 'A technician, on their route schedule',
    },
    {
      label: 'What it covers',
      a: 'Feeding and nutrients, plus limited weed products on some plans',
      b: 'Feeding and weed control, with disease, insect, aeration, and overseeding as add-ons',
    },
    {
      label: 'Diagnosis',
      a: 'None on site. The plan is built remotely from imagery and a soil test.',
      b: 'A technician looks at the lawn each visit and leaves notes',
    },
    {
      label: 'Soil test',
      a: 'Mail-in kit included',
      b: 'Varies by branch and plan. Ask before you sign.',
    },
    {
      label: 'Product type',
      a: 'All-natural liquid concentrate',
      b: 'Professional-grade commercial products',
    },
    {
      label: 'Product control',
      a: 'Sunday formulas only, but you know exactly what went down',
      b: 'The technician chooses. Ask for the service record if you want the detail.',
    },
    {
      label: 'Scheduling',
      a: 'Boxes arrive seasonally and you pick the day you spray',
      b: 'They schedule the visit, usually with a notification window',
    },
    {
      label: 'Effort required',
      a: 'Low. Spray what arrives.',
      b: 'None. Somebody else does the work.',
    },
    {
      label: 'Lock-in and cancellation',
      a: 'Recurring subscription you cancel when you want',
      b: 'A service agreement. Read the cancellation and prepayment terms before signing.',
    },
    {
      label: 'Pets and kids',
      a: 'You control the timing and the re-entry window on the label',
      b: 'Follow the re-entry guidance the technician leaves after each visit',
    },
    {
      label: 'Best for',
      a: 'Hands-off owners on a budget whose lawn mainly needs feeding',
      b: 'Owners who want the whole job gone, including weeds, disease, and pests',
    },
    {
      label: 'Verdict',
      a: 'Cheaper, narrower, and still your labor.',
      b: 'Several times the cost, and the labor and the judgment come with it.',
    },
  ],
  pickA: {
    headline: 'Pick Sunday if...',
    reasons: [
      'Feeding is the actual problem and the lawn is otherwise healthy.',
      'You want an all-natural product and you want to know exactly what went on the lawn.',
      'A few hundred dollars a year is the budget, not a few thousand.',
      'You are happy to spend twenty minutes with a hose-end sprayer a few times a season.',
      'You do not want anyone scheduling visits to your property.',
    ],
  },
  pickB: {
    headline: 'Pick TruGreen if...',
    reasons: [
      'You want the work gone entirely, not made easier.',
      'The lawn has problems beyond feeding: persistent weeds, disease, or insect damage.',
      'You travel, rent out the property, or otherwise cannot be there to apply anything.',
      'You want a professional looking at the lawn on a schedule and leaving notes.',
      'The budget supports a service that typically runs several times a subscription.',
    ],
  },
  faqs: [
    {
      question: 'Is Sunday better than TruGreen?',
      answer:
        `It is cheaper and narrower. Sunday is a feeding subscription that costs most lawns ${SUNDAY_YEARLY_PRICE} a year and asks you to do the spraying. TruGreen is a full service that typically runs ${TRUGREEN_YEARLY_PRICE} or more a year and sends a technician who handles feeding, weed control, and, depending on the plan, disease and insect treatment. If your lawn just needs consistent nutrition, Sunday does that for a fraction of the cost. If your lawn has problems you have already failed to solve, you are buying diagnosis and labor, and Sunday does not sell either.`,
    },
    {
      question: 'Is TruGreen worth paying for when Sunday costs so much less?',
      answer:
        `Only if the gap between them is a gap you actually need closed. The spread here is roughly ${SUNDAY_YEARLY_PRICE} against ${TRUGREEN_YEARLY_PRICE} or more, and what the extra money buys is a person on site: someone who looks at the lawn each visit, treats problems a feeding subscription was never scoped to handle, and does the walking. If your lawn is simply underfed, Sunday closes that gap on its own and the premium buys you nothing you were missing. If the same weeds keep coming back or something is killing patches and you cannot name it, feeding harder will not fix it and the diagnosis is the thing you are short of.`,
    },
    {
      question: 'Does Sunday handle weeds, or only feeding?',
      answer:
        'Feeding is the core of it, and weed products appear only in some plans rather than as the point of the subscription. That is the single most important thing to check before you treat Sunday as a TruGreen replacement, because the two are often compared as if they cover the same ground. A service plan is built around treating what is wrong with the lawn on a schedule; a nutrient subscription is built around keeping it fed. If your complaint is weeds rather than color, confirm exactly which products are in the plan you are looking at before you sign up for either.',
    },
    {
      question: 'What sits between Sunday and TruGreen on price?',
      answer:
        `Not much, which is part of what makes the choice feel binary. Sunday at ${SUNDAY_YEARLY_PRICE} a year and a full service at ${TRUGREEN_YEARLY_PRICE} or more are close to the two ends of the range, and the middle is mostly assembled by hand: buy your own fertilizer and herbicide, and hire out only the jobs you do not want, such as aeration or a single weed treatment. Our free calculators cover the planning half of that, so if you want to build the middle option yourself, that is the part you do not have to pay for, and Premium at ${PREMIUM_YEARLY_PRICE} a year is the only line item on our side of it.`,
    },
    {
      question: 'Which one is easier to cancel?',
      answer:
        'The subscription, and it is not close. Sunday is ended the way any mailed subscription is ended, from your account, and what you lose is the next shipment. A professional service is a service agreement, which means cancellation terms, prepayment discounts that assume a full season, and automatic renewal are all live questions you should ask about before the first visit rather than in September. The one to ask out loud: what happens if you prepay for the year and stop in July.',
    },
    {
      question: 'Which is safer around pets and kids?',
      answer:
        'Both can be used responsibly, and in both cases the answer lives on the product label rather than in the marketing. Sunday uses all-natural formulas and you control exactly when they go down and how long to keep everyone off, which is a genuine advantage if timing around a family matters. With a professional service you should follow the re-entry guidance the technician leaves after each visit, and you can ask for the specific products used. Whichever route you take, follow the re-entry interval on the label rather than a rule of thumb.',
    },
  ],
}

const truGreenVsDiy: BrandComparisonEntry = {
  slug: 'trugreen-vs-diy-lawn-care',
  kind: 'brand',
  shortLabel: 'TruGreen vs DIY Lawn Care',
  h1: 'TruGreen vs DIY Lawn Care: What You Are Actually Paying For',
  metaTitle: 'TruGreen vs DIY Lawn Care: Is the Service Worth It?',
  metaDescription:
    `TruGreen typically runs ${TRUGREEN_YEARLY_PRICE} a year. DIY with free tools and retail products covers the same feeding and weed timing for a fraction. Here is the split.`,
  lastModified: '2026-08-18',
  directAnswer:
    'You are paying for time and a diagnosis, not for better chemistry. The fertilizer and herbicides a homeowner can buy will produce the same lawn, so hire the service when you cannot do the work or cannot name the problem, and do it yourself when the lawn is just underfed.',
  a: {
    brand: 'TruGreen',
    displayName: 'TruGreen professional lawn service',
    productType: 'Technician-applied professional lawn treatment service',
  },
  b: {
    brand: 'DIY',
    displayName: 'DIY with WhatGrassIsThis.com tools',
    productType: 'Free lawn tools plus your own retail fertilizer and herbicide',
    productsCategory: 'fertilizer',
    productKey: 'fertilizer-neighbors-envy',
    buyReason: 'An all-in-one hose-end liquid feed that covers the feeding side of a service plan for one retail purchase.',
    badge: { label: 'DIY starting point', tone: 'green', tier: 'editor' },
  },
  intro: (
    <>
      <p>
        TruGreen typically costs {TRUGREEN_YEARLY_PRICE} or more a year and doing the same work yourself
        costs a small fraction of that, so the honest question is not which is better but what the
        premium buys. What it buys is labor and judgment: a technician who shows up on a schedule
        whether or not you feel like it, applies commercial-grade products, and looks at the lawn
        each visit. That is real value if your weekends are scarce or if repeated weed and disease
        problems have already beaten your own efforts.
      </p>
      <p>
        The DIY route is the same job run on free tools and retail products. WhatGrassIsThis.com
        identifies your grass from a photo, and free calculators tell you how much fertilizer and
        water your specific lawn and ZIP need, what mowing height to use, and when to apply pre-
        and post-emergent herbicides. Fertilizer runs roughly {FERTILIZER_PER_1000_SQ_FT} per 1,000
        square feet per application and weed control about {WEED_CONTROL_PER_1000_SQ_FT} per 1,000
        square feet, so a typical season is a small number of bags rather than a service contract.
        The trade is that nobody shows up. You own the calendar, though Premium reminders and
        unlimited care plans at {PREMIUM_YEARLY_PRICE} a year
        automate most of that, and Free includes three lawn diagnoses a month.
      </p>
      <SourceNote>
        Where these figures come from: the TruGreen band is from our 16 May 2026 competitor pricing
        snapshot, the fertilizer and weed control figures from our regional lawn cost dataset across
        13 US regions, and our own Premium price is read live from the pricing config rather than
        typed into this page.
      </SourceNote>
    </>
  ),
  verdict:
    'TruGreen is worth it when you are buying back time or a diagnosis you could not reach on your own. DIY wins on cost by a wide margin and on control, and free tools now cover the planning that used to be the hardest part of doing it yourself.',
  table: [
    {
      label: 'Model',
      a: 'Scheduled visits from a technician under a service agreement',
      b: 'Free tools, retail products you buy as needed, no agreement',
    },
    {
      label: 'Typical cost per year',
      a: `Typically ${TRUGREEN_YEARLY_PRICE} or more depending on lawn size and services`,
      b: `$0 for the tools, plus fertilizer at about ${FERTILIZER_PER_1000_SQ_FT} per 1,000 sq ft per application and weed control at about ${WEED_CONTROL_PER_1000_SQ_FT} per 1,000 sq ft (${PREMIUM_YEARLY_PRICE}/yr optional Premium)`,
    },
    {
      label: 'Who does the work',
      a: 'A technician, on their route schedule',
      b: 'You, on your own schedule',
    },
    {
      label: 'What it covers',
      a: 'Feeding and weed control, with disease, insects, aeration, and overseeding as add-ons',
      b: 'Feeding, watering, mowing height, aeration timing, herbicide timing, and problem diagnosis',
    },
    {
      label: 'Grass identification',
      a: 'The technician identifies it on site',
      b: 'Free photo identification, no signup required',
    },
    {
      label: 'Soil test',
      a: 'Varies by branch and plan. Ask before signing.',
      b: 'Send your own extension test and load the results into the free Soil Test Analyzer',
    },
    {
      label: 'Product control',
      a: 'The technician chooses. Ask for the service record if you want to know.',
      b: 'Any brand and form you like, granular or liquid, organic or synthetic',
    },
    {
      label: 'Weed control',
      a: 'Included in most plans and applied for you',
      b: 'Herbicide Timing Calculator plus retail products, applied at the label rate',
    },
    {
      label: 'Disease and pest help',
      a: 'Available as add-on services with a technician diagnosis',
      b: 'Three photo diagnoses a month on Free, unlimited on Premium',
    },
    {
      label: 'Scheduling and reminders',
      a: 'They schedule the visits',
      b: 'You run the calendar, or use seasonal reminders on Premium',
    },
    {
      label: 'Effort required',
      a: 'None. That is the product.',
      b: 'Medium. A few hours across a season, spread over several applications.',
    },
    {
      label: 'Lock-in and cancellation',
      a: 'A service agreement with terms to read on cancellation and prepayment',
      b: 'None at all',
    },
    {
      label: 'Best for',
      a: 'Owners buying back time, or facing problems they have not been able to solve',
      b: 'Cost-conscious owners who want control and are willing to do the applications',
    },
    {
      label: 'Verdict',
      a: 'You are paying for labor and judgment, not better chemistry.',
      b: 'Same outcomes for far less, as long as you actually do it.',
    },
  ],
  pickA: {
    headline: 'Pick TruGreen if...',
    reasons: [
      'You want the job gone rather than made easier, and the budget supports it.',
      'You have already tried and failed to fix persistent weeds, disease, or insect damage.',
      'You travel often, rent the property out, or physically cannot do the applications.',
      'You would rather pay someone than risk misapplying a product on your own lawn.',
      'You value having a professional look at the lawn on a schedule and leave notes.',
    ],
  },
  pickB: {
    headline: 'Pick the DIY route if...',
    reasons: [
      'You want to spend a fraction of a service contract and are comfortable buying products.',
      'You want control over which brand and form goes on your lawn, and when.',
      'You need help with more than feeding: weeds, mowing height, watering, aeration, and diagnosis.',
      'You want tools that personalize to your grass type, ZIP, and lawn size with no subscription.',
      `You will handle the timing yourself, or let Premium reminders at ${PREMIUM_YEARLY_PRICE} a year handle it.`,
    ],
  },
  faqs: [
    {
      question: 'Is TruGreen worth the money?',
      answer:
        `It is worth it if what you are buying is time and judgment rather than better products. At typically ${TRUGREEN_YEARLY_PRICE} or more a year it costs several times what a careful DIY program does, and the fertilizer and herbicides available to homeowners are perfectly capable of producing the same lawn. Where the service earns its price is showing up on a schedule regardless of your week, and putting a trained eye on problems you have not been able to diagnose. If the lawn is basically healthy and just needs consistent feeding, you are paying a large premium for something you could do in a few hours a season.`,
    },
    {
      question: 'Can I do what TruGreen does myself?',
      answer:
        'Yes, for the majority of what a standard plan includes. Feeding, weed control timing, and the seasonal calendar are all within reach of a homeowner with retail products and good information. The two things you cannot buy on a shelf are somebody else doing the labor and on-site expertise across many lawns. Free tools here close the information gap: photo grass identification, fertilizer and watering calculators tuned to your grass type and ZIP, herbicide timing windows, and three photo diagnoses a month. What remains is the work itself.',
    },
    {
      question: 'How much cheaper is DIY lawn care?',
      answer:
        `Dramatically cheaper, usually by a factor of several. Fertilizer runs roughly ${FERTILIZER_PER_1000_SQ_FT} per 1,000 square feet per application depending on grade and region, and weed control lands around ${WEED_CONTROL_PER_1000_SQ_FT} per 1,000 square feet, so a full season on a typical suburban lawn is a modest number of bags rather than a monthly service bill. Compare that with ${TRUGREEN_YEARLY_PRICE} or more a year for a service. The tools that tell you what to buy and when are free, and the optional Premium tier is ${PREMIUM_YEARLY_PRICE} a year for unlimited diagnoses, reminders, and unlimited care plans.`,
    },
    {
      question: 'What does TruGreen actually put on my lawn?',
      answer:
        'Commercial-grade fertilizers and herbicides selected by the technician for your grass type, season, and whatever the visit found. The specific products vary by branch, plan, and region, so the reliable way to find out is to ask for the service record after each visit, which also tells you what re-entry interval applies for pets and kids. If knowing exactly what goes on your lawn matters to you, that transparency is worth asking about up front, and it is one of the clearer advantages of doing it yourself.',
    },
    {
      question: 'Do I have to sign a contract for lawn service?',
      answer:
        'Professional lawn services generally operate on a service agreement rather than a per-visit purchase, so read the cancellation, prepayment, and automatic renewal terms before you sign. Ask what happens if you cancel mid-season after prepaying, and confirm which services are in the base plan versus billed separately, since aeration, overseeding, and pest control are commonly add-ons. The DIY route has no equivalent commitment at all, which is worth something on its own if you are not sure you will stay with a program.',
    },
    {
      question: 'Is DIY lawn care safe to do myself?',
      answer:
        'Yes, provided you treat the label as the instruction manual rather than a suggestion. Every fertilizer and herbicide sold to homeowners prints the rate, the grass types it is safe on, and the re-entry interval for people and pets, and the vast majority of DIY problems come from ignoring one of those three. Never mix products that are not labeled to be mixed, never apply above the labeled rate, and check the grass-type restrictions before you buy rather than after. Our tools tell you when to apply; the label tells you how much.',
    },
  ],
}

/* ---------------------------------------------------------------------------
 * App comparisons (kind 'brand', category 'app')
 *
 * Sourcing rules these two entries are held to, because one side is our own
 * product on both of them:
 *
 * 1. Every first-party number is imported, never typed. The free-scan ladder
 *    comes from lib/auth/feature-flags and the two Premium prices from
 *    lib/pricing-display, so a pricing change moves this copy with it instead
 *    of leaving a stale figure on a page that argues about price.
 * 2. Every competitor fact is either read out of a publisher-controlled page
 *    (the US App Store listing) and date-stamped, or read out of an in-repo
 *    teardown under obsidian/research/competitor-teardowns/. Anything we could
 *    not source is absent rather than hedged.
 * 3. No accuracy claim of any kind, in either direction. We have not run a
 *    head-to-head ID benchmark, so hit rates are not a dimension these pages
 *    compare on. Scope, output, limits, price, platform, and what happens after
 *    the ID are.
 * ------------------------------------------------------------------------- */

/** Rendered above the fold on every entry where one side is ours. */
const APP_OWNERSHIP_DISCLOSURE =
  'Full disclosure: What Grass Is This? is our own product, so read this as our case for it rather than a neutral review. Every claim we make about the other app comes either from that publisher\'s own App Store listing, which this page links, or from our own teardown of the shipped app, so you can check it yourself.'

/** The free ladder, straight from the limit constants the API actually enforces. */
const OUR_FREE_TIER = `${ANONYMOUS_WEB_ANALYSIS_LIMITS.identify} identification with no account at all, ${EMAIL_TIER_ANALYSIS_LIMITS.identify} a month once you give us an email, and ${FREE_LIMITS.ANALYSES_PER_MONTH} a month with a free account. The calculators are free with no account and no limit.`

/** Both prices, because the CTA a reader clicks decides which one they pay. */
const OUR_PREMIUM_PRICE = `Premium is ${WEB_DISCOUNT.monthly.webDisplay} or ${WEB_DISCOUNT.yearly.webDisplay} buying direct on the web, ${WEB_DISCOUNT.monthly.appStoreDisplay} or ${WEB_DISCOUNT.yearly.appStoreDisplay} through the App Store, after a ${PREMIUM_TRIAL_DAYS}-day free trial.`

const whatGrassIsThisVsPictureThis: BrandComparisonEntry = {
  slug: 'what-grass-is-this-vs-picturethis',
  kind: 'brand',
  category: 'app',
  shortLabel: 'What Grass Is This vs PictureThis',
  h1: 'What Grass Is This? vs PictureThis: Which One Should Identify Your Lawn?',
  metaTitle: 'What Grass Is This vs PictureThis for Lawn Grass (2026)',
  metaDescription:
    'PictureThis identifies plants broadly. What Grass Is This? only does turfgrass and returns a lawn plan. Compare scope, free limits, price, and what you get after the ID.',
  lastModified: '2026-08-18',
  authorSlug: 'sarah-mitchell',
  directAnswer:
    'Use a turf specialist for the lawn itself and a general plant identifier for anything in the yard that is not. What Grass Is This? identifies turfgrass only and hands back a grass type plus the care plan that follows from it. PictureThis identifies plants broadly and hands back a plant name.',
  ownershipDisclosure: APP_OWNERSHIP_DISCLOSURE,
  a: {
    brand: 'What Grass Is This?',
    displayName: 'What Grass Is This?',
    productType: 'Turfgrass identification plus a lawn plan',
    appTile: { ours: true },
  },
  b: {
    brand: 'PictureThis',
    displayName: 'PictureThis',
    productType: 'General plant identification app (Glority Global Group Ltd.)',
    appTile: { lines: ['Picture', 'This'], monogram: 'PT' },
  },
  intro: (
    <>
      <p>
        These two apps get compared constantly, and most of the comparisons miss the
        point by arguing about which one is smarter. They are not really competing
        products. PictureThis is a broad plant identifier: point it at a houseplant, a
        shrub, a flower, or a weed and it tells you what it is. Its{' '}
        <SourceLink href={PICTURETHIS_LISTING_URL}>US App Store listing</SourceLink>{' '}
        headlines plant identification across more than 400,000 species, disease
        diagnosis, care tips and reminders, toxic plant warnings, weed identification,
        and a water tracker. Turfgrass is not named anywhere in it.
      </p>
      <p>
        That absence is the whole story. Lawn grasses are a genuinely nasty
        identification problem. A dozen of the species Americans actually grow are
        narrow green blades that look nearly identical in a photo, and the features
        that separate them are things like vernation, the shape of the ligule, and
        whether the plant spreads by stolons or rhizomes. A model trained to tell a
        monstera from a philodendron is not built around that problem. Ours is, because
        turf is the only thing it does.
      </p>
      <p>
        The second difference matters more in practice: what you get back. A plant name
        is the end of the road with a general identifier. With a lawn, the name is the
        beginning. Knowing you have tall fescue only helps once it turns into a mowing
        height, a fertilizer rate, an overseeding window, and a pre-emergent date for
        your ZIP code. That is the part we built the rest of the site around.
      </p>
      <SourceNote>
        Every claim on this page about PictureThis comes from its{' '}
        <SourceLink href={PICTURETHIS_LISTING_URL}>US App Store listing</SourceLink>, read on
        19 August 2026, so you can open it and check any of them yourself.
      </SourceNote>
    </>
  ),
  verdict:
    'Pick What Grass Is This? for the lawn itself, because you need a mowing height and a fertilizer schedule, not a species name. Pick PictureThis for everything in the yard that is not the lawn, because a turf specialist will be useless on your hydrangea.',
  table: [
    {
      label: 'What it identifies',
      a: 'Turfgrass only, plus lawn weeds and lawn diseases. The cool and warm season grasses Americans actually grow, including the ones that look alike in a photo.',
      b: 'Plants broadly. The US App Store listing headlines plant identification across more than 400,000 species, disease diagnosis, toxic plant warnings, and weed identification (checked 19 August 2026). Turfgrass is not named anywhere in it.',
    },
    {
      label: 'What the result gives you',
      a: 'A grass type with a confidence score and the runner-up species to compare against, a region match from your ZIP, and a starter care plan.',
      b: 'A plant name, with care information written for that plant.',
    },
    {
      label: 'What happens after the ID',
      a: 'Free calculators that take the grass type as their input: mowing height, fertilizer rate, watering schedule, aeration, overseeding, sod, and total cost. Seasonal reminders come with Premium.',
      b: 'Per the App Store listing (August 2026): plant care instructions, a watering tracker with reminders, automatic disease diagnosis, and access to expert consultation.',
    },
    {
      label: 'Free tier',
      a: OUR_FREE_TIER,
      b: 'Free to download with in-app purchases (US App Store listing, August 2026). The listing does not state how many free identifications you get.',
    },
    {
      label: 'Price',
      a: OUR_PREMIUM_PRICE,
      b: 'The US App Store listing shows in-app purchases of $3.99, $7.99, and $39.99 for PictureThis Pro, and $49.99 for a Family Plan (checked August 2026). The listing does not label which billing period each tier covers, so check in the app before you buy.',
    },
    {
      label: 'Where it runs',
      a: 'Any browser, on a phone or a computer, with nothing to install and no account needed for the first identification.',
      b: 'A phone app. Published by Glority Global Group Ltd. on the App Store.',
    },
    {
      label: 'Who it is built for',
      a: 'A homeowner standing on their own lawn who wants to know what to do to it next.',
      b: 'Anyone who wants to know the name of a plant, anywhere, including plants that have nothing to do with a yard.',
    },
    {
      label: 'Verdict',
      a: 'The right tool for the lawn, because it answers the question that comes after the species name.',
      b: 'The right tool for the other 95 percent of your yard, and for houseplants, which we do not touch at all.',
    },
  ],
  pickA: {
    headline: 'Pick What Grass Is This? if...',
    reasons: [
      'The thing you are photographing is the lawn, and the species you are choosing between are narrow green blades that look the same to a general model.',
      'You want the answer to be actionable. A grass type on its own changes nothing. A mowing height, a fertilizer rate, and a pre-emergent window for your ZIP code do.',
      `You do not want to install anything or create an account to try it. The first identification costs nothing and asks for nothing.`,
      'You want the calculators more than you want the identification. They are free, unlimited, and work whether or not you ever upload a photo.',
      'Your real question is a lawn question wearing an identification costume: why is this patch browning, when should I seed, how much fertilizer does this yard need.',
    ],
  },
  pickB: {
    headline: 'Pick PictureThis if...',
    reasons: [
      'The plant you are photographing is not grass. Its listing claims more than 400,000 species, so shrubs, perennials, houseplants, trees, and vegetables all sit inside that and completely outside ours.',
      'You want one app for the whole property rather than a specialist for one part of it.',
      'You want plant-level care features like a watering tracker and reminders across a collection of plants, which its App Store listing describes (August 2026).',
      'You are trying to identify something in the lawn that is clearly not turf, like a broadleaf weed or a volunteer seedling, and you want a general answer fast.',
      'You already pay for it and it is doing the job you bought it for. Nothing here is a reason to cancel a subscription that works.',
    ],
  },
  faqs: [
    {
      question: 'Is PictureThis good for identifying grass?',
      answer:
        'PictureThis is built to identify plants broadly, and its US App Store listing headlines plant identification across more than 400,000 species, disease diagnosis, care reminders, toxic plant warnings, weed identification, and a water tracker (checked 19 August 2026). Turfgrass is not named anywhere in it. That is not a knock on the app, it is a statement about what it was designed to do. Lawn grasses are a narrow, unusually difficult identification problem because a dozen commonly grown species look alike in a photo and separate on features like vernation and how the plant spreads. If the plant in your frame is not the lawn, a general identifier is the better tool. If it is the lawn, use something that only does turf.',
    },
    {
      question: 'Does PictureThis identify lawn weeds?',
      answer:
        'Weed identification is one of the features its App Store listing headlines, so pointing it at a dandelion, a clover patch, or a broadleaf you cannot name is well inside what it advertises (checked 19 August 2026). The awkward case is a grassy weed. Crabgrass, dallisgrass, nutsedge, and poa annua all read as narrow green blades in a photo, which is the same problem that makes turfgrass hard, and the listing does not claim turf coverage either way. If the weed has a broad leaf or a flower, a general identifier is a reasonable place to ask. If it is grass-shaped and growing in your lawn, that is the case we built for.',
    },
    {
      question: 'Do I need to download an app to identify my grass?',
      answer:
        'Only if you want the other things an app gives you. These two products answer that differently on purpose. PictureThis ships as a phone app because it expects to live on your home screen across a whole collection of plants, with reminders and a saved library. What Grass Is This? runs on the web because a grass question is usually asked once, in the yard, with a phone already in hand, and an install plus an account is a lot of ceremony for one answer. Neither shape is better in the abstract. They match different frequencies of use.',
    },
    {
      question: 'How much does PictureThis cost?',
      answer:
        'As of August 2026, the US App Store listing for PictureThis shows the app as free to download with in-app purchases, and lists PictureThis Pro at $3.99, $7.99, and $39.99, plus a Family Plan at $49.99. The listing does not say which billing period each of those tiers covers, so confirm the term inside the app before you subscribe. Prices and tiers change, so treat that as a snapshot rather than a standing fact and check the current listing.',
    },
    {
      question: 'What do you get after the grass is identified?',
      answer:
        'With What Grass Is This?, the grass type becomes the input to everything else. You get a confidence score and the runner-up species so you can sanity check the answer, a region match from your ZIP code, and a starter care plan. From there the free calculators take over: mowing height for your species, fertilizer rate for your square footage, a watering schedule, aeration and overseeding timing, sod quantities, and a total cost estimate. That handoff is the actual product. The identification is just how it starts.',
    },
    {
      question: 'Can one app identify both my lawn and my houseplants?',
      answer:
        'A general plant identifier is the only one of the two that can, because it covers the broad plant world by design. What Grass Is This? deliberately does not: it only covers turfgrass, lawn weeds, and lawn diseases, and it will not help you with a fiddle leaf fig. If you want one app for the entire property, a general identifier is the honest recommendation. If you have a lawn problem specifically, a specialist gets you further, and there is nothing stopping you from keeping both.',
    },
  ],
}

const whatGrassIsThisVsMyLawn: BrandComparisonEntry = {
  slug: 'what-grass-is-this-vs-my-lawn-scotts',
  kind: 'brand',
  category: 'app',
  shortLabel: 'What Grass Is This vs My Lawn (Scotts)',
  h1: 'What Grass Is This? vs My Lawn by Scotts: Which Lawn App Should You Use?',
  metaTitle: 'What Grass Is This vs My Lawn by Scotts (2026 Compare)',
  metaDescription:
    'My Lawn is free because it sells Scotts products. What Grass Is This? identifies your grass and works out rates in any brand. Compare what each one really optimizes for.',
  lastModified: '2026-08-18',
  authorSlug: 'james-thornton',
  directAnswer:
    'What Grass Is This? identifies your grass first and then calculates rates that work with whatever bag you already own. My Lawn is free because it is a Scotts storefront: the plan it builds recommends Scotts products to buy. Pick on whether you want the math done for you or the products chosen for you.',
  ownershipDisclosure: APP_OWNERSHIP_DISCLOSURE,
  a: {
    brand: 'What Grass Is This?',
    displayName: 'What Grass Is This?',
    productType: 'Turfgrass identification plus brand-neutral calculators',
    appTile: { ours: true },
  },
  b: {
    brand: 'My Lawn (Scotts)',
    displayName: 'My Lawn by Scotts',
    productType: 'Free seasonal plan app from Scotts Miracle-Gro',
    appTile: { lines: ['My', 'Lawn'], monogram: 'ML' },
  },
  intro: (
    <>
      <p>
        My Lawn is the official Scotts Miracle-Gro app, and it is genuinely free. Its{' '}
        <SourceLink href={MY_LAWN_LISTING_URL}>US App Store listing</SourceLink> carries no
        in-app purchases, and there is no subscription or paywall machinery in the app either.
        Understanding why is the whole comparison. When we pulled version 10.3.0 apart in
        May 2026, there was no payment SDK anywhere in the bundle, but there was a
        product listing feature, a buy-online feature, a store locator that finds
        nearby Scotts retail partners, and an auto-ship subscription for getting Scotts
        products delivered by season. The app is free because the products are the
        business.
      </p>
      <p>
        That is not a scandal, it is a business model, and it comes with a real
        advantage. If you are happy to run a Scotts program, My Lawn removes almost
        every decision. It tells you what to put down and when, and the thing it tells
        you to put down is available at the store down the road. For a homeowner who
        wants a lawn plan and does not want to think about it, that is a completely
        reasonable trade.
      </p>
      <p>
        The cost of the trade is that the plan is only ever as brand-neutral as the
        company paying for it. If the bag in your garage is a Lesco, a Milorganite, or a
        store brand, a product-led plan has nothing useful to say about it. Our approach
        starts one step earlier: identify what grass you actually have, then calculate
        the rate for the bag you own, whoever made it.
      </p>
      <SourceNote>
        Every claim on this page about My Lawn comes from its{' '}
        <SourceLink href={MY_LAWN_LISTING_URL}>US App Store listing</SourceLink>, read on
        19 August 2026, or from our May 2026 teardown of version 10.3.0, and each one says
        which.
      </SourceNote>
    </>
  ),
  verdict:
    'What Grass Is This? wins if you want to know your grass type first and then run the numbers on any brand of product, including one you already bought. My Lawn wins if you want a done-for-you seasonal plan and you are happy buying Scotts, because it will hand you a schedule and a shopping list for free.',
  table: [
    {
      label: 'What it costs',
      a: OUR_FREE_TIER,
      b: 'Free, and the listing carries no in-app purchases either (checked 19 August 2026). Our May 2026 teardown of version 10.3.0 found no payment or subscription SDK in the bundle at all.',
    },
    {
      label: 'How it makes money',
      a: `Optional Premium, plus affiliate links on the guides. ${OUR_PREMIUM_PRICE}`,
      b: 'Product sales. The teardown found a product listing feature, buy-online, a store locator for nearby Scotts retail partners, and a seasonal auto-ship program for Scotts products.',
    },
    {
      label: 'Does it identify your grass',
      a: 'Yes, that is the starting point. Photograph a blade and get the species, a confidence score, and the runner-up grasses to compare against.',
      b: 'No. Our teardown found no photo identification anywhere in the app. It classifies your lawn from a picture-pick quiz you answer yourself, covering grass color, thickness, and how weedy it is.',
    },
    {
      label: 'Product recommendations',
      a: 'Brand-neutral. The calculators work from the analysis on the bag you already have, so a Scotts bag, a store brand, and a professional product all run through the same math.',
      b: 'Scotts branded products, by lawn type and season. The teardown found the recommendation mix is server-driven, so the products it suggests can change remotely without an app update.',
    },
    {
      label: 'What you do with the answer',
      a: 'Run your own numbers: mowing height for your species, fertilizer rate for your square footage, watering schedule, aeration and overseeding timing, sod quantity, and a cost estimate.',
      b: 'Follow a seasonal schedule of applications, with the products to buy attached to each step.',
    },
    {
      label: 'Where it runs',
      a: 'Any browser, on a phone or a computer, with nothing to install.',
      b: 'A phone app from Scotts Miracle-Gro. The listing shows no iPad build, only the iPhone app running on Apple silicon Macs and Vision (checked 19 August 2026), which matches our May 2026 teardown of version 10.3.0.',
    },
    {
      label: 'Best for',
      a: 'A homeowner who wants to know what they have before they decide what to buy, and wants the rate math to work on any brand.',
      b: 'A homeowner who wants the decisions made for them and does not mind that one company is making them.',
    },
    {
      label: 'Verdict',
      a: 'The better fit when identification comes first and the product aisle comes second.',
      b: 'A free, well-built seasonal planner, as long as you read it as a Scotts plan rather than a neutral one.',
    },
  ],
  pickA: {
    headline: 'Pick What Grass Is This? if...',
    reasons: [
      'You do not know what grass you have yet, and every recommendation downstream depends on that answer being right.',
      'The fertilizer in your garage is not a Scotts bag, and you want the rate worked out for the analysis printed on it.',
      'You want to compare products on their numbers rather than take a recommendation from the company that sells them.',
      'You want the calculators without a plan wrapped around them: square footage, rate, mowing height, seeding window, cost.',
      'You would rather read a guide that names the trade-offs than follow a schedule that does not explain itself.',
    ],
  },
  pickB: {
    headline: 'Pick My Lawn by Scotts if...',
    reasons: [
      'You want a seasonal schedule handed to you and you are genuinely fine buying Scotts products to follow it.',
      'You already know what grass you have, so identification is not the problem you are trying to solve.',
      'You value a shopping list you can walk into a store and fill, over a rate calculation you have to do yourself.',
      'You want push reminders for the next application without paying anything, and you accept that the reminder is also a purchase prompt.',
      'You are starting from zero on a lawn and want any coherent plan rather than the perfectly optimized one.',
    ],
  },
  faqs: [
    {
      question: 'Is the My Lawn app by Scotts really free?',
      answer:
        'Yes, and there is no catch hidden in a paywall. When we deconstructed version 10.3.0 in May 2026, the app contained no payment SDK, no subscription framework, and no in-app purchase products of any kind, and the live listing still carries none (checked 19 August 2026, where the current build is 10.3.1 from 1 July 2026). What the bundle did contain was a product listing feature, a buy-online flow, a store locator that points at nearby Scotts retail partners, and a seasonal auto-ship program. The app is free because it exists to sell Scotts products. That is a perfectly normal arrangement, and it is worth knowing about before you read the plan it builds for you.',
    },
    {
      question: 'Does the My Lawn app identify your grass type?',
      answer:
        'No. My Lawn builds a plan from information you provide about your lawn rather than from a photo of it. That means the plan inherits whatever you told it, so if you guessed your grass type wrong, the schedule that follows is wrong in the same direction. If you are not certain what you are growing, identify the grass first and then feed that answer into whichever planner you prefer, including this one.',
    },
    {
      question: 'Will a lawn app recommend products it does not sell?',
      answer:
        'A manufacturer app has no reason to. Our teardown of My Lawn found the product recommendation mix is driven remotely, meaning the suggested products can be changed on the company side without shipping an app update, and the recommendations are drawn from the Scotts range. That is not deceptive, it is what a brand app is for. The practical consequence is simple: if you want to compare a Scotts product against a Lesco, a Milorganite, or a store brand, you need a source that does not have a stake in the answer. Our calculators work from whatever analysis is printed on your bag.',
    },
    {
      question: 'Can I use both apps together?',
      answer:
        'Yes, and it is a sensible combination. Use a grass identifier to find out what you are actually growing and to work out the rate for the product you have, and use a seasonal planner to keep the calendar. The two do different jobs. The one thing worth avoiding is letting a plan built around a guessed grass type run for a full season, because mowing height and fertilizer timing both change meaningfully between cool season and warm season grasses.',
    },
    {
      question: 'How do I know how much fertilizer to apply?',
      answer:
        'Start with the label. Every fertilizer bag sold to homeowners prints its application rate, the grass types it is safe on, and the spreader guidance for common spreaders, and that label is the authority for that specific product. What a calculator adds is the arithmetic around it: your actual square footage, how that maps onto the bag rate, and how many bags you need to buy. Our fertilizer calculator does that part for any brand. It does not invent a rate, and neither should any app.',
    },
  ],
}

const COMPARISONS: ComparisonEntry[] = [
  annualVsPerennialRyegrass,
  blendVsKentuckyBluegrass,
  bermudaVsZoysia,
  kbgVsTallFescue,
  bermudaVsKentuckyBluegrass,
  stAugustineVsZoysia,
  bermudaVsStAugustine,
  fescueVsBermuda,
  fineVsTallFescue,
  perennialRyegrassVsTallFescue,
  centipedeVsStAugustine,
  scottsWeedFeedVsMilorganite,
  scottsVsPenningtonSeed,
  lescoVsScotts,
  scottsWeedFeedVsTripleAction,
  bahiaVsBermuda,
  zoysiaVsCentipede,
  tenacityVsQuinclorac,
  prodiamineVsDithiopyr,
  peatMossVsTopsoil,
  sundayVsDiy,
  sundayVsScotts,
  sundayVsTruGreen,
  truGreenVsDiy,
  dormantVsDeadGrass,
  crabgrassVsBermudagrass,
  poaAnnuaVsKentuckyBluegrass,
  nutsedgeVsGrass,
  dallisgrassVsCrabgrass,
  tallFescueVsCrabgrass,
  cloverVsOxalis,
  artificialTurfVsNaturalGrass,
  whatGrassIsThisVsPictureThis,
  whatGrassIsThisVsMyLawn,
]

const COMPARISONS_BY_SLUG = new Map<string, ComparisonEntry>(
  COMPARISONS.map((c) => [c.slug, c]),
)

export function getComparisonBySlug(slug: string): ComparisonEntry | null {
  return COMPARISONS_BY_SLUG.get(slug) ?? null
}

export function getAllComparisonSlugs(): string[] {
  return COMPARISONS.map((c) => c.slug)
}

export function getAllComparisons(): ComparisonEntry[] {
  return COMPARISONS
}

/**
 * Order-independent lookup of a grass-vs-grass comparison page by the two grass
 * keys a user picked in the /tools/grass-comparison tool. The tool's grass names
 * (keys of GRASS_CHARACTERISTICS) are identical to GrassKey, so a direct match
 * works. Returns null when no dedicated /compare/[slug] page exists for the pair.
 * Used to funnel the interactive tool to the static article, which is the page
 * type that actually ranks for "X vs Y" queries.
 */
export function getGrassComparisonByKeys(
  keyA: GrassKey,
  keyB: GrassKey,
): GrassComparisonEntry | null {
  for (const c of COMPARISONS) {
    if (c.kind !== 'grass') continue
    const { key: a } = c.a
    const { key: b } = c.b
    if ((a === keyA && b === keyB) || (a === keyB && b === keyA)) return c
  }
  return null
}

// Brand comparisons use long descriptive productType strings (e.g. "Synthetic
// combo herbicide + fertilizer (granular)") so exact-match cross-linking
// returns nothing. Reduce each side to a coarse category — "seed" or
// "fertilizer" — for relatedness checks.
function brandCategoryOf(productType: string): 'seed' | 'fertilizer' | 'other' {
  const lower = productType.toLowerCase()
  if (lower.includes('seed')) return 'seed'
  if (lower.includes('fertilizer')) return 'fertilizer'
  return 'other'
}

/**
 * Surface up to `limit` related comparisons for cross-linking on a /compare/[slug] page.
 *
 * Grass comparisons match on shared grass key (either side overlaps).
 * Brand comparisons match on shared product category (seed or fertilizer), except
 * app matchups, which form their own closed set.
 * Topic comparisons match on shared topicGroup, then pad from the rest of the
 * topic set, because `diagnostic` and `landscape` each hold a single page and
 * would otherwise render an empty related block.
 * Results exclude the source slug and are sorted by slug for deterministic SSR.
 */
export function getRelatedComparisons(slug: string, limit = 3): ComparisonEntry[] {
  const current = COMPARISONS_BY_SLUG.get(slug)
  if (!current) return []

  const candidates = COMPARISONS.filter((c) => c.slug !== slug && c.kind === current.kind)

  const matches = candidates.filter((c) => {
    if (current.kind === 'topic' && c.kind === 'topic') {
      return c.topicGroup === current.topicGroup
    }
    if (current.kind === 'grass' && c.kind === 'grass') {
      const keys = new Set<GrassKey>([current.a.key, current.b.key])
      return keys.has(c.a.key) || keys.has(c.b.key)
    }
    if (current.kind === 'brand' && c.kind === 'brand') {
      // App matchups relate to each other, and never to a product matchup.
      // Checked before the category reduction below because brandCategoryOf()
      // collapses every app productType to 'other', which matches nothing.
      const currentIsApp = current.category === 'app'
      const otherIsApp = c.category === 'app'
      if (currentIsApp || otherIsApp) return currentIsApp && otherIsApp
      const currentCats = new Set([
        brandCategoryOf(current.a.productType),
        brandCategoryOf(current.b.productType),
      ])
      const otherCats = new Set([brandCategoryOf(c.a.productType), brandCategoryOf(c.b.productType)])
      for (const cat of currentCats) {
        if (cat !== 'other' && otherCats.has(cat)) return true
      }
      return false
    }
    return false
  })

  const sorted = matches.sort((a, b) => a.slug.localeCompare(b.slug))

  if (current.kind === 'topic' && sorted.length < limit) {
    const taken = new Set(sorted.map((c) => c.slug))
    const filler = candidates
      .filter((c) => !taken.has(c.slug))
      .sort((a, b) => a.slug.localeCompare(b.slug))
    sorted.push(...filler.slice(0, limit - sorted.length))
  }

  return sorted.slice(0, limit)
}

/**
 * Surface every grass comparison that mentions a given guide slug, for the
 * "See how X compares" block on /guides/[slug] pages. Stable sort by slug.
 */
export function getComparisonsForGuide(guideSlug: GuideSlug): GrassComparisonEntry[] {
  return COMPARISONS.filter(
    (c): c is GrassComparisonEntry =>
      c.kind === 'grass' && (c.a.guideSlug === guideSlug || c.b.guideSlug === guideSlug),
  ).sort((a, b) => a.slug.localeCompare(b.slug))
}
